import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useEncryptedSearchParams } from "../hooks/useEncryptedSearchParams";
import "bootstrap/dist/css/bootstrap.min.css";
import $ from "jquery";
import "select2";
import "select2/dist/css/select2.min.css";
import FilterListIcon from "@mui/icons-material/FilterList";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import LabelIcon from "@mui/icons-material/Label";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DescriptionIcon from "@mui/icons-material/Description";
import ChecklistRtlIcon from "@mui/icons-material/ChecklistRtl";
import SubjectIcon from "@mui/icons-material/Subject";
import EditNoteIcon from "@mui/icons-material/EditNote";
import NumbersIcon from "@mui/icons-material/Numbers";
import ImageIcon from "@mui/icons-material/Image";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SaveIcon from "@mui/icons-material/Save";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Button, Stack, CircularProgress } from "@mui/material";
import "./RaiseComplaint.css";
import "./RaiseComplaintChecklist.css";
import "./AddEditUser.css";
import successIcon from "../assets/success-icon.png";
import {
  ppbAPI,
  complaintAPI,
  checklistQuestionAPI,
  checklistTemplateAPI,
  plantMasterAPI,
  masterDataAPI,
  getApiBaseUrl,
} from "../services/api";
import { buildPlantCodesCsvFromSelectedLines } from "../utils/plantCodeResolve";
import {
  validateBusinessVerticalsForUser,
  INITIATOR_GEO_MISMATCH_MESSAGE,
} from "../utils/raiseComplaintInvoiceAuth";
import { escapeHtml } from "../utils/xss";
import {
  flattenChecklistQuestionNodes,
  partitionTemplateAnswersForStorage,
} from "../utils/checklistSrCategoryUtils";
import {
  DEFAULT_PRELAM_PPB_MFC_VARIANT_OPTIONS,
  matchesPpbMfcBusinessVerticalText,
  resolvePrelamPpbMfcInvoiceFields,
} from "../utils/prelamPpbMfcInvoiceUtils";
import Swal from "sweetalert2";
import { useSidebar } from "../contexts/SidebarContext";
import FileUploadPanelLayout from "../components/FileUploadPanelLayout";

const IMAGE_UPLOAD_FORMAT_NOTE =
  "( Allowed formats: JPG, PNG, total size up to 12MB )";
const IMAGE_UPLOAD_HELPER = "Choose image files to attach documents";
const COMPLAINT_DAMAGE_IMAGE_UPLOAD_HELPER =
  "Images Required specifically for Complaint Material/ Area of Application / Storage Area";
const VIDEO_UPLOAD_FORMAT_NOTE = "( Paste the URL of the video )";
const VISIT_REPORT_FORMAT_NOTE = "( Enter visit report comments, max 3000 characters )";
/** Same panel layout as raise-complaint InvoiceUpload (FileUploadPanelLayout--invoice). */
const IMAGE_UPLOAD_PANEL_CLASS = "file-upload-panel-layout--invoice";
const MAX_DAMAGE_IMAGE_TOTAL_BYTES = 12 * 1024 * 1024;

const getDamageImageTotalBytes = (files) => {
  const list = Array.isArray(files) ? files : [];
  return list.reduce(
    (sum, item) => sum + (Number(item?.file?.size ?? item?.size ?? item?.fileSize) || 0),
    0
  );
};

const isDamageImageUploadAtSizeLimit = (files) =>
  getDamageImageTotalBytes(files) >= MAX_DAMAGE_IMAGE_TOTAL_BYTES;

const getImageUploadChooseStyle = (disabled) => ({
  opacity: disabled ? 0.6 : 1,
  backgroundColor: disabled ? "#e9ecef" : "#fff",
  cursor: disabled ? "not-allowed" : "pointer",
  pointerEvents: disabled ? "none" : "auto",
});

const buildImageUploadTotalHint = (files) => {
  const list = Array.isArray(files) ? files : [];
  if (list.length === 0) return null;
  const totalBytes = list.reduce(
    (sum, item) => sum + (Number(item?.file?.size ?? item?.size ?? item?.fileSize) || 0),
    0
  );
  const totalMB = (totalBytes / (1024 * 1024)).toFixed(2);
  const maxMB = (MAX_DAMAGE_IMAGE_TOTAL_BYTES / (1024 * 1024)).toFixed(0);
  const over = totalBytes > MAX_DAMAGE_IMAGE_TOTAL_BYTES;
  return (
    <>
      Total: {totalMB} MB / {maxMB} MB
      {over ? (
        <span style={{ color: "#d9534f", marginLeft: "8px" }}>Over limit. Remove some files.</span>
      ) : null}
    </>
  );
};

const resolveFileUrl = (pathOrUrl) => {
  if (!pathOrUrl) return "";
  const s = String(pathOrUrl).trim();
  if (!s) return "";
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  const base = getApiBaseUrl() || "";
  return base ? `${base.replace(/\/$/, "")}/${s.replace(/^\//, "")}` : `/${s.replace(/^\//, "")}`;
};

const normalizeQueryValue = (raw) => {
  if (raw == null) return "";
  let value = String(raw).replace(/\+/g, " ").trim();
  if (!value) return "";
  // Some entry points pass double-encoded values (e.g. DOOR%2520SPECIAL).
  for (let i = 0; i < 2; i += 1) {
    try {
      const decoded = decodeURIComponent(value);
      if (!decoded || decoded === value) break;
      value = decoded.trim();
    } catch (_) {
      break;
    }
  }
  return value;
};

const CHECKLIST_FUTURE_DATE_ERROR = "Date cannot be greater than current date.";

const getLocalTodayISODate = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const normalizeChecklistDateValue = (value) => {
  if (value == null || String(value).trim() === "") return "";
  const trimmed = String(value).trim();

  const slashMatch = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (slashMatch) return `${slashMatch[3]}-${slashMatch[2]}-${slashMatch[1]}`;

  const dashMatch = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dashMatch) return `${dashMatch[3]}-${dashMatch[2]}-${dashMatch[1]}`;

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;

  const parsed = new Date(trimmed);
  if (!isNaN(parsed.getTime())) {
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, "0");
    const day = String(parsed.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  return "";
};

const isChecklistDateAfterToday = (value) => {
  const iso = normalizeChecklistDateValue(value);
  if (!iso) return false;
  return iso > getLocalTodayISODate();
};

const clampChecklistDateToToday = (value) => {
  const iso = normalizeChecklistDateValue(value);
  if (!iso) return "";
  const today = getLocalTodayISODate();
  return iso > today ? today : iso;
};

const isComplaintReceiptDateQuestion = (text) =>
  /date\s+of\s+receipt\s+of\s+complaint/i.test(
    String(text || "")
      .replace(/\s+/g, " ")
      .trim()
  );

const isManufacturingDateQuestion = (text) =>
  /date\s+of\s+manufactur(?:ing|e)\s*\(\s*mfd\s*\)/i.test(
    String(text || "")
      .replace(/\s+/g, " ")
      .trim()
  );

const MFD_DATE_FORMAT_ERROR = "Enter date as DD-MM-YYYY (e.g. 29-09-2026).";
const MFD_DATE_INVALID_ERROR = "Enter a valid date in DD-MM-YYYY format.";
const MFD_DATE_FUTURE_ERROR = "Date of manufacturing cannot be greater than current date.";

const toManufacturingDateDisplay = (value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "";
  if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) return trimmed;
  const iso = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
  return trimmed;
};

const formatManufacturingDateTyping = (raw) => {
  const trimmed = String(raw ?? "").trim();
  const iso = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
  const digits = trimmed.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
};

const parseManufacturingDate = (value) => {
  const trimmed = String(value ?? "").trim();
  const match = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!match) return null;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  if (year < 1900 || month < 1 || month > 12 || day < 1) return null;
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return `${match[3]}-${match[2]}-${match[1]}`;
};

const firstChecklistValidationMessage = (errors) => {
  if (!errors || errors === true) return CHECKLIST_FUTURE_DATE_ERROR;
  for (const tc of Object.keys(errors)) {
    const messages = Object.values(errors[tc] || {}).filter(Boolean);
    if (messages.length) return String(messages[0]);
  }
  return CHECKLIST_FUTURE_DATE_ERROR;
};

const validateManufacturingDate = (value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "";
  const display = toManufacturingDateDisplay(trimmed);
  if (!/^\d{2}-\d{2}-\d{4}$/.test(display)) return MFD_DATE_FORMAT_ERROR;
  const iso = parseManufacturingDate(display);
  if (!iso) return MFD_DATE_INVALID_ERROR;
  if (iso > getLocalTodayISODate()) return MFD_DATE_FUTURE_ERROR;
  return "";
};

/** True when text is Laminate business vertical (excludes Prelam). */
const matchesLaminateText = (text) => {
  const raw = String(text || "").trim().toLowerCase();
  if (!raw || raw.includes("prelam")) return false;
  const normalized = raw.replace(/[^a-z0-9]/g, "");
  if (normalized === "laminate") return true;
  return raw.includes("laminate");
};

/** True when text is Sturdo business vertical (e.g. Greenlam Cubicle Sturdo). */
const matchesSturdoText = (text) => {
  const raw = String(text || "").trim();
  if (!raw) return false;
  const n = raw.toLowerCase().replace(/\s+/g, " ").trim();
  if (n.includes("non-sturdo") || n.includes("non sturdo")) return false;
  if (n === "sturdo") return true;
  const cubicleNorm = n.replace(/\(\s*/g, "(").replace(/\s*\)/g, ")");
  if (cubicleNorm === "greenlam cubicle (sturdo)") return true;
  if (n.includes("greenlam") && n.includes("cubicle") && n.includes("sturdo")) return true;
  return n.includes("sturdo");
};

/** True when text is Interior Compacts - Non-Sturdo Applications (not Sturdo cubicle). */
const matchesInteriorCompactsNonSturdoText = (text) => {
  const raw = String(text || "").trim().toLowerCase();
  if (!raw) return false;
  const n = raw.replace(/\s+/g, " ").trim();
  if (!n.includes("non-sturdo") && !n.includes("non sturdo")) return false;
  const normalized = raw.replace(/[^a-z0-9]/g, "");
  if (normalized.includes("interiorcompact") && normalized.includes("nonsturdo")) return true;
  return n.includes("interior compact");
};

/** CATEGORYMASTER / raised-query API expect "Sturdo", not long cubicle labels. */
const resolveBusinessVerticalForChecklistApi = (text) => {
  const raw = normalizeQueryValue(text) || String(text || "").trim();
  if (!raw) return raw;
  if (matchesSturdoText(raw)) return "Sturdo";
  return raw;
};

/** True when text is Prelam - MFC business vertical (excludes PPB–MFC product category). */
const matchesPrelamMfcText = (text) => {
  const t = String(text || "").toLowerCase().replace(/\s+/g, " ").trim();
  if (!t) return false;
  if (
    t.includes("mfc") &&
    (t.includes("ppb") || t.includes("plain particle") || t.includes("particle board"))
  ) {
    return false;
  }
  const normalized = t.replace(/[^a-z0-9]/g, "");
  return normalized === "prelammfc" || (t.includes("prelam") && t.includes("mfc"));
};

/** True when text is MikasaPly business vertical (not Mikasa Veneer). */
const matchesMikasaPlyText = (text) => {
  const raw = String(text || "").trim().toLowerCase();
  if (!raw || raw.includes("veneer")) return false;
  const normalized = raw.replace(/[^a-z0-9]/g, "");
  if (
    normalized === "mikasaply" ||
    normalized === "makasaply" ||
    normalized.includes("mikasaply") ||
    normalized.includes("makasaply")
  ) {
    return true;
  }
  return raw.includes("mikasa") && raw.includes("ply");
};

/** True when text is Mikasa Floors business vertical (not Ply / Veneer). */
const matchesMikasaFloorsText = (text) => {
  const raw = String(text || "").trim().toLowerCase();
  if (!raw || raw.includes("veneer")) return false;
  if (raw.includes("mikasa") && raw.includes("ply") && !raw.includes("floor")) return false;
  const normalized = raw.replace(/[^a-z0-9]/g, "");
  if (normalized === "mikasafloors" || normalized.includes("mikasafloor")) return true;
  return raw.includes("mikasa") && raw.includes("floor");
};

/** True when text is Mikasa Veneer business vertical (not Ply / Floors / Door). */
const matchesMikasaVeneerText = (text) => {
  const raw = String(text || "").trim().toLowerCase();
  if (!raw) return false;
  if (raw.includes("floor") && raw.includes("mikasa")) return false;
  if (raw.includes("door") && raw.includes("mikasa")) return false;
  if (raw.includes("ply") && raw.includes("mikasa") && !raw.includes("veneer")) return false;
  const normalized = raw.replace(/[^a-z0-9]/g, "");
  if (normalized === "mikasaveneer" || normalized.includes("mikasaveneer")) return true;
  return raw.includes("mikasa") && raw.includes("veneer");
};

/** True when text is Mikasa Door business vertical (not Ply / Floors / Veneer). */
const matchesMikasaDoorText = (text) => {
  const raw = String(text || "").trim().toLowerCase();
  if (!raw || raw.includes("veneer")) return false;
  if (raw.includes("floor") && raw.includes("mikasa")) return false;
  if (raw.includes("ply") && raw.includes("mikasa") && !raw.includes("door")) return false;
  const normalized = raw.replace(/[^a-z0-9]/g, "");
  if (
    normalized === "mikasadoor" ||
    normalized === "mikasadoors" ||
    normalized.includes("mikasadoor")
  ) {
    return true;
  }
  return raw.includes("mikasa") && raw.includes("door");
};

/** True when text is Exterior Compact - Clads business vertical. */
const matchesExteriorCompactCladsText = (text) => {
  const raw = String(text || "").trim().toLowerCase();
  if (!raw) return false;
  const normalized = raw.replace(/[^a-z0-9]/g, "");
  if (normalized.includes("exteriorcompact") && normalized.includes("clad")) return true;
  if (raw.includes("exterior compact") && raw.includes("clad")) return true;
  return (
    normalized === "exteriorcompactclads" ||
    normalized.includes("exteriorcompactclad")
  );
};

/** True when text is Exterior Compact - Mikasa Fx business vertical (not Clads). */
const matchesExteriorCompactMikasaFxText = (text) => {
  if (matchesExteriorCompactCladsText(text)) return false;
  const raw = String(text || "").trim().toLowerCase();
  if (!raw) return false;
  const normalized = raw.replace(/[^a-z0-9]/g, "");
  if (!normalized.includes("exteriorcompact")) return false;
  if (normalized.includes("mikasafx") || normalized.includes("newmikafx")) return true;
  if (raw.includes("exterior compact") && raw.includes("mikasa") && raw.includes("fx")) {
    return true;
  }
  return normalized === "exteriorcompactmikasafx";
};

/** Visit Report Comments is required when dynamic checklist templates are shown. */
const hasVisitReportCommentsValue = (value) => String(value ?? "").trim().length > 0;

const normalizeChecklistQuestionLabel = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const isProductDetailsSectionLabel = (sectionText) =>
  normalizeChecklistQuestionLabel(sectionText) === "product details";

/** Maps to total SR Qty from Material Section (selected rows, incl. + clone rows). */
const isComplaintQuantityLabel = (questionText) => {
  const core = normalizeChecklistQuestionLabel(questionText).replace(/^\d+\s+/, "");
  return (
    core.includes("quantity under complaint") ||
    core === "complaint quantity" ||
    core.startsWith("complaint quantity ") ||
    core.includes("complaint defective quantity") ||
    core === "complaint defective quantity"
  );
};

/** Maps to deduped invoice qty from Material Section (e.g. "Total quantity purchased"). */
const isTotalQuantityPurchasedLabel = (questionText) => {
  const core = normalizeChecklistQuestionLabel(questionText).replace(/^\d+\s+/, "");
  if (core.includes("groups purchased")) return false;
  if (core.includes("complaint quantity") && !core.includes("purchased")) return false;
  return (
    core === "total quantity purchased" ||
    core.includes("total quantity purchased") ||
    (core.includes("total") && core.includes("purchased")) ||
    core.includes("total qty purchased")
  );
};

const isMaterialDerivedQt008Field = (questionText) =>
  isComplaintQuantityLabel(questionText) || isTotalQuantityPurchasedLabel(questionText);

/** "Is Greenlam Logo/Branding appearing on the product?" — Yes shows branding image upload follow-up. */
const isGreenlamBrandingQuestion = (questionText) => {
  const core = normalizeChecklistQuestionLabel(questionText).replace(/^\d+\s+/, "");
  return (
    core.includes("greenlam branding appearing on the product") ||
    core.includes("greenlam logo branding appearing on the product") ||
    (core.includes("greenlam") &&
      core.includes("branding") &&
      core.includes("appearing on the product"))
  );
};

/** Branding / QR capture label or upload row (QT002 / QT009). */
const isGreenlamBrandingCaptureQuestion = (questionText) => {
  const core = normalizeChecklistQuestionLabel(questionText).replace(/^\d+\s+/, "");
  return (
    core.includes("capture the picture of branding") ||
    (core.includes("capture") && core.includes("branding") && core.includes("qr code")) ||
    (core.includes("branding") && core.includes("sanded side") && core.includes("qr code"))
  );
};

const isAffirmativeChecklistAnswer = (value) => {
  const normalized = normalizeChecklistQuestionLabel(value);
  return normalized === "yes" || normalized === "y";
};

const findGreenlamBrandingQuestionNode = (nodes) => {
  let found = null;
  const walk = (list) => {
    (list || []).forEach((node) => {
      if (found) return;
      const text = node?.questionText || node?.QuestionText || "";
      if (isGreenlamBrandingQuestion(text)) {
        found = node;
        return;
      }
      if (node?.children?.length) walk(node.children);
    });
  };
  walk(nodes);
  return found;
};

const collectGreenlamBrandingCaptureNodes = (nodes, bucket = []) => {
  (nodes || []).forEach((node) => {
    const text = node?.questionText || node?.QuestionText || "";
    const typeCode = String(node?.questionTypeCode || node?.QuestionTypeCode || "").toUpperCase();
    if (typeCode === "QT009" && isGreenlamBrandingCaptureQuestion(text)) {
      bucket.push(node);
    }
    if (node?.children?.length) collectGreenlamBrandingCaptureNodes(node.children, bucket);
  });
  return bucket;
};

const isGreenlamBrandingCaptureVisible = (tree, answers) => {
  const brandingNode = findGreenlamBrandingQuestionNode(tree);
  if (!brandingNode) return false;
  const code = brandingNode.questionCode || brandingNode.QuestionCode;
  if (!code) return false;
  return isAffirmativeChecklistAnswer(answers?.[code]);
};

/** "Did you visit the site?" — Yes locks Complaint Attended by to the logged-in user. */
const isSiteVisitQuestion = (questionText) => {
  const core = normalizeChecklistQuestionLabel(questionText).replace(/^\d+\s+/, "");
  return core.includes("did you visit the site");
};

/** "Complaint Attended by" text field (QT005 / QT007). */
const isComplaintAttendedByQuestion = (questionText) => {
  const core = normalizeChecklistQuestionLabel(questionText).replace(/^\d+\s+/, "");
  return core.includes("complaint attended by");
};

const findSiteVisitQuestionNode = (nodes) => {
  let found = null;
  const walk = (list) => {
    (list || []).forEach((node) => {
      if (found) return;
      const text = node?.questionText || node?.QuestionText || "";
      if (isSiteVisitQuestion(text)) {
        found = node;
        return;
      }
      if (node?.children?.length) walk(node.children);
    });
  };
  walk(nodes);
  return found;
};

const isSiteVisitAffirmative = (tree, answers) => {
  const visitNode = findSiteVisitQuestionNode(tree);
  if (!visitNode) return false;
  const code = visitNode.questionCode || visitNode.QuestionCode;
  if (!code) return false;
  return isAffirmativeChecklistAnswer(answers?.[code]);
};

const isSiteVisitAnswered = (tree, answers) => {
  const visitNode = findSiteVisitQuestionNode(tree);
  if (!visitNode) return false;
  const code = visitNode.questionCode || visitNode.QuestionCode;
  if (!code) return false;
  const val = answers?.[code];
  if (val == null || String(val).trim() === "") return false;
  if (Array.isArray(val) && val.length === 0) return false;
  return true;
};

/** Hidden until "Did you visit the site?" has any answer (Yes or No). */
const isComplaintAttendedByVisible = (tree, answers) => {
  const visitNode = findSiteVisitQuestionNode(tree);
  if (!visitNode) return true;
  return isSiteVisitAnswered(tree, answers);
};

const collectComplaintAttendedByTextNodes = (nodes, bucket = []) => {
  (nodes || []).forEach((node) => {
    const text = node?.questionText || node?.QuestionText || "";
    const typeCode = String(node?.questionTypeCode || node?.QuestionTypeCode || "").toUpperCase();
    if (
      (typeCode === "QT005" || typeCode === "QT007") &&
      isComplaintAttendedByQuestion(text)
    ) {
      bucket.push(node);
    }
    if (node?.children?.length) collectComplaintAttendedByTextNodes(node.children, bucket);
  });
  return bucket;
};

const getLoggedInUserDisplayName = () => {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    return String(
      u?.fullName ??
        u?.FullName ??
        u?.displayName ??
        u?.DisplayName ??
        u?.userName ??
        u?.UserName ??
        ""
    ).trim();
  } catch {
    return "";
  }
};

/** Selected material rows: invoice qty once per ProductCode (clone/+ rows), SR qty summed. */
const computeMaterialSectionTotals = (details) => {
  const seenBaseProducts = new Set();
  let totalInvoiceQty = 0;
  let totalSrQty = 0;
  (details || []).forEach((d) => {
    // Draft InvoiceDetails only contains selected rows; explicit 0 = skip.
    if (d.IsSelected === 0 || d.isSelected === 0) return;
    totalSrQty += parseFloat(d.SRQuantity ?? d.srQuantity ?? 0) || 0;
    const baseCode = String(d.ProductCode ?? d.productCode ?? "").trim();
    if (baseCode && seenBaseProducts.has(baseCode)) return;
    if (baseCode) seenBaseProducts.add(baseCode);
    totalInvoiceQty += parseFloat(d.InvoiceQuantity ?? d.invoiceQuantity ?? 0) || 0;
  });
  return {
    totalInvoiceQty: Math.floor(totalInvoiceQty),
    totalSrQty: Math.floor(totalSrQty),
  };
};

const GENERAL_SR_TYPE_CODE = "0000";
const EMPTY_QUESTION_TREE = [];
const SR_ANSWER_KEY_SEP = "::";

const buildCategoryErrorKey = (srCategoryCode, questionCode) =>
  `${srCategoryCode}${SR_ANSWER_KEY_SEP}${questionCode}`;

const stripLegacyScopedKeys = (answers) => {
  const out = {};
  Object.entries(answers || {}).forEach(([key, val]) => {
    if (!key.includes(SR_ANSWER_KEY_SEP)) out[key] = val;
  });
  return out;
};

const mergeChecklistAnswerMaps = (...maps) => {
  const out = {};
  maps.forEach((m) => {
    Object.assign(out, m || {});
  });
  return out;
};

const flattenAllSrCategoryAnswers = (storedSrCategory) => {
  const out = {};
  Object.values(storedSrCategory || {}).forEach((catAnswers) => {
    Object.assign(out, catAnswers || {});
  });
  return out;
};

const getStoredAnswersForSrCategory = (storedSrCategory, catCode) => {
  if (!storedSrCategory || !catCode) return {};
  if (storedSrCategory[catCode]) return storedSrCategory[catCode];
  const target = String(catCode).trim().toLowerCase();
  for (const [k, v] of Object.entries(storedSrCategory)) {
    if (String(k).trim().toLowerCase() === target) return v || {};
  }
  return {};
};

const getStoredAnswerForQuestion = (answers, questionCode) => {
  const k = questionCode == null ? "" : String(questionCode).trim();
  if (!k) return undefined;
  if (answers && Object.prototype.hasOwnProperty.call(answers, k)) return answers[k];
  const lower = k.toLowerCase();
  for (const key of Object.keys(answers || {})) {
    if (String(key).trim().toLowerCase() === lower) return answers[key];
  }
  return undefined;
};

/** Split legacy draft keys (SRTYPE::QCODE) into general + per-SR-category maps. */
const splitLegacyMergedTemplateAnswers = (stored) => {
  const general = {};
  const bySr = {};
  Object.entries(stored || {}).forEach(([key, val]) => {
    const idx = key.indexOf(SR_ANSWER_KEY_SEP);
    if (idx < 0) {
      general[key] = val;
      return;
    }
    const sr = key.slice(0, idx);
    const q = key.slice(idx + SR_ANSWER_KEY_SEP.length);
    if (!bySr[sr]) bySr[sr] = {};
    bySr[sr][q] = val;
  });
  return { general, bySr };
};

/** Ensure category-tagged answers are stored under srCategoryAnswers (correct SRTypeCode on insert). */
const partitionChecklistAnswersForTemplateStorage = (
  templateCode,
  generalAnswers,
  srCategoryAnswers,
  questionTree
) => {
  const flat = flattenChecklistQuestionNodes(questionTree || []);
  return partitionTemplateAnswersForStorage(
    flat,
    stripLegacyScopedKeys(generalAnswers || {}),
    srCategoryAnswers || {}
  );
};

const parseSrTypeCodes = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map((c) => String(c).trim()).filter(Boolean);
  }
  return String(raw)
    .split(",")
    .map((c) => c.trim())
    .filter(Boolean);
};

/** Unique SR categories from selected Material Section rows (draft InvoiceDetails). */
const extractMaterialSrCategories = (details) => {
  const map = new Map();
  (details || []).forEach((d) => {
    if (d.IsSelected === 0 || d.isSelected === 0) return;
    const code = String(
      d.SRTypeCode ?? d.srTypeCode ?? d.SRTYPECODE ?? ""
    ).trim();
    if (!code) return;
    const nameRaw =
      d.SRTypeName ??
      d.srTypeName ??
      d.SRTypeDesc ??
      d.srTypeDesc ??
      "";
    const name =
      code === GENERAL_SR_TYPE_CODE
        ? "All"
        : String(nameRaw).trim() || code;
    if (!map.has(code)) map.set(code, name);
  });
  const entries = Array.from(map.entries()).map(([code, name]) => ({ code, name }));
  entries.sort((a, b) => {
    if (a.code === GENERAL_SR_TYPE_CODE) return -1;
    if (b.code === GENERAL_SR_TYPE_CODE) return 1;
    return a.name.localeCompare(b.name);
  });
  return entries;
};

const isGeneralSrCategoryQuestion = (node) => {
  const codes = parseSrTypeCodes(node?.srTypeCode ?? node?.SRTypeCode ?? "");
  return codes.length === 0 || codes.includes(GENERAL_SR_TYPE_CODE);
};

/** All / untagged questions — shown above SR Category tabs. */
const filterGeneralQuestionTree = (nodes) => {
  const walk = (list) => {
    const out = [];
    (list || []).forEach((node) => {
      const filteredChildren = walk(node.children || []);
      if (isGeneralSrCategoryQuestion(node)) {
        out.push({ ...node, children: filteredChildren });
      } else if (filteredChildren.length > 0) {
        out.push({ ...node, children: filteredChildren });
      }
    });
    return out;
  };
  return walk(nodes);
};

const questionAppliesToSrCategoryOnly = (node, activeCode) => {
  if (!activeCode) return false;
  const codes = parseSrTypeCodes(node?.srTypeCode ?? node?.SRTypeCode ?? "");
  if (codes.length === 0) return false;
  if (codes.includes(GENERAL_SR_TYPE_CODE)) return false;
  return codes.includes(activeCode);
};

/** Category-specific questions — shown under the active SR Category tab. */
const filterQuestionTreeBySrCategoryExcludingGeneral = (nodes, activeCode) => {
  if (!activeCode) return [];
  const walk = (list) => {
    const out = [];
    (list || []).forEach((node) => {
      const filteredChildren = walk(node.children || []);
      const selfMatches = questionAppliesToSrCategoryOnly(node, activeCode);
      if (selfMatches || filteredChildren.length > 0) {
        out.push({ ...node, children: filteredChildren });
      }
    });
    return out;
  };
  return walk(nodes);
};

const findChecklistQuestionLabel = (tree, questionCode) => {
  let label = "";
  const walk = (nodes) => {
    (nodes || []).forEach((node) => {
      if (label) return;
      const code = node?.questionCode || node?.QuestionCode;
      if (code === questionCode) {
        label = String(node?.questionText || node?.QuestionText || code).trim();
        return;
      }
      walk(node?.children || []);
    });
  };
  walk(tree);
  return label;
};

const getMaterialDerivedQt008EffectiveValue = (questionText, materialSectionTotals) => {
  if (!materialSectionTotals) return "";
  if (isComplaintQuantityLabel(questionText)) {
    return materialSectionTotals.totalSrQty != null
      ? String(materialSectionTotals.totalSrQty)
      : "";
  }
  if (isTotalQuantityPurchasedLabel(questionText)) {
    return materialSectionTotals.totalInvoiceQty != null
      ? String(materialSectionTotals.totalInvoiceQty)
      : "";
  }
  return "";
};

const buildChecklistAnswersForValidation = (tree, answers, materialSectionTotals) => {
  const merged = { ...(answers || {}) };
  const today = getLocalTodayISODate();
  const walk = (nodes) => {
    (nodes || []).forEach((node) => {
      const code = node?.questionCode || node?.QuestionCode;
      const typeCode = String(node?.questionTypeCode || node?.QuestionTypeCode || "").toUpperCase();
      const rawText = node?.questionText || node?.QuestionText || "";
      if (
        code &&
        typeCode === "QT010" &&
        !isManufacturingDateQuestion(rawText)
      ) {
        const current = merged[code];
        if (current == null || String(current).trim() === "") {
          merged[code] = today;
        } else {
          merged[code] = clampChecklistDateToToday(current);
        }
      }
      if (code && typeCode === "QT008" && isMaterialDerivedQt008Field(rawText)) {
        const derived = getMaterialDerivedQt008EffectiveValue(rawText, materialSectionTotals);
        if (derived) merged[code] = derived;
      }
      walk(node?.children || []);
    });
  };
  walk(tree);
  return merged;
};

const findMaterialDerivedQt008Codes = (tree) => {
  const complaintQtyCodes = [];
  const totalPurchasedCodes = [];
  const walk = (nodes) => {
    (nodes || []).forEach((node) => {
      const typeCode = String(node.questionTypeCode || node.QuestionTypeCode || "").toUpperCase();
      const code = node.questionCode || node.QuestionCode;
      const label = node.questionText || node.QuestionText || "";
      if (typeCode === "QT008" && code) {
        if (isComplaintQuantityLabel(label)) complaintQtyCodes.push(code);
        if (isTotalQuantityPurchasedLabel(label)) totalPurchasedCodes.push(code);
      }
      walk(node.children || []);
    });
  };
  walk(tree);
  return { complaintQtyCodes, totalPurchasedCodes };
};

const RaiseComplaintChecklistPage = ({ searchParams }) => {
  const navigate = useNavigate();
  const { collapseSidebar, restoreSidebar } = useSidebar();
  const [activeTab, setActiveTab] = useState("ppb");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [productCode, setProductCode] = useState("");
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [invoiceDetailsLoading, setInvoiceDetailsLoading] = useState(() => {
    const urlInvoiceNo = searchParams.get("invoiceNo");
    return Boolean(urlInvoiceNo && String(urlInvoiceNo).trim());
  });
  const [submitPlantMasterList, setSubmitPlantMasterList] = useState([]);
  const [invoiceCopyFiles, setInvoiceCopyFiles] = useState([]);
  const [complaintReceivedDate, setComplaintReceivedDate] = useState("");
  const [visitReportComments, setVisitReportComments] = useState("");
  const [visitReportCommentsError, setVisitReportCommentsError] = useState("");
  /** Total SR/Comp Qty from selected material rows on raise-complaint (URL or draft). */
  const [selectedMaterialQtyLimit, setSelectedMaterialQtyLimit] = useState(null);
  /** Totals from Material Section for auto-filled Product Details fields. */
  const [materialSectionTotals, setMaterialSectionTotals] = useState(null);
  /** Selected material rows from draft — used for SR Category tabs and question filtering. */
  const [materialInvoiceDetails, setMaterialInvoiceDetails] = useState([]);
  const [activeSrCategoryTab, setActiveSrCategoryTab] = useState(0);
  const [srTypeOptions, setSrTypeOptions] = useState([]);
  /** Bumped after checklist draft answers load so material totals re-apply over saved values. */
  const [checklistDraftAnswersRevision, setChecklistDraftAnswersRevision] = useState(0);
  // Checklist media returned from SRCHECKLISTATTACHMENTS for this SRCode.
  // We will filter it per active template tab (templateCode + MarketCode + CategoryCode).
  const [draftChecklistAttachments, setDraftChecklistAttachments] = useState([]);
  const [savingDraft, setSavingDraft] = useState(false);
  /** Final submit (submitFinal API) — green "Submit and raise SR" button */
  const [submittingFinal, setSubmittingFinal] = useState(false);
  const submitFinalInFlightRef = useRef(false);
  const [formData, setFormData] = useState({
    // PPB has printed information
    ppbPrintedInfo: "yes",
    // Variant - Product Grade
    variantProductGrade: "",
    // Other fields
    thickness: "6",
    batchNo: "BT1234556",
    size: "8 * 4",
    emission: "E2",
    salesInvoiceNo: "234567890",
    invoiceDate: "05.05.25",
    natureOfDefect: "",
    materialUsedForFurniture: "yes",
    defectiveSize: "",
    defectiveQty: "",
    defectiveSizeNotUsed: "",
    defectiveQtyNotUsed: "",
    verifiedWithDevice: "yes",
    deviceDetails: "",
    rightFabricationTools: "yes",
    materialStacked: "",
    contactWithWall: "yes",
    complaintNoticedAt: "dealerShop",
    specifyOthers: "",
    liabilityExpected: "",
    defectsNotedOnLR: "yes",
    statusOfComplainer: "",
    otherComments: "",
  });

  const [prelamForm, setPrelamForm] = useState({
    printedInfo: "yes",
    designCategory: "",
    productGrade: "",
    thickness: "",
    size: "",
    designNo: "",
    finish: "",
    emission: "",
    batchNo: "",
    mfdDate: "",
    salesInvoiceNo: "",
    salesInvoiceDate: "",
    natureOfDefect: "",
    usedForInterior: "yes",
    defectiveSize: "",
    defectiveQty: "",
    notUsedPortion: "",
    verifiedWithDevice: "yes",
    deviceDetails: "",
    rightTools: "yes",
    stackedOrientation: "horizontally",
    storedInContact: "yes",
    complaintNoticedAt: "",
    othersSpecify: "",
    complainerStatus: "",
    liabilityExpected: "",
    defectsDuringUnloading: "no",
    otherComments: "",
  });

  // Normalize date strings (DD/MM/YYYY, DD-MM-YYYY, or ISO) to ISO for native date inputs
  const toISODate = (value) => {
    if (!value || typeof value !== "string") return "";
    const trimmed = value.trim();

    const slashMatch = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (slashMatch) return `${slashMatch[3]}-${slashMatch[2]}-${slashMatch[1]}`;

    const dashMatch = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dashMatch) return `${dashMatch[3]}-${dashMatch[2]}-${dashMatch[1]}`;

    if (trimmed.match(/^\d{4}-\d{2}-\d{2}$/)) return trimmed;

    const parsed = new Date(trimmed);
    if (!isNaN(parsed.getTime())) {
      const y = parsed.getFullYear();
      const m = String(parsed.getMonth() + 1).padStart(2, "0");
      const d = String(parsed.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    }

    return "";
  };

  const [uploadFiles, setUploadFiles] = useState([]);

  const [prelamUploadFiles, setPrelamUploadFiles] = useState([]);

  const [videoUrl, setVideoUrl] = useState("");
  const [prelamVideoUrl, setPrelamVideoUrl] = useState("");

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [srNumber, setSrNumber] = useState("");
  const [childSRNumbers, setChildSRNumbers] = useState([]);

  const handleOpenChecklistAttachment = (fileItem) => {
    if (!fileItem) return;
    if (fileItem.file) {
      try {
        const url = URL.createObjectURL(fileItem.file);
        window.open(url, "_blank");
        setTimeout(() => {
          try {
            URL.revokeObjectURL(url);
          } catch (_) {}
        }, 60000);
      } catch (_) {}
      return;
    }

    const url = resolveFileUrl(fileItem.url || fileItem.path || fileItem.filePath);
    if (url) window.open(url, "_blank");
  };

  // Disable background scroll when any modal/popup on this page is open, without causing page jump
  useEffect(() => {
    const hasOpenModal = showSuccessModal;
    if (hasOpenModal) {
      // Keep the sidebar collapsed while the success modal is visible.
      collapseSidebar();
      const scrollY = window.scrollY || window.pageYOffset;
      const originalStyle = {
        position: document.body.style.position,
        top: document.body.style.top,
        width: document.body.style.width,
      };

      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";

      return () => {
        restoreSidebar();
        document.body.style.position = originalStyle.position || "";
        document.body.style.top = originalStyle.top || "";
        document.body.style.width = originalStyle.width || "";
        window.scrollTo(0, scrollY);
      };
    }
    return undefined;
  }, [showSuccessModal, collapseSidebar, restoreSidebar]);

  const serializeUploadFiles = (files) =>
    (files || []).map((f) => ({
      id: f.id,
      name: f.name,
      preview: f.preview,
      type: f.type,
      size: f.file?.size ?? f.size ?? null,
    }));

  // Build checklistAttachments for SRCHECKLISTATTACHMENTS: upload damage images then add video URL. Returns { complaintReceivedDate, checklistAttachments }.
  const buildChecklistAttachmentsForDraft = async (files, videoUrlValue) => {
    const attachments = [];
    const list = Array.isArray(files) ? files : [];
    let sortOrder = 0;

    // 1) Add already-uploaded images (draft-restored): items without File
    //    These usually have { path/url, name, fileSize, fileType } but no `file` object.
    list
      .filter((f) => !(f?.file instanceof File))
      .forEach((item) => {
        const filePath = item?.filePath ?? item?.FilePath ?? item?.path ?? item?.url ?? "";
        if (!String(filePath).trim()) return;
        attachments.push({
          attachmentType: "DamageImage",
          filePath: String(filePath).trim(),
          fileName: item?.fileName ?? item?.FileName ?? item?.name ?? "attachment",
          fileSize: item?.fileSize ?? item?.FileSize ?? item?.size ?? null,
          fileType: item?.fileType ?? item?.FileType ?? item?.type ?? null,
          sortOrder: sortOrder++,
        });
      });

    // 2) Upload newly added images (File objects).
    const filesToUpload = list.filter((f) => f?.file instanceof File);
    for (let i = 0; i < filesToUpload.length; i++) {
      try {
        const res = await complaintAPI.uploadChecklistAttachment(
          filesToUpload[i].file,
          "DamageImage"
        );
        const data = res?.data ?? res;
        const path = data?.filePath ?? data?.FilePath;
        if (path) {
          attachments.push({
            attachmentType: "DamageImage",
            filePath: path,
            fileName: data?.fileName ?? data?.FileName ?? filesToUpload[i].name,
            fileSize:
              data?.fileSize ?? data?.FileSize ?? filesToUpload[i].file?.size ?? null,
            fileType: data?.fileType ?? data?.FileType ?? filesToUpload[i].type ?? null,
            sortOrder: sortOrder++,
          });
        }
      } catch (err) {
        console.warn("Upload checklist image failed:", err);
      }
    }

    // 3) Add video URL if present
    if (videoUrlValue && String(videoUrlValue).trim()) {
      attachments.push({
        attachmentType: "Video",
        filePath: String(videoUrlValue).trim(),
        fileName: "video",
        fileSize: null,
        fileType: null,
        sortOrder: sortOrder++,
      });
    }

    // Defensive de-dupe: remove identical (attachmentType + filePath) duplicates.
    // This addresses cases where JSON shows more pics than the UI.
    const uniqueMap = new Map();
    attachments.forEach((a) => {
      const typeKey = String(a?.attachmentType ?? "").trim();
      const pathKey = String(a?.filePath ?? "").trim();
      if (!pathKey) return;
      const key = `${typeKey}|${pathKey}`;
      if (!uniqueMap.has(key)) uniqueMap.set(key, a);
    });
    return Array.from(uniqueMap.values());
  };

  // Build SRATTACHMENTS payload for final submit (invoice copies). Used when submitting from draft so attachments are saved or can be modified.
  const buildSubmissionAttachmentsFromInvoiceCopies = () => {
    const list = Array.isArray(invoiceCopyFiles) ? invoiceCopyFiles : [];
    return list.map((item) => {
      const path = item?.filePath ?? item?.FilePath ?? item?.path ?? "";
      const name = item?.fileName ?? item?.FileName ?? item?.name ?? "N/A";
      const size = item?.fileSize ?? item?.FileSize ?? item?.size ?? null;
      const type = item?.fileType ?? item?.FileType ?? item?.type ?? "N/A";
      const copyFor = item?.copyFor ?? item?.CopyFor ?? "Invoice Copy";
      return {
        FilePath: path || "N/A",
        FileName: name,
        FileSize: size,
        FileType: type,
        CopyFor: copyFor,
        CatgCode: "0000",
        CustomerCode: formData?.customerCode ?? prelamForm?.customerCode ?? "0000",
        EndCustomerCode: formData?.endCustomerCode ?? prelamForm?.endCustomerCode ?? "0000",
      };
    }).filter((a) => a.FilePath && a.FilePath !== "N/A");
  };

  // State for API dropdown data
  const [productVariants, setProductVariants] = useState([]);
  const [sizeUOM, setSizeUOM] = useState([]);
  const [quantityUOM, setQuantityUOM] = useState([]);
  const [complaintLocations, setComplaintLocations] = useState([]);
  const [liabilityTypes, setLiabilityTypes] = useState([]);
  const [complainerStatuses, setComplainerStatuses] = useState([]);

  // Dynamic checklist: 1 to n templates (tabular), each with own questions and Draft Save
  const [templates, setTemplates] = useState([]);
  const [templateQuestionTrees, setTemplateQuestionTrees] = useState({});
  const [templateAnswers, setTemplateAnswers] = useState({});
  const [srCategoryAnswers, setSrCategoryAnswers] = useState({}); // { [templateCode]: { [srTypeCode]: { [questionCode]: value } } }
  const [dynamicAnswerErrors, setDynamicAnswerErrors] = useState({}); // { [templateCode]: { [questionCode]: errorMessage } }
  const [qt009ImageAnswers, setQt009ImageAnswers] = useState({}); // { [templateCode::questionCode]: Array<{ id, file?, preview?, name?, type?, size?, filePath? }> }
  const [qt009Uploading, setQt009Uploading] = useState({}); // { [templateCode::questionCode]: boolean }
  const [collapsedSections, setCollapsedSections] = useState(new Set()); // Track collapsed sections by question code
  const [isChecklistQuestionsCollapsed, setIsChecklistQuestionsCollapsed] = useState(false); // UI-only collapse for the whole checklist questions area
  const [isUploadSectionCollapsed, setIsUploadSectionCollapsed] = useState(false); // UI-only collapse for upload/video/comments section (default open on this page)
  const [activeChecklistTab, setActiveChecklistTab] = useState(0);
  const [checklistLoading, setChecklistLoading] = useState(() => {
    const urlTemplateCode = normalizeQueryValue(searchParams.get("templateCode"));
    const urlMarketName = normalizeQueryValue(searchParams.get("MarketCode"));
    const urlBusinessVertical = resolveBusinessVerticalForChecklistApi(
      searchParams.get("businessVertical") || searchParams.get("divisionName")
    );
    return Boolean(urlTemplateCode || (urlMarketName && urlBusinessVertical));
  });
  const [draftLoading, setDraftLoading] = useState(() => {
    const srCode = searchParams.get("srCode");
    return Boolean(srCode && String(srCode).trim());
  });
  /** Set when API (PagedResponse) returned no templates for market+division; used so popup only shows when backend says no checklist. */
  const noChecklistFromApiRef = useRef(false);
  const [savingDraftTemplateCode, setSavingDraftTemplateCode] = useState(null);
  // Derived for backward compat where single template is used
  const templateCode = templates[activeChecklistTab]?.templateCode ?? templates[0]?.templateCode ?? "";
  const rawQuestionTree = templateCode
    ? templateQuestionTrees[templateCode] ?? EMPTY_QUESTION_TREE
    : EMPTY_QUESTION_TREE;
  const materialSrCategoryKey = useMemo(
    () =>
      extractMaterialSrCategories(materialInvoiceDetails)
        .map((cat) => cat.code)
        .join(","),
    [materialInvoiceDetails]
  );
  const materialSrCategories = useMemo(
    () =>
      extractMaterialSrCategories(materialInvoiceDetails).map((cat) => {
        if (cat.code === GENERAL_SR_TYPE_CODE) return { ...cat, name: "All" };
        const match = srTypeOptions.find((t) => String(t.code) === String(cat.code));
        return { ...cat, name: match?.name || cat.name };
      }),
    [materialInvoiceDetails, srTypeOptions]
  );
  const srCategoriesForTabs = useMemo(
    () => materialSrCategories.filter((cat) => cat.code !== GENERAL_SR_TYPE_CODE),
    [materialSrCategories]
  );
  const activeSrCategoryCode =
    srCategoriesForTabs.length > 1
      ? srCategoriesForTabs[activeSrCategoryTab]?.code ?? null
      : srCategoriesForTabs.length === 1
      ? srCategoriesForTabs[0].code
      : null;
  const hasMaterialSrCategories = materialSrCategories.length > 0;
  const generalQuestionTree = useMemo(() => {
    if (!hasMaterialSrCategories) return rawQuestionTree;
    return filterGeneralQuestionTree(rawQuestionTree);
  }, [rawQuestionTree, hasMaterialSrCategories]);
  const categoryQuestionTree = useMemo(() => {
    if (!hasMaterialSrCategories || !activeSrCategoryCode) return EMPTY_QUESTION_TREE;
    return filterQuestionTreeBySrCategoryExcludingGeneral(
      rawQuestionTree,
      activeSrCategoryCode
    );
  }, [rawQuestionTree, hasMaterialSrCategories, activeSrCategoryCode]);
  const questionTree = useMemo(
    () => [...generalQuestionTree, ...categoryQuestionTree],
    [generalQuestionTree, categoryQuestionTree]
  );
  const usePerTabCategoryAnswers = srCategoriesForTabs.length > 1;
  const generalDynamicAnswers = useMemo(
    () => stripLegacyScopedKeys(templateCode ? templateAnswers[templateCode] || {} : {}),
    [templateCode, templateAnswers]
  );
  const categoryDynamicAnswers = useMemo(() => {
    if (!templateCode || !activeSrCategoryCode) return {};
    return srCategoryAnswers[templateCode]?.[activeSrCategoryCode] || {};
  }, [templateCode, srCategoryAnswers, activeSrCategoryCode]);
  const dynamicAnswers = useMemo(
    () => ({ ...generalDynamicAnswers, ...categoryDynamicAnswers }),
    [generalDynamicAnswers, categoryDynamicAnswers]
  );
  const errorsForTemplate = templateCode ? dynamicAnswerErrors[templateCode] || {} : {};

  // Build tree from flat checklist questions (ParentQuestionCode / parentQuestionCode).
  // Order matches DB: DisplayOrder, then CreatedOn, then questionCode.
  const buildChecklistTree = (flatQuestions) => {
    if (!flatQuestions || flatQuestions.length === 0) return [];
    const flat = Array.isArray(flatQuestions) ? flatQuestions : [];
    const map = new Map();
    flat.forEach((q, idx) => {
      const code = q.questionCode ?? q.QuestionCode;
      if (code == null || String(code).trim() === "") return;
      const node = {
        ...q,
        questionCode: q.questionCode ?? q.QuestionCode,
        parentQuestionCode: q.parentQuestionCode ?? q.ParentQuestionCode,
        questionText: q.questionText ?? q.QuestionText,
        questionAnsText: q.questionAnsText ?? q.QuestionAnsText,
        questionTypeCode: q.questionTypeCode ?? q.QuestionTypeCode,
        displayOrder: q.displayOrder ?? q.DisplayOrder ?? 0,
        createdOn: q.createdOn ?? q.CreatedOn,
        children: [],
        _flatIndex: idx,
      };
      map.set(String(code).trim(), node);
    });
    const roots = [];
    flat.forEach((q) => {
      const code = q.questionCode ?? q.QuestionCode;
      const node = code != null && String(code).trim() !== "" ? map.get(String(code).trim()) : null;
      if (!node) return;
      const parentCode = node.parentQuestionCode;
      const parentKey = parentCode != null && String(parentCode).trim() !== "" ? String(parentCode).trim() : null;
      if (parentKey && map.has(parentKey)) map.get(parentKey).children.push(node);
      else roots.push(node);
    });
    const sortByOrder = (a, b) => {
      const orderA = Number(a.displayOrder) ?? 0;
      const orderB = Number(b.displayOrder) ?? 0;
      if (orderA !== orderB) return orderA - orderB;
      const dateA = a.createdOn ? new Date(a.createdOn).getTime() : 0;
      const dateB = b.createdOn ? new Date(b.createdOn).getTime() : 0;
      if (dateA !== dateB) return dateA - dateB;
      const idxA = a._flatIndex ?? 999999;
      const idxB = b._flatIndex ?? 999999;
      if (idxA !== idxB) return idxA - idxB;
      const codeA = a.questionCode ?? "";
      const codeB = b.questionCode ?? "";
      return String(codeA).localeCompare(String(codeB));
    };
    const sortChildrenRecursive = (node) => {
      if (node.children && node.children.length) {
        node.children.sort(sortByOrder);
        node.children.forEach(sortChildrenRecursive);
      }
    };
    roots.sort(sortByOrder);
    roots.forEach(sortChildrenRecursive);
    return roots;
  };

  const setDynamicAnswerForTemplate = (tc, questionCode, value) => {
    setTemplateAnswers((prev) => ({
      ...prev,
      [tc]: { ...(prev[tc] || {}), [questionCode]: value },
    }));
    // Clear validation error when user enters value
    setDynamicAnswerErrors((prev) => {
      const tcErrs = prev[tc] || {};
      if (!tcErrs[questionCode]) return prev;
      const next = { ...tcErrs };
      delete next[questionCode];
      return { ...prev, [tc]: Object.keys(next).length ? next : undefined };
    });
  };
  const setDynamicAnswer = (questionCode, value) => {
    if (templateCode) setDynamicAnswerForTemplate(templateCode, questionCode, value);
  };
  const setSrCategoryAnswerForTemplate = (tc, srTypeCode, questionCode, value) => {
    if (!tc || !srTypeCode || !questionCode) return;
    setSrCategoryAnswers((prev) => ({
      ...prev,
      [tc]: {
        ...(prev[tc] || {}),
        [srTypeCode]: {
          ...((prev[tc] || {})[srTypeCode] || {}),
          [questionCode]: value,
        },
      },
    }));
    setDynamicAnswerErrors((prev) => {
      const errKey = buildCategoryErrorKey(srTypeCode, questionCode);
      const tcErrs = prev[tc] || {};
      if (!tcErrs[errKey]) return prev;
      const next = { ...tcErrs };
      delete next[errKey];
      return { ...prev, [tc]: Object.keys(next).length ? next : undefined };
    });
  };
  const setScopedAnswer = (questionCode, value, answerScope = "combined") => {
    if (!templateCode) return;
    if (answerScope === "category" && activeSrCategoryCode) {
      setSrCategoryAnswerForTemplate(templateCode, activeSrCategoryCode, questionCode, value);
      return;
    }
    setDynamicAnswerForTemplate(templateCode, questionCode, value);
  };
  const setScopedAnswerError = (questionCode, message, answerScope = "combined") => {
    if (!templateCode) return;
    const errorKey =
      answerScope === "category" && activeSrCategoryCode
        ? buildCategoryErrorKey(activeSrCategoryCode, questionCode)
        : questionCode;
    setDynamicAnswerError(templateCode, errorKey, message);
  };
  const getScopedAnswerError = (questionCode, answerScope = "combined") => {
    const errorKey =
      answerScope === "category" && activeSrCategoryCode
        ? buildCategoryErrorKey(activeSrCategoryCode, questionCode)
        : questionCode;
    return errorsForTemplate[errorKey] || errorsForTemplate[questionCode];
  };

  const normalizeOptionText = (value) =>
    String(value ?? "")
      .replace(/\*/g, "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();

  const conditionalMatchesSelected = (conditionalValue, selectedTexts) => {
    const raw = String(conditionalValue ?? "").trim();
    if (!raw) return false;
    const parts = raw
      .split(/[|,]/)
      .map((p) => normalizeOptionText(p))
      .filter(Boolean);
    if (parts.length === 0) return false;
    return parts.some((part) => selectedTexts.has(part));
  };

  const parseQuestionAnsTextOptions = (node) => {
    const ansText = String(node?.questionAnsText ?? node?.QuestionAnsText ?? "").trim();
    if (!ansText) return [];
    return ansText
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean)
      .map((text) => ({ questionText: text }));
  };

  const getSelectableOptionItems = (node) => {
    const children = Array.isArray(node?.children) ? node.children : [];
    const filtered = children.filter((child) => {
      const conditionalValue = String(child?.conditionalAnswerValue ?? child?.ConditionalAnswerValue ?? "").trim();
      const text = String(child?.questionText || child?.QuestionText || "").trim();
      if (conditionalValue) return false;
      return Boolean(text);
    });
    if (filtered.length) return filtered;
    const fromAns = parseQuestionAnsTextOptions(node);
    if (fromAns.length && children.some(
      (c) => String(c?.conditionalAnswerValue ?? c?.ConditionalAnswerValue ?? "").trim()
    )) {
      return fromAns;
    }
    return children;
  };

  const getQt001Qt003OptionItems = (node) => {
    const selectable = getSelectableOptionItems(node);
    if (selectable.length) return selectable;
    const fromAns = parseQuestionAnsTextOptions(node);
    if (fromAns.length) return fromAns;
    return Array.isArray(node?.children) ? node.children : [];
  };

  const getSelectedOptionTextsForNode = (node, answers) => {
    const typeCode = String(node?.questionTypeCode || node?.QuestionTypeCode || "").toUpperCase();
    const code = node?.questionCode || node?.QuestionCode;
    const val = code ? getStoredAnswerForQuestion(answers, code) : null;

    if (typeCode === "QT003") {
      if (Array.isArray(val)) return val.map((v) => String(v).trim()).filter(Boolean);
      if (typeof val === "string") {
        const s = val.trim();
        if (!s) return [];
        if (s.startsWith("[") && s.endsWith("]")) {
          try {
            const parsed = JSON.parse(s);
            if (Array.isArray(parsed)) return parsed.map((v) => String(v).trim()).filter(Boolean);
          } catch (_) {
            // ignore malformed draft string and fall back to single selection
          }
        }
        return [s];
      }
      return val != null ? [String(val).trim()] : [];
    }

    if (val == null) return [];
    const single = String(val).trim();
    return single ? [single] : [];
  };

  // QT001 / QT003: show direct children for each selected option only; deeper levels recurse
  // via renderDynamicQuestion (supports any option count and n-level nesting).
  const getVisibleConditionalTextChildren = (node, answers) => {
    const parentType = String(node?.questionTypeCode || node?.QuestionTypeCode || "").toUpperCase();
    if (parentType !== "QT001" && parentType !== "QT003") return [];

    const children = Array.isArray(node?.children) ? node.children : [];
    const selectedTexts = new Set(
      getSelectedOptionTextsForNode(node, answers).map((text) => normalizeOptionText(text))
    );
    if (selectedTexts.size === 0) return [];

    const optionTexts = new Set(
      getQt001Qt003OptionItems(node)
        .map((opt) => normalizeOptionText(opt?.questionText || opt?.QuestionText || ""))
        .filter(Boolean)
    );

    const byKey = new Map();
    const addDirectChild = (child) => {
      if (!child) return;
      const childText = normalizeOptionText(child?.questionText || child?.QuestionText || "");
      const conditionalValue = String(
        child?.conditionalAnswerValue ?? child?.ConditionalAnswerValue ?? ""
      ).trim();
      const branchKids = Array.isArray(child?.children) ? child.children : [];
      // Bare option rows (no nested content) stay in the parent checkbox/radio list only.
      if (childText && optionTexts.has(childText) && !conditionalValue && branchKids.length === 0) {
        return;
      }
      const t = String(child.questionTypeCode || child.QuestionTypeCode || "").toUpperCase();
      const key =
        String(child.questionCode || child.QuestionCode || "").trim() ||
        `${String(child.questionText || child.QuestionText || "").trim()}__${t}`;
      if (!byKey.has(key)) byKey.set(key, child);
    };

    children.forEach((optionNode) => {
      const optionText = normalizeOptionText(
        optionNode?.questionText || optionNode?.QuestionText || ""
      );
      if (!optionText || !selectedTexts.has(optionText)) return;
      addDirectChild(optionNode);
    });

    children.forEach((child) => {
      const conditionalValue = child?.conditionalAnswerValue ?? child?.ConditionalAnswerValue ?? "";
      if (!conditionalMatchesSelected(conditionalValue, selectedTexts)) return;
      addDirectChild(child);
    });

    children.forEach((child) => {
      const childText = normalizeOptionText(child?.questionText || child?.QuestionText || "");
      if (childText && selectedTexts.has(childText) && !optionTexts.has(childText)) {
        addDirectChild(child);
      }
    });

    return Array.from(byKey.values());
  };

  /** Option shells / labels only — not standalone inputs stored on the same row as Yes/No. */
  const OPTION_ROW_LABEL_ONLY_TYPE_CODES = new Set(["QT001", "QT002", "QT003"]);

  const isChecklistOptionBasedParentType = (typeCode) => {
    const t = String(typeCode || "").toUpperCase();
    return t === "QT001" || t === "QT003" || t === "QT011" || t === "QT012";
  };

  const normalizeChecklistOptionParentNode = (node) => {
    const t = String(node?.questionTypeCode || node?.QuestionTypeCode || "").toUpperCase();
    if (t === "QT011") return { ...node, questionTypeCode: "QT003", QuestionTypeCode: "QT003" };
    if (t === "QT012") return { ...node, questionTypeCode: "QT001", QuestionTypeCode: "QT001" };
    return node;
  };

  /** Tree-editor pattern: option label row (e.g. Yes) with an input type on the same node. */
  const isOptionRowTypedFollowUpChild = (child) => {
    if (!child) return false;
    const branchKids = Array.isArray(child?.children) ? child.children : [];
    if (branchKids.length > 0) return false;
    const conditionalValue = String(
      child?.conditionalAnswerValue ?? child?.ConditionalAnswerValue ?? ""
    ).trim();
    if (conditionalValue) return false;
    const t = String(child?.questionTypeCode || child?.QuestionTypeCode || "").toUpperCase();
    if (!t || OPTION_ROW_LABEL_ONLY_TYPE_CODES.has(t)) return false;
    return true;
  };

  const getVisibleOptionRowTypedFollowUps = (node, answers) => {
    const parentType = String(node?.questionTypeCode || node?.QuestionTypeCode || "").toUpperCase();
    if (!isChecklistOptionBasedParentType(parentType)) return [];

    const normalizedParent = normalizeChecklistOptionParentNode(node);
    const children = Array.isArray(node?.children) ? node.children : [];
    const selectedTexts = new Set(
      getSelectedOptionTextsForNode(normalizedParent, answers).map((text) =>
        normalizeOptionText(text)
      )
    );
    if (selectedTexts.size === 0) return [];

    const optionTexts = new Set(
      getQt001Qt003OptionItems(normalizedParent)
        .map((opt) => normalizeOptionText(opt?.questionText || opt?.QuestionText || ""))
        .filter(Boolean)
    );

    const byKey = new Map();
    children.forEach((child) => {
      const childText = normalizeOptionText(child?.questionText || child?.QuestionText || "");
      if (!childText || !selectedTexts.has(childText) || !optionTexts.has(childText)) return;
      if (!isOptionRowTypedFollowUpChild(child)) return;
      const t = String(child?.questionTypeCode || child?.QuestionTypeCode || "").toUpperCase();
      const key =
        String(child?.questionCode || child?.QuestionCode || "").trim() ||
        `${childText}__${t}`;
      if (!byKey.has(key)) byKey.set(key, child);
    });
    return Array.from(byKey.values());
  };

  const mergeVisibleChecklistFollowUpChildren = (node, answers) => {
    const normalizedParent = normalizeChecklistOptionParentNode(node);
    const byKey = new Map();
    const addChild = (child) => {
      if (!child) return;
      const t = String(child?.questionTypeCode || child?.QuestionTypeCode || "").toUpperCase();
      const key =
        String(child?.questionCode || child?.QuestionCode || "").trim() ||
        `${String(child?.questionText || child?.QuestionText || "").trim()}__${t}`;
      if (!byKey.has(key)) byKey.set(key, child);
    };
    getVisibleConditionalTextChildren(normalizedParent, answers).forEach(addChild);
    getVisibleOptionRowTypedFollowUps(node, answers).forEach(addChild);
    return Array.from(byKey.values());
  };

  const buildOptionRowFollowUpRenderOptions = (child, parentNode, baseOptions = {}) => {
    const parentType = String(
      parentNode?.questionTypeCode || parentNode?.QuestionTypeCode || ""
    ).toUpperCase();
    if (!isChecklistOptionBasedParentType(parentType)) return baseOptions;
    const normalizedParent = normalizeChecklistOptionParentNode(parentNode);
    const optionTexts = new Set(
      getQt001Qt003OptionItems(normalizedParent)
        .map((opt) => normalizeOptionText(opt?.questionText || opt?.QuestionText || ""))
        .filter(Boolean)
    );
    const childText = normalizeOptionText(child?.questionText || child?.QuestionText || "");
    if (!childText || !optionTexts.has(childText) || !isOptionRowTypedFollowUpChild(child)) {
      return baseOptions;
    }
    return {
      ...baseOptions,
      optionRowTypedFollowUp: true,
      parentOptionText: child?.questionText || child?.QuestionText || "",
    };
  };

  const getChecklistLevelInputText = (questionNode) =>
    String(questionNode?.questionAnsText ?? questionNode?.QuestionAnsText ?? "").trim();

  /** QT014 / QT015: level text as question label (same style as QT001 radio labels). */
  const renderChecklistLevelInputLabel = (levelText, {
    typeIcon,
    labelClass,
    labelStyle,
    mandatory,
    indentStyle,
  }) => {
    if (!levelText) return null;
    return (
      <div
        className="dynamic-q-option-item dynamic-q-row dynamic-q-product-details"
        style={indentStyle}
      >
        <span className="dynamic-q-icon" aria-hidden="true">
          {typeIcon}
        </span>
        <div className="dynamic-q-control dynamic-q-control-inline-auto">
          <label className={`${labelClass} dynamic-q-label-qt001-qt003`} style={labelStyle}>
            {levelText}
            {mandatory && <span className="text-danger ms-1">*</span>}
          </label>
        </div>
      </div>
    );
  };

  const getTodayISODate = () => getLocalTodayISODate();

  // QT007: no special-character or format validation; only required (mandatory) is enforced on submit.
  const validateQt007Text = (value) => "";

  const setDynamicAnswerError = (tc, questionCode, message) => {
    setDynamicAnswerErrors((prev) => {
      const next = { ...prev };
      const tcErrs = { ...(next[tc] || {}) };
      if (message) tcErrs[questionCode] = message;
      else delete tcErrs[questionCode];
      next[tc] = Object.keys(tcErrs).length ? tcErrs : undefined;
      return next;
    });
  };
  const getQt009UploadKey = (tc, questionCode, answerScope = "combined", srTypeCode = activeSrCategoryCode) => {
    if (answerScope === "category" && srTypeCode) {
      return `${tc || ""}::${srTypeCode}::${questionCode || ""}`;
    }
    return `${tc || ""}::${questionCode || ""}`;
  };

  const clearGreenlamBrandingCaptureAnswers = (tc = templateCode) => {
    const tree = tc ? templateQuestionTrees[tc] || [] : [];
    const captureNodes = collectGreenlamBrandingCaptureNodes(tree);
    if (!captureNodes.length) return;
    captureNodes.forEach((node) => {
      const qCode = node?.questionCode || node?.QuestionCode;
      if (!qCode) return;
      if (tc) setDynamicAnswerForTemplate(tc, qCode, "");
      if (tc) setDynamicAnswerError(tc, qCode, undefined);
      const uploadKey = getQt009UploadKey(tc, qCode);
      setQt009ImageAnswers((prev) => {
        if (!prev[uploadKey]) return prev;
        const next = { ...prev };
        delete next[uploadKey];
        return next;
      });
    });
  };

  const syncComplaintAttendedByForSiteVisit = (tc = templateCode, siteVisitYes) => {
    const tree = tc ? templateQuestionTrees[tc] || [] : [];
    const attendedNodes = collectComplaintAttendedByTextNodes(tree);
    if (!attendedNodes.length || !tc) return;
    const loginName = getLoggedInUserDisplayName();
    attendedNodes.forEach((node) => {
      const qCode = node?.questionCode || node?.QuestionCode;
      if (!qCode) return;
      if (siteVisitYes) {
        if (loginName) setDynamicAnswerForTemplate(tc, qCode, loginName);
      } else {
        setDynamicAnswerForTemplate(tc, qCode, "");
        setDynamicAnswerError(tc, qCode, undefined);
      }
    });
  };
  const parseQt009AnswerToItems = (answerValue) => {
    if (Array.isArray(answerValue)) {
      return answerValue
        .map((p, idx) => {
          const path = String(p || "").trim();
          if (!path) return null;
          return {
            id: `path_${idx}_${Date.now()}_${Math.random()}`,
            filePath: path,
            name: decodeURIComponent(path.split("/").pop() || path),
            type: "image/*",
            size: null,
          };
        })
        .filter(Boolean);
    }
    if (typeof answerValue === "string" && answerValue.trim()) {
      const raw = answerValue.trim();
      if (raw.startsWith("[") && raw.endsWith("]")) {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) return parseQt009AnswerToItems(parsed);
        } catch (_) {
          // keep as plain path string
        }
      }
      return [{
        id: `path_0_${Date.now()}_${Math.random()}`,
        filePath: raw,
        name: decodeURIComponent(raw.split("/").pop() || raw),
        type: "image/*",
        size: null,
      }];
    }
    return [];
  };

  const uploadQt009AnswersForTemplate = async (tc, generalAnswers, categoryAnswersBySr = {}) => {
    const tree = templateQuestionTrees[tc] || [];
    const nextGeneral = stripLegacyScopedKeys(generalAnswers);
    const nextCategory = {};
    Object.entries(categoryAnswersBySr || {}).forEach(([sr, map]) => {
      nextCategory[sr] = { ...(map || {}) };
    });

    const qt009Targets = [];
    const walk = (nodes, ctx) => {
      (nodes || []).forEach((node) => {
        const code = node.questionCode || node.QuestionCode;
        const typeCode = (node.questionTypeCode || node.QuestionTypeCode || "").toUpperCase();
        if ((typeCode === "QT009" || typeCode === "QT014") && code) {
          qt009Targets.push({ qCode: code, ...ctx });
        }
        if (node.children?.length) walk(node.children, ctx);
      });
    };
    const tabCats = extractMaterialSrCategories(materialInvoiceDetails).filter(
      (c) => c.code !== GENERAL_SR_TYPE_CODE
    );
    if (tabCats.length > 0) {
      walk(filterGeneralQuestionTree(tree), { answerScope: "general", srTypeCode: null });
      tabCats.forEach((cat) => {
        walk(filterQuestionTreeBySrCategoryExcludingGeneral(tree, cat.code), {
          answerScope: "category",
          srTypeCode: cat.code,
        });
      });
    } else {
      walk(tree, { answerScope: "general", srTypeCode: null });
    }

    for (const { qCode, answerScope, srTypeCode } of qt009Targets) {
      const key = getQt009UploadKey(tc, qCode, answerScope, srTypeCode);
      const answersMap =
        answerScope === "category" && srTypeCode
          ? (nextCategory[srTypeCode] ||= {})
          : nextGeneral;
      const localItems = Array.isArray(qt009ImageAnswers[key]) ? qt009ImageAnswers[key] : [];
      const fallbackFromAnswer = parseQt009AnswerToItems(answersMap[qCode]);
      const sourceItems = localItems.length > 0 ? localItems : fallbackFromAnswer;
      if (sourceItems.length === 0) continue;

      const finalizedItems = [];
      for (const item of sourceItems) {
        if (item?.file instanceof File) {
          setQt009Uploading((prev) => ({ ...prev, [key]: true }));
          try {
            const response = await complaintAPI.uploadChecklistAttachment(
              item.file,
              "DamageImage"
            );
            const uploaded = response?.data ?? response?.Data ?? response;
            const uploadedPath = uploaded?.filePath ?? uploaded?.FilePath ?? "";
            if (uploadedPath) {
              finalizedItems.push({
                id: item.id,
                file: null,
                filePath: String(uploadedPath).trim(),
                preview: item.preview || "",
                name: uploaded?.fileName ?? uploaded?.FileName ?? item.name ?? "",
                type: uploaded?.fileType ?? uploaded?.FileType ?? item.type ?? "image/*",
                size: uploaded?.fileSize ?? uploaded?.FileSize ?? item.size ?? null,
              });
            }
          } catch (error) {
            throw new Error(
              error?.response?.data?.message || error?.message || "Failed to upload checklist image."
            );
          } finally {
            setQt009Uploading((prev) => ({ ...prev, [key]: false }));
          }
        } else {
          const existingPath = String(item?.filePath || "").trim();
          if (existingPath) finalizedItems.push(item);
        }
      }

      const uploadedPaths = finalizedItems
        .map((x) => String(x?.filePath || "").trim())
        .filter(Boolean);
      answersMap[qCode] = uploadedPaths;
      setQt009ImageAnswers((prev) => ({ ...prev, [key]: finalizedItems }));
    }

    return { general: nextGeneral, category: nextCategory };
  };

  const getSelectedInvoiceProduct = () => {
    const products = invoiceDetails && (
      Array.isArray(invoiceDetails.products)
        ? invoiceDetails.products
        : Array.isArray(invoiceDetails.Products)
          ? invoiceDetails.Products
          : []
    );
    if (!products || products.length === 0) return null;
    const urlProdCode = searchParams.get("productCode") || productCode;
    return (
      products.find(
        (p) => (p.ProductCode || p.productCode || "").toString() === String(urlProdCode || "").trim()
      ) || products[0]
    );
  };

  const normalizeQuestionLabel = normalizeChecklistQuestionLabel;

  const handleInputChange = (field) => (e) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // QT010 default date: prefill current date when answer is empty; clamp future dates (all templates).
  useEffect(() => {
    if (!templates?.length) return;
    const today = getLocalTodayISODate();
    const clampQt010Answers = (tree, tcAnswers) => {
      let tcChanged = false;
      const nextAnswers = { ...tcAnswers };
      const walk = (nodes) => {
        (nodes || []).forEach((node) => {
          const code = node.questionCode || node.QuestionCode;
          const typeCode = (node.questionTypeCode || node.QuestionTypeCode || "").toUpperCase();
          if (
            typeCode === "QT010" &&
            code &&
            !isManufacturingDateQuestion(node.questionText || node.QuestionText || "")
          ) {
            const current = nextAnswers[code];
            if (current == null || String(current).trim() === "") {
              nextAnswers[code] = today;
              tcChanged = true;
            } else {
              const clamped = clampChecklistDateToToday(current);
              const normalized = normalizeChecklistDateValue(current);
              if (clamped && normalized && clamped !== normalized) {
                nextAnswers[code] = clamped;
                tcChanged = true;
              }
            }
          }
          if (node.children?.length) walk(node.children);
        });
      };
      walk(tree);
      return { answers: nextAnswers, changed: tcChanged };
    };
    setTemplateAnswers((prev) => {
      let changed = false;
      const next = { ...prev };
      templates.forEach((t) => {
        const tc = t.templateCode;
        if (!tc) return;
        const tree = templateQuestionTrees[tc] || [];
        if (!Array.isArray(tree) || tree.length === 0) return;
        const { answers: tcAnswers, changed: tcChanged } = clampQt010Answers(tree, next[tc] || {});
        if (tcChanged) {
          next[tc] = tcAnswers;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setSrCategoryAnswers((prev) => {
      let changed = false;
      const next = { ...prev };
      templates.forEach((t) => {
        const tc = t.templateCode;
        if (!tc) return;
        const tree = templateQuestionTrees[tc] || [];
        if (!Array.isArray(tree) || tree.length === 0) return;
        const tcSr = { ...(next[tc] || {}) };
        let tcChanged = false;
        Object.keys(tcSr).forEach((srCode) => {
          const { answers, changed: srChanged } = clampQt010Answers(tree, tcSr[srCode] || {});
          if (srChanged) {
            tcSr[srCode] = answers;
            tcChanged = true;
          }
        });
        if (tcChanged) {
          next[tc] = tcSr;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [templates, templateQuestionTrees, checklistDraftAnswersRevision]);

  // When "Did you visit the site?" = Yes, prefill Complaint Attended by with logged-in user (draft reload / tree load).
  useEffect(() => {
    if (!templates?.length) return;
    const loginName = getLoggedInUserDisplayName();
    if (!loginName) return;
    setTemplateAnswers((prev) => {
      let changed = false;
      const next = { ...prev };
      templates.forEach((t) => {
        const tc = t.templateCode;
        if (!tc) return;
        const tree = templateQuestionTrees[tc] || [];
        if (!isSiteVisitAffirmative(tree, prev[tc] || {})) return;
        const attendedNodes = collectComplaintAttendedByTextNodes(tree);
        if (!attendedNodes.length) return;
        const tcAnswers = { ...(next[tc] || {}) };
        let tcChanged = false;
        attendedNodes.forEach((node) => {
          const qCode = node?.questionCode || node?.QuestionCode;
          if (!qCode) return;
          if (tcAnswers[qCode] !== loginName) {
            tcAnswers[qCode] = loginName;
            tcChanged = true;
          }
        });
        if (tcChanged) {
          next[tc] = tcAnswers;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [templates, templateQuestionTrees, checklistDraftAnswersRevision]);

  // Raise-complaint-checklist: keep all collapsible QT006 sections expanded when template/questions load or change.
  useEffect(() => {
    if (!templateCode) return;
    if (!Array.isArray(questionTree) || questionTree.length === 0) return;

    setCollapsedSections((prev) => {
      if (prev.size === 0) return prev;
      return new Set();
    });
  }, [templateCode, questionTree]);

  // Auto-align child question controls in each section row:
  // if one label wraps to 2/3 lines, other controls in that same row align accordingly.
  useEffect(() => {
    const normalizeChildRowLabelHeights = () => {
      const rowNodes = document.querySelectorAll(
        ".raise-page .dynamic-q-section-children .row.g-3"
      );
      rowNodes.forEach((rowNode) => {
        const rowItems = Array.from(rowNode.children).filter((el) => {
          return (
            el instanceof HTMLElement &&
            el.classList.contains("dynamic-q-row") &&
            el.classList.contains("dynamic-q-product-details") &&
            !el.classList.contains("dynamic-q-section") &&
            !el.classList.contains("dynamic-q-section-toggle")
          );
        });
        if (rowItems.length < 2) return;

        const labels = rowItems
          .map((item) =>
            item.querySelector(
              ".dynamic-q-control > .component-details-label"
            )
          )
          .filter(Boolean);
        if (labels.length < 2) return;

        labels.forEach((labelEl) => {
          labelEl.style.minHeight = "";
        });

        const maxHeight = labels.reduce((mx, labelEl) => {
          const h = Math.ceil(labelEl.getBoundingClientRect().height || 0);
          return h > mx ? h : mx;
        }, 0);

        if (maxHeight > 0) {
          labels.forEach((labelEl) => {
            labelEl.style.minHeight = `${maxHeight}px`;
          });
        }
      });
    };

    const rafId = window.requestAnimationFrame(normalizeChildRowLabelHeights);
    const handleResize = () => normalizeChildRowLabelHeights();
    window.addEventListener("resize", handleResize);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", handleResize);
    };
  }, [templateCode, questionTree, dynamicAnswers, collapsedSections, activeChecklistTab, isChecklistQuestionsCollapsed]);

  // For QT001/QT003: keep label+options on same line only when both fit.
  // If they overflow parent width, move all options to the next line.
  useEffect(() => {
    const applyInlineAutoFit = () => {
      const getNaturalOptionsWidth = (optionsEl) => {
        if (!(optionsEl instanceof HTMLElement)) return 0;
        const inner = optionsEl.firstElementChild;
        if (!(inner instanceof HTMLElement)) {
          return Math.ceil(optionsEl.scrollWidth || optionsEl.getBoundingClientRect().width || 0);
        }
        const items = Array.from(inner.children).filter((el) => el instanceof HTMLElement);
        if (items.length === 0) {
          return Math.ceil(inner.scrollWidth || inner.getBoundingClientRect().width || 0);
        }
        const innerStyles = window.getComputedStyle(inner);
        const gap =
          parseFloat(innerStyles.columnGap || innerStyles.gap || "0") ||
          parseFloat(innerStyles.getPropertyValue("row-gap") || "0") ||
          0;
        const itemsWidth = items.reduce(
          (sum, item) => sum + Math.ceil(item.getBoundingClientRect().width || 0),
          0
        );
        return Math.ceil(itemsWidth + gap * Math.max(items.length - 1, 0));
      };

      const controls = document.querySelectorAll(
        ".raise-page .dynamic-q-control.dynamic-q-control-inline-auto"
      );
      controls.forEach((controlEl) => {
        const labelEl = controlEl.querySelector(":scope > .component-details-label");
        const optionsEl = controlEl.querySelector(":scope > .dynamic-q-inline-options");
        if (!(labelEl instanceof HTMLElement) || !(optionsEl instanceof HTMLElement)) return;

        const parentContainer =
          controlEl.closest(".dynamic-q-section-children") ||
          controlEl.closest(".dynamic-q-section-with-children") ||
          controlEl.closest(".qtnstrvw") ||
          controlEl.closest(".checklist-form");
        const availableWidth =
          (parentContainer instanceof HTMLElement ? parentContainer.clientWidth : 0) ||
          controlEl.clientWidth ||
          0;
        if (!availableWidth) return;

        const labelWidth = Math.ceil(labelEl.getBoundingClientRect().width || 0);
        const optionsOneLineWidth = getNaturalOptionsWidth(optionsEl);
        const gap = parseFloat(window.getComputedStyle(controlEl).columnGap || "0") || 0;
        const needsBreak = labelWidth + gap + optionsOneLineWidth > availableWidth;

        controlEl.classList.toggle("dynamic-q-control-inline-break", needsBreak);
      });
    };

    const rafId = window.requestAnimationFrame(applyInlineAutoFit);
    const handleResize = () => applyInlineAutoFit();
    window.addEventListener("resize", handleResize);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", handleResize);
    };
  }, [templateCode, questionTree, dynamicAnswers, collapsedSections, activeChecklistTab, isChecklistQuestionsCollapsed]);

  const handlePrelamChange = (field) => (e) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setPrelamForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePrelamCheckbox = (field, subField) => (e) => {
    setPrelamForm((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [subField]: e.target.checked,
      },
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Filter to only allow images
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    const validFiles = files.filter((file) => allowedTypes.includes(file.type));

    if (validFiles.length !== files.length) {
      alert("Only JPG and PNG images are allowed.");
    }

    // Check total upload size (max 12 MB) across selected damage images.
    const existingSize = uploadFiles.reduce(
      (total, item) => total + (Number(item?.file?.size ?? item?.size ?? item?.fileSize) || 0),
      0
    );
    const incomingSize = validFiles.reduce(
      (total, file) => total + (Number(file?.size) || 0),
      0
    );
    if (existingSize + incomingSize > MAX_DAMAGE_IMAGE_TOTAL_BYTES) {
      alert("Total image size cannot exceed 12 MB.");
      e.target.value = "";
      return;
    }

    validFiles.forEach((file) => {
      // For images, create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        const fileId = Date.now() + Math.random();
        setUploadFiles((prev) => [
          ...prev,
          {
            id: fileId,
            file: file,
            name: file.name,
            preview: reader.result,
            type: file.type,
          },
        ]);
      };
      reader.readAsDataURL(file);
    });

    // Reset the input so the same file can be selected again
    e.target.value = "";
  };

  const handleRemoveFile = (id) => {
    setUploadFiles((prev) => prev.filter((item) => item.id !== id));
  };

  const handlePrelamFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Filter to only allow images
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    const validFiles = files.filter((file) => allowedTypes.includes(file.type));

    if (validFiles.length !== files.length) {
      alert("Only JPG and PNG images are allowed.");
    }

    // Check total upload size (max 12 MB) across selected damage images.
    const existingSize = prelamUploadFiles.reduce(
      (total, item) => total + (Number(item?.file?.size ?? item?.size ?? item?.fileSize) || 0),
      0
    );
    const incomingSize = validFiles.reduce(
      (total, file) => total + (Number(file?.size) || 0),
      0
    );
    if (existingSize + incomingSize > MAX_DAMAGE_IMAGE_TOTAL_BYTES) {
      alert("Total image size cannot exceed 12 MB.");
      e.target.value = "";
      return;
    }

    validFiles.forEach((file) => {
      // For images, create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        const fileId = Date.now() + Math.random();
        setPrelamUploadFiles((prev) => [
          ...prev,
          {
            id: fileId,
            file: file,
            name: file.name,
            preview: reader.result,
            type: file.type,
          },
        ]);
      };
      reader.readAsDataURL(file);
    });

    // Reset the input so the same file can be selected again
    e.target.value = "";
  };

  const handlePrelamRemoveFile = (id) => {
    setPrelamUploadFiles((prev) => prev.filter((item) => item.id !== id));
  };

  const handleNextTab = async () => {
    if (savingDraft || submittingFinal) return;

    // Multiple SR Category tabs within current checklist template.
    if (srCategoriesForTabs.length > 1 && activeSrCategoryTab < srCategoriesForTabs.length - 1) {
      const currentCode = srCategoriesForTabs[activeSrCategoryTab]?.code;
      const validationResult = validateDynamicChecklistAnswers({
        srCategoryCodeOnly: currentCode,
      });
      if (validationResult !== true) {
        setIsChecklistQuestionsCollapsed(false);
        Swal.fire({
          title: "Validation Error",
          text: "Please fill in all required checklist fields for this SR Category before continuing.",
          icon: "warning",
          confirmButtonColor: "#ff9800",
        });
        return;
      }
      setActiveSrCategoryTab((prev) => prev + 1);
      return;
    }

    // Multiple checklist tabs: if not on last tab, go to next checklist tab
    if (templates.length > 1 && activeChecklistTab < templates.length - 1) {
      const dateValidation = validateDynamicChecklistAnswers({ onlyFutureDate: true });
      if (dateValidation !== true) {
        setIsChecklistQuestionsCollapsed(false);
        Swal.fire({
          title: "Validation Error",
          text: firstChecklistValidationMessage(dateValidation),
          icon: "warning",
          confirmButtonColor: "#ff9800",
        });
        return;
      }
      setActiveChecklistTab((prev) => prev + 1);
      return;
    }

    // Last checklist tab (or single checklist tab): Submit and raise SR
    if (templates.length > 0 && activeChecklistTab === templates.length - 1) {
      await handleSubmitAndRaiseSR();
      return;
    }

    // No checklist: PPB tab – save draft and switch to prelam
    if (templates.length === 0 && activeTab === "ppb") {
      try {
        setSavingDraft(true);
        const invoiceNumber =
          formData.salesInvoiceNo ||
          searchParams.get("invoiceNo") ||
          invoiceNo ||
          "";
        const prodCode = searchParams.get("productCode") || productCode || "";

        if (!invoiceNumber || !prodCode) {
          console.warn("Invoice number or product code missing.");
          setActiveTab("prelam");
          setSavingDraft(false);
          return;
        }

        const serializedUpload = serializeUploadFiles(uploadFiles);
        const srCodeParam = searchParams.get("srCode") || undefined;
        let complaintReceivedDateParam = null;
        let checklistAttachmentsParam = [];
        if (complaintReceivedDate && String(complaintReceivedDate).trim()) {
          const d = complaintReceivedDate.trim();
          complaintReceivedDateParam = /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : (d.includes("/") || d.includes("-") ? toISODate(d) : d) || null;
        }
        if (srCodeParam) {
          checklistAttachmentsParam = await buildChecklistAttachmentsForDraft(uploadFiles, videoUrl);
        }
        const floorFormPayload = {
          ...formData,
          uploadFiles: serializedUpload,
          videoUrl,
          invoiceCopyFiles,
          formData,
          prelamForm,
          complaintReceivedDate,
          visitReportComments,
        };
        const catgCode = searchParams.get("productCategoryId") || searchParams.get("catgCode");
        const draftData = {
          invoiceNo: invoiceNumber,
          productCode: prodCode,
          category: "PPB",
          complaintQty:
            formData.defectiveQty ||
            formData.defectiveQtyNotUsed ||
            formData.UsedFurnitureQuantity ||
            formData.qty ||
            "",
          floorForm: floorFormPayload,
          complaintLines: null,
          catgCode: catgCode || undefined,
          srCode: srCodeParam,
          marketCode: searchParams.get("MarketCode") || undefined,
          divisionName:
            searchParams.get("businessVertical") ||
            searchParams.get("divisionName") ||
            undefined,
          ...(complaintReceivedDateParam ? { complaintReceivedDate: complaintReceivedDateParam } : {}),
          ...(visitReportComments && String(visitReportComments).trim()
            ? { visitReportComments: String(visitReportComments).trim() }
            : {}),
          ...(checklistAttachmentsParam.length > 0 ? { checklistAttachments: checklistAttachmentsParam } : {}),
        };

        const response = await complaintAPI.saveChecklistDraft(draftData);
        if (response.success) console.log("Draft saved successfully");
      } catch (error) {
        console.error("Error saving draft:", error);
      } finally {
        setSavingDraft(false);
        setActiveTab("prelam");
      }
      return;
    }

    // Prelam tab (no checklist): Submit and raise SR
    if (templates.length === 0 && activeTab === "prelam") {
      await handleSubmitAndRaiseSR();
    }
  };

  const validateVisitReportComments = () => {
    if (templates.length === 0) return true;
    if (hasVisitReportCommentsValue(visitReportComments)) {
      setVisitReportCommentsError("");
      return true;
    }
    setVisitReportCommentsError("Visit Report Comments is required.");
    setIsUploadSectionCollapsed(false);
    Swal.fire({
      title: "Validation Error",
      text: "Visit Report Comments is required.",
      icon: "warning",
      confirmButtonColor: "#ff9800",
    });
    return false;
  };

  // Validate mandatory dynamic checklist answers and QT007 format (no special characters); returns true if valid, else sets dynamicAnswerErrors and returns false.
  const validateDynamicChecklistAnswers = (options = {}) => {
    const { srCategoryCodeOnly = null, onlyFutureDate = false } = options;
    const errors = {};
    templates.forEach((t) => {
      const tc = t.templateCode;
      const rawTree = templateQuestionTrees[tc] || [];
      const categories = extractMaterialSrCategories(materialInvoiceDetails);
      const tabCategories = categories.filter((c) => c.code !== GENERAL_SR_TYPE_CODE);
      const perTabAnswers = tabCategories.length > 1;
      const storedGeneral = stripLegacyScopedKeys(templateAnswers[tc] || {});
      const storedSrCategory = srCategoryAnswers[tc] || {};
      const validationPasses = [];
      if (categories.length > 0) {
        validationPasses.push({
          tree: filterGeneralQuestionTree(rawTree),
          answers: buildChecklistAnswersForValidation(
            filterGeneralQuestionTree(rawTree),
            mergeChecklistAnswerMaps(storedGeneral, flattenAllSrCategoryAnswers(storedSrCategory)),
            materialSectionTotals
          ),
          errorKeyFor: (qCode) => qCode,
          validateAsGeneralOnly: true,
        });
        const catsToValidate =
          srCategoryCodeOnly != null
            ? tabCategories.filter((c) => c.code === srCategoryCodeOnly)
            : tabCategories;
        catsToValidate.forEach((cat) => {
          const catTree = filterQuestionTreeBySrCategoryExcludingGeneral(rawTree, cat.code);
          validationPasses.push({
            tree: catTree,
            answers: buildChecklistAnswersForValidation(
              catTree,
              mergeChecklistAnswerMaps(
                storedGeneral,
                getStoredAnswersForSrCategory(storedSrCategory, cat.code)
              ),
              materialSectionTotals
            ),
            errorKeyFor: (qCode) =>
              perTabAnswers ? buildCategoryErrorKey(cat.code, qCode) : qCode,
          });
        });
      } else {
        validationPasses.push({
          tree: rawTree,
          answers: buildChecklistAnswersForValidation(
            rawTree,
            storedGeneral,
            materialSectionTotals
          ),
          errorKeyFor: (qCode) => qCode,
        });
      }
      validationPasses.forEach(({ tree, answers, errorKeyFor, validateAsGeneralOnly = false }) => {
        const walk = (nodes, walkCtx = { inProductDetailsSection: false }) => {
        (nodes || []).forEach((node) => {
          // Category-scoped questions are answered under srCategoryAnswers and validated
          // in per-category passes — do not require them again from templateAnswers.
          if (
            validateAsGeneralOnly &&
            categories.length > 0 &&
            !isGeneralSrCategoryQuestion(node)
          ) {
            return;
          }

          const code = node.questionCode || node.QuestionCode;
          const typeCode = (node.questionTypeCode || node.QuestionTypeCode || "").toUpperCase();
          const questionText = node.questionText || node.QuestionText || "";

          if (onlyFutureDate) {
            const val = code ? getStoredAnswerForQuestion(answers, code) : null;
            const isReceiptDateField =
              typeCode === "QT010" || isComplaintReceiptDateQuestion(questionText);
            if (
              isManufacturingDateQuestion(questionText) &&
              code &&
              val != null &&
              String(val).trim() !== ""
            ) {
              const mfdErr = validateManufacturingDate(val);
              if (mfdErr) {
                errors[tc] = errors[tc] || {};
                errors[tc][errorKeyFor(code)] = mfdErr;
              }
            } else if (isReceiptDateField && code && val != null && String(val).trim() !== "") {
              if (isChecklistDateAfterToday(val)) {
                errors[tc] = errors[tc] || {};
                errors[tc][errorKeyFor(code)] = CHECKLIST_FUTURE_DATE_ERROR;
              }
            }
            if (typeCode === "QT002") {
              walk(node.children || [], walkCtx);
              return;
            }
            if (typeCode === "QT013") {
              walk(node.children || [], walkCtx);
              return;
            }
            if (isChecklistOptionBasedParentType(typeCode)) {
              walk(mergeVisibleChecklistFollowUpChildren(node, answers), walkCtx);
              return;
            }
            const childWalkCtx =
              typeCode === "QT006"
                ? {
                    inProductDetailsSection: isProductDetailsSectionLabel(questionText),
                  }
                : walkCtx;
            walk(node.children || [], childWalkCtx);
            return;
          }

          // QT002: label row; nested children validated when parent branch is visible.
          if (typeCode === "QT002") {
            walk(node.children || [], walkCtx);
            return;
          }

          // QT013 (Level): transparent grouping — validate nested questions only.
          if (typeCode === "QT013") {
            walk(node.children || [], walkCtx);
            return;
          }

          // QT014 / QT015: level label + input on same node — validate like QT009 / QT007.
          if (typeCode === "QT014" || typeCode === "QT015") {
            const mandatoryRawNest = node.isMandatory ?? node.IsMandatory;
            const mandatoryNest =
              mandatoryRawNest !== null &&
              mandatoryRawNest !== undefined &&
              mandatoryRawNest !== false &&
              mandatoryRawNest !== 0 &&
              mandatoryRawNest !== "0";
            const nestVal = code ? getStoredAnswerForQuestion(answers, code) : null;
            if (mandatoryNest && code) {
              const isEmpty =
                nestVal == null ||
                String(nestVal).trim() === "" ||
                (Array.isArray(nestVal) && nestVal.length === 0);
              if (isEmpty) {
                errors[tc] = errors[tc] || {};
                errors[tc][errorKeyFor(code)] = "This field is required.";
              }
            }
            if (typeCode === "QT015" && code && nestVal != null && String(nestVal).trim() !== "") {
              const qt007Err = validateQt007Text(nestVal);
              if (qt007Err) {
                errors[tc] = errors[tc] || {};
                errors[tc][errorKeyFor(code)] = qt007Err;
              }
            }
            return;
          }

          // Branding image upload only when "Is Greenlam Logo/Branding appearing on the product?" = Yes.
          if (
            typeCode === "QT009" &&
            isGreenlamBrandingCaptureQuestion(node.questionText || node.QuestionText || "") &&
            !isGreenlamBrandingCaptureVisible(tree, answers)
          ) {
            return;
          }

          // Complaint Attended by — only when "Did you visit the site?" is answered.
          if (
            (typeCode === "QT005" || typeCode === "QT007") &&
            isComplaintAttendedByQuestion(node.questionText || node.QuestionText || "") &&
            !isComplaintAttendedByVisible(tree, answers)
          ) {
            return;
          }

          // QT006: (a) Section with nested sub-questions, or (b) block of QT007 fields — no value on parent; children hold answers.
          // (c) Multiple Choice from admin (QT006 + option rows e.g. Label) — value stored on parent.
          // QT006 may show * in UI but is never required on submit; children are validated normally.
          const qt006Kids = node.children || [];
          const qt006HasNested = qt006Kids.some((c) => (c.children || []).length > 0);
          const qt006AllQt007 =
            qt006Kids.length > 0 &&
            qt006Kids.every((c) => (c.questionTypeCode || c.QuestionTypeCode || "").toUpperCase() === "QT007");
          if (typeCode === "QT006" && qt006Kids.length > 0 && (qt006HasNested || qt006AllQt007)) {
            const sectionText = node.questionText || node.QuestionText || "";
            walk(qt006Kids, {
              inProductDetailsSection: isProductDetailsSectionLabel(sectionText),
            });
            return;
          }

          const mandatoryRaw = node.isMandatory ?? node.IsMandatory;
          const mandatory =
            mandatoryRaw !== null &&
            mandatoryRaw !== undefined &&
            mandatoryRaw !== false &&
            mandatoryRaw !== 0 &&
            mandatoryRaw !== "0";
          const val = code ? getStoredAnswerForQuestion(answers, code) : null;

          if (mandatory && code && typeCode !== "QT006") {
            const isEmpty = val == null || String(val).trim() === "" || (Array.isArray(val) && val.length === 0);
            if (isEmpty) {
              errors[tc] = errors[tc] || {};
              errors[tc][errorKeyFor(code)] = "This field is required.";
            }
          }
          // QT007: same validation as Invoice Number – no special characters
          if (typeCode === "QT007" && code && val != null && String(val).trim() !== "") {
            const qt007Err = validateQt007Text(val);
            if (qt007Err) {
              errors[tc] = errors[tc] || {};
              errors[tc][errorKeyFor(code)] = qt007Err;
            }
          }
          // QT008: digits only (no <= invoice/SR qty cap on checklist page).
          if (typeCode === "QT008" && code && val != null && String(val).trim() !== "") {
            const raw = String(val).trim();
            if (!/^\d+$/.test(raw)) {
              errors[tc] = errors[tc] || {};
              errors[tc][errorKeyFor(code)] = "Only digits are allowed.";
            }
          }
          if (
            isManufacturingDateQuestion(questionText) &&
            code &&
            val != null &&
            String(val).trim() !== ""
          ) {
            const mfdErr = validateManufacturingDate(val);
            if (mfdErr) {
              errors[tc] = errors[tc] || {};
              errors[tc][errorKeyFor(code)] = mfdErr;
            }
          }
          // QT010 / complaint receipt date: cannot be a future date.
          const isReceiptDateField =
            typeCode === "QT010" || isComplaintReceiptDateQuestion(questionText);
          if (
            !isManufacturingDateQuestion(questionText) &&
            isReceiptDateField &&
            code &&
            val != null &&
            String(val).trim() !== ""
          ) {
            if (isChecklistDateAfterToday(val)) {
              errors[tc] = errors[tc] || {};
              errors[tc][errorKeyFor(code)] = CHECKLIST_FUTURE_DATE_ERROR;
            }
          }
          // If "Others (Please specify)" is selected in QT003 / Group Check Box, its follow-up text field is mandatory.
          if (typeCode === "QT003" || typeCode === "QT011") {
            const optionParent = normalizeChecklistOptionParentNode(node);
            const selectedTexts = getSelectedOptionTextsForNode(optionParent, answers).map((v) =>
              normalizeOptionText(v)
            );
            const hasOthersSelected = selectedTexts.includes(
              normalizeOptionText("Others (Please specify)")
            );
            if (hasOthersSelected) {
              const followUps = getVisibleConditionalTextChildren(optionParent, answers).filter((child) => {
                const childType = String(
                  child?.questionTypeCode || child?.QuestionTypeCode || ""
                ).toUpperCase();
                if (childType !== "QT005" && childType !== "QT007") return false;
                const childText = normalizeOptionText(
                  child?.questionText || child?.QuestionText || ""
                );
                const condText = normalizeOptionText(
                  child?.conditionalAnswerValue ?? child?.ConditionalAnswerValue ?? ""
                );
                return (
                  childText === normalizeOptionText("Others (Please specify)") ||
                  condText === normalizeOptionText("Others (Please specify)")
                );
              });
              followUps.forEach((child) => {
                const childCode = child?.questionCode || child?.QuestionCode;
                if (!childCode) return;
                const childVal = getStoredAnswerForQuestion(answers, childCode);
                const isEmpty =
                  childVal == null ||
                  String(childVal).trim() === "" ||
                  (Array.isArray(childVal) && childVal.length === 0);
                if (isEmpty) {
                  errors[tc] = errors[tc] || {};
                  errors[tc][errorKeyFor(childCode)] = "This field is required.";
                }
              });
            }
          }
          if (isChecklistOptionBasedParentType(typeCode)) {
            walk(mergeVisibleChecklistFollowUpChildren(node, answers), walkCtx);
            return;
          }

          const childWalkCtx =
            typeCode === "QT006"
              ? {
                  inProductDetailsSection: isProductDetailsSectionLabel(
                    node.questionText || node.QuestionText || ""
                  ),
                }
              : walkCtx;
          walk(node.children || [], childWalkCtx);
        });
      };
        walk(tree);
      });
    });
    if (Object.keys(errors).length === 0) return true;
    setDynamicAnswerErrors(errors);
    return errors;
  };

  const handleSubmitAndRaiseSR = async () => {
    if (templates.length > 0) {
      const validationErrors = validateDynamicChecklistAnswers();
      if (validationErrors !== true) {
        const missingLabels = [];
        templates.forEach((t) => {
          const tc = t.templateCode;
          if (!tc) return;
          const tree = templateQuestionTrees[tc] || [];
          const tcErrors = validationErrors[tc] || {};
          Object.keys(tcErrors).forEach((qCode) => {
            const sepIdx = qCode.indexOf(SR_ANSWER_KEY_SEP);
            const actualCode = sepIdx >= 0 ? qCode.slice(sepIdx + SR_ANSWER_KEY_SEP.length) : qCode;
            const label = findChecklistQuestionLabel(tree, actualCode) || actualCode;
            const errMsg = tcErrors[qCode] || "Invalid value";
            const tabName = t.templateName || tc;
            missingLabels.push(
              templates.length > 1 ? `${tabName}: ${label} — ${errMsg}` : `${label} — ${errMsg}`
            );
          });
        });
        const firstErrorTabIdx = templates.findIndex(
          (t) => validationErrors[t.templateCode] && Object.keys(validationErrors[t.templateCode]).length > 0
        );
        if (firstErrorTabIdx >= 0) setActiveChecklistTab(firstErrorTabIdx);
        setIsChecklistQuestionsCollapsed(false);
        const listHtml =
          missingLabels.length > 0
            ? `<ul style="text-align:left;margin:0.75rem 0 0;padding-left:1.25rem;">${missingLabels
                .map((item) => `<li>${escapeHtml(item)}</li>`)
                .join("")}</ul>`
            : "";
        Swal.fire({
          title: "Validation Error",
          html: `Please correct the following checklist fields.${listHtml}`,
          icon: "warning",
          confirmButtonColor: "#ff9800",
        });
        return;
      }
    }
    if (!validateVisitReportComments()) return;

    const damageImageFiles =
      templates.length === 0 && activeTab === "prelam" ? prelamUploadFiles : uploadFiles;
    const totalDamageImageBytes = damageImageFiles.reduce(
      (total, item) => total + (Number(item?.file?.size ?? item?.size ?? item?.fileSize) || 0),
      0
    );
    if (damageImageFiles.length === 0) {
      Swal.fire({
        title: "Validation Error",
        text: "Upload Images related to the complaint is mandatory before submitting SR.",
        icon: "warning",
        confirmButtonColor: "#ff9800",
      });
      return;
    }
    if (totalDamageImageBytes > MAX_DAMAGE_IMAGE_TOTAL_BYTES) {
      Swal.fire({
        title: "Validation Error",
        text: "Total image size cannot exceed 12 MB.",
        icon: "warning",
        confirmButtonColor: "#ff9800",
      });
      return;
    }

    const invoiceNumber =
      formData.salesInvoiceNo ||
      prelamForm.salesInvoiceNo ||
      searchParams.get("invoiceNo") ||
      invoiceNo ||
      "N/A";

    const srCode = searchParams.get("srCode") || "";

    const result = await Swal.fire({
      title: "Confirm Submission",
      html: `You are submitting an SR for Invoice No: <strong>${escapeHtml(invoiceNumber)}</strong>. Please confirm to proceed.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#ff9800",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, submit",
      cancelButtonText: "Cancel",
      didOpen: () => {
        collapseSidebar();
        document.body.style.overflow = "hidden";
      },
      didClose: () => {
        restoreSidebar();
        document.body.style.overflow = "";
      },
    });

    if (!result.isConfirmed) return;

    // Authoritative geo check uses SAP header (+ fallbacks) by invoice/ODN — not UI draft fields.
    let plantCodesCsvForGeo = null;
    if (materialInvoiceDetails?.length) {
      plantCodesCsvForGeo = buildPlantCodesCsvFromSelectedLines(
        materialInvoiceDetails,
        submitPlantMasterList
      );
    }

    try {
      const geoValRes = await complaintAPI.validateInitiatorInvoiceGeo(
        invoiceNumber,
        plantCodesCsvForGeo
      );
      const geoValOk = geoValRes?.success ?? geoValRes?.Success ?? false;
      if (!geoValOk) {
        const geoValMsg =
          geoValRes?.message ??
          geoValRes?.Message ??
          INITIATOR_GEO_MISMATCH_MESSAGE;
        Swal.fire({
          title: "Cannot Submit",
          text: geoValMsg,
          icon: "warning",
          confirmButtonColor: "#ff9800",
        });
        return;
      }
    } catch (geoValErr) {
      const geoValMsg =
        geoValErr?.response?.data?.message ??
        geoValErr?.response?.data?.Message ??
        "Could not validate invoice geography. Please try again or contact your administrator.";
      Swal.fire({
        title: "Cannot Submit",
        text: geoValMsg,
        icon: "warning",
        confirmButtonColor: "#ff9800",
      });
      return;
    }

    const bvError = validateBusinessVerticalsForUser(materialInvoiceDetails);
    if (bvError) {
      Swal.fire({
        title: "Cannot Submit",
        text: bvError,
        icon: "warning",
        confirmButtonColor: "#ff9800",
      });
      return;
    }

    if (!srCode || !String(srCode).trim()) {
      Swal.fire({
        title: "Cannot Submit",
        text: "Draft code is missing. Please start from Raise SR and complete the flow with a saved draft.",
        icon: "warning",
        confirmButtonColor: "#ff9800",
      });
      return;
    }

    if (submitFinalInFlightRef.current) return;
    submitFinalInFlightRef.current = true;
    setSubmittingFinal(true);
    try {
      let processedTemplateAnswers = { ...templateAnswers };
      let processedSrCategoryAnswers = { ...srCategoryAnswers };
      if (templates.length > 0) {
        const tabCats = extractMaterialSrCategories(materialInvoiceDetails).filter(
          (c) => c.code !== GENERAL_SR_TYPE_CODE
        );
        templates.forEach((t) => {
          const tc = t.templateCode;
          if (!tc) return;
          const rawTree = templateQuestionTrees[tc] || [];
          processedTemplateAnswers[tc] = buildChecklistAnswersForValidation(
            filterGeneralQuestionTree(rawTree),
            stripLegacyScopedKeys(processedTemplateAnswers[tc] || {}),
            materialSectionTotals
          );
          if (tabCats.length > 0) {
            const bySr = { ...(processedSrCategoryAnswers[tc] || {}) };
            tabCats.forEach((cat) => {
              bySr[cat.code] = buildChecklistAnswersForValidation(
                filterQuestionTreeBySrCategoryExcludingGeneral(rawTree, cat.code),
                bySr[cat.code] || {},
                materialSectionTotals
              );
            });
            processedSrCategoryAnswers[tc] = bySr;
          } else {
            processedTemplateAnswers[tc] = buildChecklistAnswersForValidation(
              rawTree,
              processedTemplateAnswers[tc] || {},
              materialSectionTotals
            );
          }
          const partitioned = partitionChecklistAnswersForTemplateStorage(
            tc,
            processedTemplateAnswers[tc],
            processedSrCategoryAnswers[tc],
            rawTree
          );
          processedTemplateAnswers[tc] = partitioned.generalAnswers;
          processedSrCategoryAnswers[tc] = partitioned.categoryAnswersByCode;
        });
      }
      if (Object.keys(processedTemplateAnswers).length > 0) {
        const nextGeneral = { ...processedTemplateAnswers };
        const nextSrCategory = { ...processedSrCategoryAnswers };
        for (const t of templates) {
          const tc = t.templateCode;
          if (!tc) continue;
          const uploaded = await uploadQt009AnswersForTemplate(
            tc,
            nextGeneral[tc] || {},
            nextSrCategory[tc] || {}
          );
          nextGeneral[tc] = uploaded.general;
          nextSrCategory[tc] = uploaded.category;
        }
        processedTemplateAnswers = nextGeneral;
        processedSrCategoryAnswers = nextSrCategory;
        setTemplateAnswers(nextGeneral);
        setSrCategoryAnswers(nextSrCategory);
      }
      const hasChecklistPayload =
        templates.length > 0 &&
        (Object.keys(processedTemplateAnswers).length > 0 ||
          Object.keys(processedSrCategoryAnswers).length > 0);
      const floorFormPayload = hasChecklistPayload
        ? {
            templateAnswers: JSON.parse(JSON.stringify(processedTemplateAnswers)),
            srCategoryAnswers: JSON.parse(JSON.stringify(processedSrCategoryAnswers)),
          }
        : null;
      const floorFormJson = floorFormPayload ? JSON.stringify(floorFormPayload) : null;

      let submittedBy = null;
      try {
        const u = localStorage.getItem("user");
        if (u) {
          const user = JSON.parse(u);
          submittedBy = user?.userIdCode ?? user?.UserIdCode ?? null;
        }
      } catch (_) {}

      const urlMarketName = searchParams.get("MarketCode");
      const urlBv = resolveBusinessVerticalForChecklistApi(
        searchParams.get("businessVertical") || searchParams.get("divisionName")
      );
      const submissionAttachments = buildSubmissionAttachmentsFromInvoiceCopies();
      let complaintReceivedDateParam = null;
      if (complaintReceivedDate && String(complaintReceivedDate).trim()) {
        const d = complaintReceivedDate.trim();
        complaintReceivedDateParam =
          /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : (d.includes("/") || d.includes("-") ? toISODate(d) : d) || null;
      }
      const checklistFiles = templates.length === 0 && activeTab === "prelam" ? prelamUploadFiles : uploadFiles;
      const checklistVideoUrl = templates.length === 0 && activeTab === "prelam" ? prelamVideoUrl : videoUrl;
      const checklistAttachmentsParam = await buildChecklistAttachmentsForDraft(checklistFiles, checklistVideoUrl);
      const trimmedVisitReportComments = String(visitReportComments ?? "").trim();
      const shouldIncludeSubmission =
        submissionAttachments.length > 0 ||
        (checklistAttachmentsParam?.length ?? 0) > 0 ||
        !!complaintReceivedDateParam ||
        trimmedVisitReportComments.length > 0;

      const submissionPayload = {
        attachments: submissionAttachments,
        ...(checklistAttachmentsParam?.length > 0 ? { checklistAttachments: checklistAttachmentsParam } : {}),
        ...(complaintReceivedDateParam ? { complaintReceivedDate: complaintReceivedDateParam } : {}),
        ...(trimmedVisitReportComments ? { visitReportComments: trimmedVisitReportComments } : {}),
      };

      // Validation: pass only selected Material Section plant code(s) — not all invoice plants.
      let plantCodesCsv = plantCodesCsvForGeo;
      if (!plantCodesCsv && materialInvoiceDetails?.length) {
        plantCodesCsv = buildPlantCodesCsvFromSelectedLines(
          materialInvoiceDetails,
          submitPlantMasterList
        );
      }
      if (!plantCodesCsv) {
        try {
          const draftRes = await complaintAPI.getDraftByCode(srCode.trim());
          const draft = draftRes?.data ?? draftRes?.Data ?? draftRes;
          const lines = draft?.InvoiceDetails ?? draft?.invoiceDetails ?? [];
          plantCodesCsv = buildPlantCodesCsvFromSelectedLines(
            lines,
            submitPlantMasterList
          );
        } catch (e) {
          console.warn("Checklist submit: could not resolve plant codes from draft", e);
        }
      }

      const response = await complaintAPI.submitFinal({
        srCode: srCode.trim(),
        floorFormJson: floorFormJson ?? null,
        submittedBy: submittedBy || undefined,
        marketCode: urlMarketName || undefined,
        businessVertical: urlBv || undefined,
        ...(plantCodesCsv ? { plantCodesCsv } : {}),
        ...(shouldIncludeSubmission ? { submission: submissionPayload } : {}),
      });

      const ok = response?.success ?? response?.Success ?? false;
      const parentSRNumber =
        response?.data?.parentSRNumber ??
        response?.data?.ParentSRNumber ??
        response?.parentSRNumber ??
        response?.ParentSRNumber ??
        "";

      if (ok && parentSRNumber) {
        setSrNumber(parentSRNumber);
        const children =
          response?.data?.childSRNumbers ??
          response?.data?.ChildSRNumbers ??
          [];
        setChildSRNumbers(Array.isArray(children) ? children : []);
        // Collapse immediately to avoid a brief sidebar expansion before the modal renders.
        collapseSidebar();
        setShowSuccessModal(true);
      } else {
        throw new Error(response?.message ?? response?.Message ?? "Failed to submit service request.");
      }
    } catch (err) {
      const errMsg =
        err.response?.data?.message ??
        err.response?.data?.Message ??
        err.message ??
        "An error occurred while submitting. Please try again.";
      Swal.fire({
        title: "Error",
        text: errMsg,
        icon: "error",
        confirmButtonColor: "#ff9800",
      });
    } finally {
      submitFinalInFlightRef.current = false;
      setSubmittingFinal(false);
    }
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    setChildSRNumbers([]);
    navigate("/complaints");
  };

  const handleViewChildServiceRequest = (childSrCode) => {
    const parent = (srNumber || "").trim();
    const child = (childSrCode || "").trim();
    if (!parent && !child) return;
    const parentPath = parent || child;
    const query = child ? `?childSrCode=${encodeURIComponent(child)}` : "";
    navigate(`/complaints/${encodeURIComponent(parentPath)}${query}`);
  };

  const handlePrevTab = () => {
    // When backend returns no checklist templates at all, "Back" should exit
    // this page. Otherwise we end up toggling ppb/prelam internal tabs and
    // the user thinks navigation didn't happen.
    if (templates.length === 0) {
      const srCode = searchParams.get("srCode");
      navigate(srCode ? `/raise-complaint?srCode=${encodeURIComponent(srCode)}` : "/raise-complaint");
      return;
    }

    if (srCategoriesForTabs.length > 1 && activeSrCategoryTab > 0) {
      setActiveSrCategoryTab((prev) => prev - 1);
      return;
    }

    // Multiple checklist tabs: go to previous checklist tab
    if (templates.length > 1) {
      if (activeChecklistTab > 0) {
        setActiveChecklistTab((prev) => prev - 1);
        setActiveSrCategoryTab(Math.max(0, srCategoriesForTabs.length - 1));
      } else {
        const srCode = searchParams.get("srCode");
        navigate(srCode ? `/raise-complaint?srCode=${encodeURIComponent(srCode)}` : "/raise-complaint");
      }
      return;
    }
    // Single checklist: no previous tab on page, go back to raise-complaint with srCode
    if (templates.length === 1) {
      const srCode = searchParams.get("srCode");
      navigate(srCode ? `/raise-complaint?srCode=${encodeURIComponent(srCode)}` : "/raise-complaint");
      return;
    }
    // PPB/Prelam tabs: go to previous tab (prelam -> ppb) or back to raise-complaint
    if (activeTab === "prelam") {
      setActiveTab("ppb");
    } else {
      const srCode = searchParams.get("srCode");
      navigate(srCode ? `/raise-complaint?srCode=${encodeURIComponent(srCode)}` : "/raise-complaint");
    }
  };

  const handleSaveDraftForTemplate = async (eventOrTc, tcParam) => {
    const e = eventOrTc && typeof eventOrTc.preventDefault === "function" ? eventOrTc : null;
    const tc = tcParam ?? eventOrTc;
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    try {
      setSavingDraftTemplateCode(tc);
      if (!validateVisitReportComments()) {
        setSavingDraftTemplateCode(null);
        return;
      }
      const dateValidation = validateDynamicChecklistAnswers({ onlyFutureDate: true });
      if (dateValidation !== true) {
        setIsChecklistQuestionsCollapsed(false);
        Swal.fire({
          title: "Validation Error",
          text: firstChecklistValidationMessage(dateValidation),
          icon: "warning",
          confirmButtonColor: "#ff9800",
        });
        setSavingDraftTemplateCode(null);
        return;
      }
      const invoiceNumber =
        searchParams.get("invoiceNo") || invoiceNo || "";
      const urlProductCode = searchParams.get("productCode") || productCode;
      const urlProductCategoryId = searchParams.get("productCategoryId") || searchParams.get("catgCode");
      const prodCode = urlProductCode || urlProductCategoryId || "";
      if (!invoiceNumber) {
        Swal.fire({
          title: "Missing Information",
          text: "Invoice number is missing. Please open this page from Raise SR with an invoice selected.",
          icon: "warning",
          confirmButtonColor: "#ff9800",
        });
        setSavingDraftTemplateCode(null);
        return;
      }
      if (!prodCode) {
        Swal.fire({
          title: "Missing Information",
          text: "Product code or category is missing. Please open this page from Raise SR after selecting a product.",
          icon: "warning",
          confirmButtonColor: "#ff9800",
        });
        setSavingDraftTemplateCode(null);
        return;
      }
      // Save this template's checklist answers (backend deletes/replaces by SRCode + TemplateCode)
      const uploaded = await uploadQt009AnswersForTemplate(
        tc,
        templateAnswers[tc] || {},
        srCategoryAnswers[tc] || {}
      );
      const mergedTemplateAnswers = {
        ...templateAnswers,
        [tc]: uploaded.general,
      };
      const mergedSrCategoryAnswers = {
        ...srCategoryAnswers,
        [tc]: uploaded.category,
      };
      const partitionedForStorage = partitionChecklistAnswersForTemplateStorage(
        tc,
        mergedTemplateAnswers[tc],
        mergedSrCategoryAnswers[tc],
        templateQuestionTrees[tc] || []
      );
      mergedTemplateAnswers[tc] = partitionedForStorage.generalAnswers;
      mergedSrCategoryAnswers[tc] = partitionedForStorage.categoryAnswersByCode;
      setTemplateAnswers(mergedTemplateAnswers);
      setSrCategoryAnswers(mergedSrCategoryAnswers);
      const serializedUploadFiles = serializeUploadFiles(uploadFiles);
      const srCodeParam = searchParams.get("srCode") || undefined;
      let complaintReceivedDateParam = null;
      let checklistAttachmentsParam = [];
      if (complaintReceivedDate && String(complaintReceivedDate).trim()) {
        const d = complaintReceivedDate.trim();
        complaintReceivedDateParam = /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : (d.includes("/") || d.includes("-") ? toISODate(d) : d) || null;
      }
      if (srCodeParam) {
        checklistAttachmentsParam = await buildChecklistAttachmentsForDraft(uploadFiles, videoUrl);
      }
      const draftData = {
        invoiceNo: invoiceNumber,
        productCode: prodCode,
        category: "CHECKLIST",
        complaintQty: "",
        floorForm: {
          templateCode: tc,
          templateAnswers: mergedTemplateAnswers,
          srCategoryAnswers: mergedSrCategoryAnswers,
          checklistAnswers: uploaded.general,
          uploadFiles: serializedUploadFiles,
          videoUrl,
          invoiceCopyFiles,
          formData,
          prelamForm,
          complaintReceivedDate,
          visitReportComments,
        },
        complaintLines: null,
        catgCode: urlProductCategoryId || undefined,
        srCode: srCodeParam,
        templateCode: tc,
        marketCode: searchParams.get("MarketCode") || undefined,
        divisionName:
          searchParams.get("businessVertical") ||
          searchParams.get("divisionName") ||
          undefined,
        ...(complaintReceivedDateParam ? { complaintReceivedDate: complaintReceivedDateParam } : {}),
        visitReportComments: String(visitReportComments ?? "").trim(),
        ...(checklistAttachmentsParam.length > 0 ? { checklistAttachments: checklistAttachmentsParam } : {}),
      };
      const response = await complaintAPI.saveChecklistDraft(draftData);
      if (response.success) {
        const draftCode = searchParams.get("srCode") || draftData.srCode || response.srCode || response.data?.srCode || "";
        const successText = draftCode
          ? `Your draft has been saved successfully with Draft Code : ${draftCode}.`
          : "Your draft has been saved successfully.";
        Swal.fire({
          title: "Draft Saved",
          text: successText,
          icon: "success",
          confirmButtonColor: "#ff9800",
          didOpen: () => { document.body.style.overflow = "hidden"; },
          didClose: () => { document.body.style.overflow = ""; },
        });
      } else {
        Swal.fire({
          title: "Error",
          text: response.message || "Failed to save draft.",
          icon: "error",
          confirmButtonColor: "#ff9800",
        });
      }
    } catch (error) {
      console.error("Error saving draft:", error);
      Swal.fire({
        title: "Error",
        text: error?.response?.data?.message || "An error occurred while saving the draft.",
        icon: "error",
        confirmButtonColor: "#ff9800",
      });
    } finally {
      setSavingDraftTemplateCode(null);
    }
  };

  const handleSaveDraft = async () => {
    if (templates.length > 0 && templateCode) {
      setSavingDraft(true);
      try {
        await handleSaveDraftForTemplate(templateCode);
      } finally {
        setSavingDraft(false);
      }
      return;
    }
    try {
      setSavingDraft(true);
      const invoiceNumber =
        (activeTab === "ppb" ? formData.salesInvoiceNo : prelamForm.salesInvoiceNo) ||
        searchParams.get("invoiceNo") || invoiceNo || "";
      const prodCode = searchParams.get("productCode") || productCode || "";
      if (!invoiceNumber || !prodCode) {
        Swal.fire({ title: "Missing Information", text: "Invoice number or product code is missing.", icon: "warning", confirmButtonColor: "#ff9800" });
        setSavingDraft(false);
        return;
      }
      const serializedUpload = serializeUploadFiles(
        activeTab === "ppb" ? uploadFiles : prelamUploadFiles
      );
      const srCodeParam = searchParams.get("srCode") || undefined;
      let complaintReceivedDateParam = null;
      let checklistAttachmentsParam = [];
      if (complaintReceivedDate && String(complaintReceivedDate).trim()) {
        const d = complaintReceivedDate.trim();
        complaintReceivedDateParam = /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : (d.includes("/") || d.includes("-") ? toISODate(d) : d) || null;
      }
      if (srCodeParam) {
        const files = activeTab === "ppb" ? uploadFiles : prelamUploadFiles;
        const vidUrl = activeTab === "ppb" ? videoUrl : prelamVideoUrl;
        checklistAttachmentsParam = await buildChecklistAttachmentsForDraft(files, vidUrl);
      }
      const floorFormPayload = {
        ...(activeTab === "ppb" ? formData : prelamForm),
        uploadFiles: serializedUpload,
        videoUrl: activeTab === "ppb" ? videoUrl : prelamVideoUrl,
        invoiceCopyFiles,
        formData,
        prelamForm,
        complaintReceivedDate,
      };
      const catgCode = searchParams.get("productCategoryId") || searchParams.get("catgCode");
      const draftData = {
        invoiceNo: invoiceNumber,
        productCode: prodCode,
        category: activeTab === "ppb" ? "PPB" : "PRELAM",
        complaintQty: activeTab === "ppb" ? (formData.defectiveQty || formData.defectiveQtyNotUsed || formData.UsedFurnitureQuantity || formData.qty || "") : (prelamForm.defectiveQty || ""),
        floorForm: floorFormPayload,
        complaintLines: null,
        catgCode: catgCode || undefined,
        srCode: srCodeParam,
        marketCode: searchParams.get("MarketCode") || undefined,
        divisionName:
          searchParams.get("businessVertical") ||
          searchParams.get("divisionName") ||
          undefined,
        ...(complaintReceivedDateParam ? { complaintReceivedDate: complaintReceivedDateParam } : {}),
        ...(visitReportComments && String(visitReportComments).trim()
          ? { visitReportComments: String(visitReportComments).trim() }
          : {}),
        ...(checklistAttachmentsParam.length > 0 ? { checklistAttachments: checklistAttachmentsParam } : {}),
      };
      const response = await complaintAPI.saveChecklistDraft(draftData);
      if (response.success) {
        const draftCode = searchParams.get("srCode") || draftData.srCode || response.srCode || response.data?.srCode || "";
        const successText = draftCode
          ? `Your draft has been saved successfully with Draft Code : ${draftCode}.`
          : "Your draft has been saved successfully.";
        Swal.fire({ title: "Draft Saved", text: successText, icon: "success", confirmButtonColor: "#ff9800" });
      } else {
        Swal.fire({ title: "Error", text: response.message || "Failed to save draft.", icon: "error", confirmButtonColor: "#ff9800" });
      }
    } catch (error) {
      console.error("Error saving draft:", error);
      Swal.fire({ title: "Error", text: "An error occurred while saving the draft.", icon: "error", confirmButtonColor: "#ff9800" });
    } finally {
      setSavingDraft(false);
    }
  };

  // Initialize invoice number, product code from URL params
  useEffect(() => {
    const urlInvoiceNo = searchParams.get("invoiceNo");
    const urlProductCode = searchParams.get("productCode");
    const urlProductCategoryId = searchParams.get("productCategoryId") || searchParams.get("catgCode");

    if (urlInvoiceNo) setInvoiceNo(urlInvoiceNo);
    if (urlProductCode) setProductCode(urlProductCode);
    else if (urlProductCategoryId) setProductCode(urlProductCategoryId);
  }, [searchParams]);

  // Material Section totals (URL from raise-complaint, or draft InvoiceDetails).
  useEffect(() => {
    const fromUrlSr =
      searchParams.get("selectedSrQty") || searchParams.get("selectedQty");
    const fromUrlInvoice = searchParams.get("selectedInvoiceQty");
    const parsedSr =
      fromUrlSr != null && String(fromUrlSr).trim() !== ""
        ? Number(String(fromUrlSr).replace(/,/g, "").trim())
        : NaN;
    const parsedInvoice =
      fromUrlInvoice != null && String(fromUrlInvoice).trim() !== ""
        ? Number(String(fromUrlInvoice).replace(/,/g, "").trim())
        : NaN;
    const hasUrlTotals =
      (Number.isFinite(parsedSr) && parsedSr >= 0) ||
      (Number.isFinite(parsedInvoice) && parsedInvoice >= 0);
    if (hasUrlTotals) {
      const totals = {
        totalInvoiceQty: Number.isFinite(parsedInvoice) ? Math.floor(parsedInvoice) : 0,
        totalSrQty: Number.isFinite(parsedSr) ? Math.floor(parsedSr) : 0,
      };
      setMaterialSectionTotals(totals);
      setSelectedMaterialQtyLimit(totals.totalSrQty > 0 ? totals.totalSrQty : null);
      // Still load draft for SR categories when srCode is present.
      const srCodeForCategories = searchParams.get("srCode");
      if (!srCodeForCategories || !String(srCodeForCategories).trim()) {
        setMaterialInvoiceDetails([]);
        return;
      }
    } else {
      const srCode = searchParams.get("srCode");
      if (!srCode || !String(srCode).trim()) {
        setMaterialSectionTotals(null);
        setSelectedMaterialQtyLimit(null);
        setMaterialInvoiceDetails([]);
        return;
      }
    }

    const srCode = searchParams.get("srCode");
    if (!srCode || !String(srCode).trim()) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await complaintAPI.getDraftByCode(String(srCode).trim());
        const draft = response?.data ?? response?.Data ?? response;
        const details = draft?.InvoiceDetails ?? draft?.invoiceDetails ?? [];
        if (cancelled || !Array.isArray(details) || details.length === 0) {
          if (!cancelled) {
            if (!hasUrlTotals) {
              setMaterialSectionTotals(null);
              setSelectedMaterialQtyLimit(null);
            }
            setMaterialInvoiceDetails([]);
          }
          return;
        }
        if (!hasUrlTotals) {
          const totals = computeMaterialSectionTotals(details);
          setMaterialSectionTotals(totals);
          setSelectedMaterialQtyLimit(totals.totalSrQty > 0 ? totals.totalSrQty : null);
        }
        if (!cancelled) setMaterialInvoiceDetails(details);
      } catch {
        if (!cancelled) {
          if (!hasUrlTotals) {
            setMaterialSectionTotals(null);
            setSelectedMaterialQtyLimit(null);
          }
          setMaterialInvoiceDetails([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  useEffect(() => {
    setActiveSrCategoryTab(0);
  }, [templateCode, materialSrCategoryKey]);

  useEffect(() => {
    const marketCode = searchParams.get("MarketCode");
    if (!marketCode || !String(marketCode).trim()) {
      setSrTypeOptions([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await masterDataAPI.getSRTypes(String(marketCode).trim());
        const data = response?.data ?? response?.Data ?? response ?? [];
        if (cancelled) return;
        const mapped = (Array.isArray(data) ? data : [])
          .map((st) => ({
            code:
              st.SRTypeCode ||
              st.srTypeCode ||
              st.SRTYPECODE ||
              st.SrTypeCode ||
              "",
            name: st.Name || st.name || st.NAME || "",
          }))
          .filter((t) => t.code && t.name);
        setSrTypeOptions(mapped);
      } catch {
        if (!cancelled) setSrTypeOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  useEffect(() => {
    const code = searchParams.get("srCode");
    if (!code || !String(code).trim()) return undefined;
    if ((submitPlantMasterList || []).length > 0) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await plantMasterAPI.getPaged({
          status: "active",
          pageNumber: 1,
          pageSize: 8000,
        });
        if (cancelled) return;
        const payload = res?.data;
        const list =
          payload?.Plants || payload?.plants || payload?.data?.Plants || [];
        if (Array.isArray(list) && list.length > 0) {
          setSubmitPlantMasterList(list);
        }
      } catch (e) {
        console.warn("Checklist submit: plant master load failed", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams, submitPlantMasterList]);

  // Auto-fill Product Details: complaint qty + total purchased from Material Section totals.
  useEffect(() => {
    if (!materialSectionTotals || !templates?.length) return;
    const hasTrees = templates.some((t) => {
      const tc = t.templateCode;
      return tc && Array.isArray(templateQuestionTrees[tc]) && templateQuestionTrees[tc].length > 0;
    });
    if (!hasTrees) return;

    const complaintStr =
      materialSectionTotals.totalSrQty != null
        ? String(materialSectionTotals.totalSrQty)
        : "";
    const purchasedStr =
      materialSectionTotals.totalInvoiceQty != null
        ? String(materialSectionTotals.totalInvoiceQty)
        : "";

    setTemplateAnswers((prev) => {
      let changed = false;
      const next = { ...prev };
      templates.forEach((t) => {
        const tc = t.templateCode;
        if (!tc) return;
        const tree = templateQuestionTrees[tc] || [];
        const codes = findMaterialDerivedQt008Codes(tree);
        if (codes.complaintQtyCodes.length === 0 && codes.totalPurchasedCodes.length === 0) return;
        const tcAnswers = { ...(next[tc] || {}) };
        let tcChanged = false;
        codes.complaintQtyCodes.forEach((qCode) => {
          if (tcAnswers[qCode] !== complaintStr) {
            tcAnswers[qCode] = complaintStr;
            tcChanged = true;
          }
        });
        codes.totalPurchasedCodes.forEach((qCode) => {
          if (tcAnswers[qCode] !== purchasedStr) {
            tcAnswers[qCode] = purchasedStr;
            tcChanged = true;
          }
        });
        if (tcChanged) {
          next[tc] = tcAnswers;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [materialSectionTotals, templates, templateQuestionTrees, checklistDraftAnswersRevision]);

  // Fetch invoice details from SAP when invoiceNo is in URL (add and draft mode)
  useEffect(() => {
    const urlInvoiceNo = searchParams.get("invoiceNo");
    const urlProductCode = searchParams.get("productCode") || productCode;
    if (!urlInvoiceNo || !String(urlInvoiceNo).trim()) return;
    let cancelled = false;
    (async () => {
      setInvoiceDetailsLoading(true);
      try {
        const invNo = String(urlInvoiceNo).trim().toUpperCase();
        const response = await complaintAPI.getInvoiceDetails(invNo, null);
        const data = response?.data ?? response?.Data ?? response;
        if (cancelled) return;
        if (response?.success !== false && data) {
          setInvoiceDetails(data);
          const today = getLocalTodayISODate();
          setComplaintReceivedDate((prev) => prev || today);
          const products = Array.isArray(data.products)
            ? data.products
            : Array.isArray(data?.Products)
            ? data.Products
            : [];
          const firstProduct = products.find(
            (p) =>
              (p.ProductCode || p.productCode || "").toString() ===
              String(urlProductCode || "").trim()
          ) || products[0];
          if (firstProduct) {
            const p = firstProduct;
            const invDate = data.invoiceDate ?? data.InvoiceDate ?? "";
            const invNoVal = data.invoiceNo ?? data.InvoiceNo ?? data.invoiceNumber ?? urlInvoiceNo;
            setFormData((prev) => ({
              ...prev,
              salesInvoiceNo: invNoVal ?? prev.salesInvoiceNo,
              invoiceDate: invDate ?? prev.invoiceDate,
              size: p.Size ?? p.size ?? prev.size,
              batchNo: p.BatchNo ?? p.batchNo ?? prev.batchNo,
              emission: p.Emission ?? p.emission ?? prev.emission,
              thickness: p.ProductThickness ?? p.productThickness ?? p.Thickness ?? prev.thickness,
              variantProductGrade: p.productVariantName ?? p.ProductVariantName ?? p.productVariantCode ?? p.ProductVariantCode ?? prev.variantProductGrade,
            }));
            setPrelamForm((prev) => ({
              ...prev,
              salesInvoiceNo: invNoVal ?? prev.salesInvoiceNo,
              salesInvoiceDate: invDate ?? prev.salesInvoiceDate,
              size: p.Size ?? p.size ?? prev.size,
              designNo: p.DesignNo ?? p.designNo ?? prev.designNo,
              finish: p.ProductFinish ?? p.productFinish ?? prev.finish,
              emission: p.Emission ?? p.emission ?? prev.emission,
              batchNo: p.BatchNo ?? p.batchNo ?? prev.batchNo,
              mfdDate: p.MFGDate ?? p.mfgDate ?? prev.mfdDate,
              thickness: p.ProductThickness ?? p.productThickness ?? prev.thickness,
            }));
          }
        }
      } catch (err) {
        if (!cancelled) console.error("Error loading invoice details:", err);
      } finally {
        if (!cancelled) setInvoiceDetailsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [searchParams]);

  // PPB / Prelam MFC: product grade & variant options for Invoice & Product Details.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const variantsResponse = await ppbAPI.getDropdownValues("ProductVariant");
        if (cancelled) return;
        if (variantsResponse?.success && Array.isArray(variantsResponse.data)) {
          setProductVariants(variantsResponse.data);
        }
      } catch {
        if (!cancelled) setProductVariants([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load checklist templates: (1) explicit templateCode (tree / deep link), (2) raised-query list from
  // market + business vertical + invoice/product (sp_GetChecklistTemplatesNSRaiseQuery), or (3) merge both
  // so URL templateCode is the first tab when you also pass MarketCode + businessVertical.
  useEffect(() => {
    const urlTemplateCode = normalizeQueryValue(searchParams.get("templateCode"));
    const urlMarketName = normalizeQueryValue(searchParams.get("MarketCode"));
    const urlBusinessVertical = resolveBusinessVerticalForChecklistApi(
      searchParams.get("businessVertical") || searchParams.get("divisionName")
    );
    const urlInvoiceNo = normalizeQueryValue(searchParams.get("invoiceNo"));
    const urlProductCode = normalizeQueryValue(searchParams.get("productCode"));
    const urlProductCategoryId = normalizeQueryValue(
      searchParams.get("productCategoryId") || searchParams.get("catgCode")
    );

    const hasMarketAndDivision = urlMarketName && urlBusinessVertical;

    if (!urlTemplateCode && !hasMarketAndDivision) {
      setTemplates([]);
      return;
    }

    let cancelled = false;
    noChecklistFromApiRef.current = false;
    if (hasMarketAndDivision) noChecklistPopupShownRef.current = false;
    (async () => {
      setChecklistLoading(true);
      try {
        const toTemplateList = (response) => {
          const success = response?.success ?? response?.Success ?? true;
          const paged = response?.data ?? response?.Data;
          const totalRecords = paged?.totalRecords ?? paged?.TotalRecords ?? 0;
          let list = Array.isArray(paged?.data) ? paged.data : Array.isArray(paged?.Data) ? paged.Data : [];
          if (!Array.isArray(list) || list.length === 0) list = [];
          const templateList = list
            .map((t) => ({
              templateCode: t.templateCode ?? t.TemplateCode ?? t.TEMPLATECODE,
              templateName:
                t.templateName ??
                t.TemplateName ??
                t.TEMPLATENAME ??
                t.templateCode ??
                t.TemplateCode ??
                t.TEMPLATECODE,
            }))
            .filter((t) => t.templateCode);
          const hasTemplates = success && (totalRecords > 0 || templateList.length > 0);
          return { hasTemplates, templateList };
        };

        // Explicit templateCode only (no market/BV context): tree editor / deep link — load that template only.
        if (urlTemplateCode && !hasMarketAndDivision) {
          setTemplates([{ templateCode: urlTemplateCode, templateName: urlTemplateCode }]);
        } else if (hasMarketAndDivision) {
          // Raised SR flow: templates come from sp_GetChecklistTemplatesNSRaiseQuery (market, BV, invoice, product…).
          // That list can differ from the template you edit in /checklist-questions/tree — merge optional URL templateCode first.
          const baseQuery = {
            marketName: urlMarketName || undefined,
            marketCode: urlMarketName || undefined,
            invoiceNo: urlInvoiceNo || undefined,
            productCode: urlProductCode || undefined,
            productCategoryId: urlProductCategoryId || undefined,
            status: "active",
            pageNumber: 1,
            pageSize: 100,
          };

          const response = await checklistTemplateAPI.getAllForRaisedQuery({
            ...baseQuery,
            businessVertical: urlBusinessVertical || undefined,
          });
          const { hasTemplates, templateList } = toTemplateList(response);

          // Do not re-query without businessVertical — that loads every Domestic checklist (Veneer, PPB, Door, etc.).
          let finalList = templateList;
          if (urlTemplateCode) {
            const explicit = { templateCode: urlTemplateCode, templateName: urlTemplateCode };
            const by = new Map();
            by.set(urlTemplateCode, explicit);
            (hasTemplates ? templateList : []).forEach((t) => {
              if (t.templateCode && !by.has(t.templateCode)) by.set(t.templateCode, t);
            });
            finalList = Array.from(by.values());
          }

          if (!cancelled) {
            const any = finalList.length > 0;
            setTemplates(any ? finalList : []);
            noChecklistFromApiRef.current = !any;
          }
        }
      } catch (err) {
        console.error("Error loading checklist templates:", err);
        if (!cancelled) {
          setTemplates([]);
          noChecklistFromApiRef.current = hasMarketAndDivision;
        }
      } finally {
        if (!cancelled) setChecklistLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [searchParams]);

  // Show "no checklist" popup only when API returned no templates for market + business vertical.
  const noChecklistPopupShownRef = React.useRef(false);
  useEffect(() => {
    if (checklistLoading || templates.length > 0) return;
    if (!noChecklistFromApiRef.current) return;
    const urlMarketName = normalizeQueryValue(searchParams.get("MarketCode"));
    const urlBv = normalizeQueryValue(
      searchParams.get("businessVertical") || searchParams.get("divisionName")
    );
    if (!urlMarketName || !urlBv) return;
    if (noChecklistPopupShownRef.current) return;
    noChecklistPopupShownRef.current = true;
    Swal.fire({
      icon: "info",
      title: "No checklist available",
      text: "No checklist template is available for this market and business vertical. You cannot submit without a checklist.",
      // Prevent accidental dismissal. Users must click the confirm ("Ok") button.
      allowOutsideClick: false,
      allowEscapeKey: false,
      showCloseButton: false,
    });
  }, [checklistLoading, templates.length, searchParams]);

  // Load saved checklist responses (FloorForm) when srCode is in URL. Populate by template code only; supports multiple templates (e.g. TEMP202602240000001, TEMP202602240000002, TEMP202602240000003).
  useEffect(() => {
    const srCode = searchParams.get("srCode");
    if (!srCode || !srCode.trim()) return;
    let cancelled = false;
    (async () => {
      setDraftLoading(true);
      try {
        // Reset media state for this SR code; actual PPB media will be restored per template tab below.
        setDraftChecklistAttachments([]);
        setUploadFiles([]);
        setVideoUrl("");
        const response = await complaintAPI.getDraftByCode(srCode.trim());
        const draft = response?.data ?? response?.Data ?? response;
        const floorForm = draft?.FloorForm ?? draft?.floorForm;
        if (cancelled) return;
        if (!floorForm || typeof floorForm !== "object") {
          if (!cancelled) setChecklistDraftAnswersRevision((r) => r + 1);
          return;
        }
        const normalizeString = (v) => {
          if (v == null || v === undefined) return "";
          if (typeof v === "number") return String(v);
          const s = String(v).trim();
          return s.replace(/\s+/g, " ");
        };
        const normalizeAnswerValue = (v) => {
          if (v == null || v === undefined) return "";
          if (Array.isArray(v)) return v.map(normalizeString).filter(Boolean);
          if (typeof v === "string") {
            const s = v.trim();
            if (s.startsWith("[") && s.endsWith("]")) {
              try {
                const parsed = JSON.parse(s);
                if (Array.isArray(parsed)) return parsed.map(normalizeString).filter(Boolean);
              } catch (_) {
                // fall through
              }
            }
          }
          return normalizeString(v);
        };
        const normalizeAnswersObject = (obj) => {
          const out = {};
          if (!obj || typeof obj !== "object") return out;
          Object.keys(obj).forEach((qc) => {
            out[qc] = normalizeAnswerValue(obj[qc]);
          });
          return out;
        };
        const rawTemplate =
          floorForm.templateAnswers ?? floorForm.TemplateAnswers ?? {};
        const rawSrCategory =
          floorForm.srCategoryAnswers ?? floorForm.SrCategoryAnswers ?? {};
        const nextTemplate = {};
        const nextSrCategory = {};
        if (rawTemplate && typeof rawTemplate === "object") {
          Object.keys(rawTemplate).forEach((tc) => {
            const templateObj = rawTemplate[tc];
            if (!templateObj || typeof templateObj !== "object") return;
            const { general, bySr } = splitLegacyMergedTemplateAnswers(templateObj);
            nextTemplate[tc] = normalizeAnswersObject(general);
            if (Object.keys(bySr).length > 0) {
              nextSrCategory[tc] = nextSrCategory[tc] || {};
              Object.keys(bySr).forEach((sr) => {
                nextSrCategory[tc][sr] = {
                  ...(nextSrCategory[tc][sr] || {}),
                  ...normalizeAnswersObject(bySr[sr]),
                };
              });
            }
          });
        }
        if (rawSrCategory && typeof rawSrCategory === "object") {
          Object.keys(rawSrCategory).forEach((tc) => {
            const srTypes = rawSrCategory[tc];
            if (!srTypes || typeof srTypes !== "object") return;
            nextSrCategory[tc] = nextSrCategory[tc] || {};
            Object.keys(srTypes).forEach((sr) => {
              const answers = srTypes[sr];
              if (!answers || typeof answers !== "object") return;
              nextSrCategory[tc][sr] = {
                ...(nextSrCategory[tc][sr] || {}),
                ...normalizeAnswersObject(answers),
              };
            });
          });
        }
        if (cancelled) return;
        if (Object.keys(nextTemplate).length > 0) {
          setTemplateAnswers((prev) => ({ ...prev, ...nextTemplate }));
        }
        if (Object.keys(nextSrCategory).length > 0) {
          setSrCategoryAnswers((prev) => ({ ...prev, ...nextSrCategory }));
        }
        // Restore form data, uploads, video and invoice copy from FloorForm when present (draft save stores these)
        // Prefer checklistAttachments from API (SRCHECKLISTATTACHMENTS) when present, then fall back to uploadFiles/videoUrl from saved FloorForm
        const checklistAttachments = floorForm.checklistAttachments ?? floorForm.ChecklistAttachments;
        if (Array.isArray(checklistAttachments) && checklistAttachments.length > 0) {
          // Store everything and populate uploadFiles/videoUrl per active template tab.
          setDraftChecklistAttachments(checklistAttachments);
        } else {
          if (floorForm.videoUrl != null && String(floorForm.videoUrl).trim() !== "") setVideoUrl(String(floorForm.videoUrl).trim());
          if (Array.isArray(floorForm.uploadFiles) && floorForm.uploadFiles.length > 0) setUploadFiles(floorForm.uploadFiles);
        }
        if (floorForm.prelamVideoUrl != null && String(floorForm.prelamVideoUrl).trim() !== "") setPrelamVideoUrl(String(floorForm.prelamVideoUrl).trim());
        if (Array.isArray(floorForm.invoiceCopyFiles) && floorForm.invoiceCopyFiles.length > 0) setInvoiceCopyFiles(floorForm.invoiceCopyFiles);
        if (Array.isArray(floorForm.prelamUploadFiles) && floorForm.prelamUploadFiles.length > 0) setPrelamUploadFiles(floorForm.prelamUploadFiles);
        if (floorForm.formData && typeof floorForm.formData === "object") setFormData((prev) => ({ ...prev, ...floorForm.formData }));
        if (floorForm.prelamForm && typeof floorForm.prelamForm === "object") setPrelamForm((prev) => ({ ...prev, ...floorForm.prelamForm }));
        if (floorForm.complaintReceivedDate != null && String(floorForm.complaintReceivedDate).trim() !== "") setComplaintReceivedDate(String(floorForm.complaintReceivedDate).trim());
        if (floorForm.visitReportComments != null && String(floorForm.visitReportComments).trim() !== "") {
          setVisitReportComments(String(floorForm.visitReportComments).trim());
        }
        if (!cancelled) setChecklistDraftAnswersRevision((r) => r + 1);
      } catch (err) {
        console.error("Error loading checklist draft by srCode:", err);
        if (!cancelled) setChecklistDraftAnswersRevision((r) => r + 1);
      } finally {
        if (!cancelled) setDraftLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [searchParams]);

  // Populate checklist attachments media per active template tab
  // using TemplateCode + MarketCode + CategoryCode filters on SRCHECKLISTATTACHMENTS rows.
  useEffect(() => {
    if (!Array.isArray(draftChecklistAttachments) || draftChecklistAttachments.length === 0) return;
    if (!templateCode) return;

    const trimmedTemplate = String(templateCode).trim();
    if (!trimmedTemplate) return;

    const templateMatches = draftChecklistAttachments.filter((a) => {
      const tc = String(a?.TemplateCode ?? a?.templateCode ?? "").trim();
      return tc && tc.toUpperCase() === trimmedTemplate.toUpperCase();
    });

    // Populate by template match first (robust even when URL values are names
    // like Domestic/Laminate while attachment rows store codes like M001/CTG000001).
    const matched = templateMatches;

    if (!Array.isArray(matched) || matched.length === 0) {
      setUploadFiles([]);
      setVideoUrl("");
      return;
    }

    const first = matched[0] ?? {};
    const draftVisitComments =
      first?.VisitReportComments ?? first?.visitReportComments ?? "";
    if (draftVisitComments != null) {
      setVisitReportComments(String(draftVisitComments).trim());
    }
    const rawComplaintReceivedDate =
      first?.ComplaintReceivedDate ?? first?.complaintReceivedDate ?? null;
    if (rawComplaintReceivedDate != null && String(rawComplaintReceivedDate).trim()) {
      const d = String(rawComplaintReceivedDate).trim();
      const iso = /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : d.includes("/") || d.includes("-") ? toISODate(d) : d;
      if (iso) setComplaintReceivedDate(iso);
    }

    const videoAttachment = matched.find((a) => {
      const copyFor = String(a?.CopyFor ?? a?.copyFor ?? "").trim().toLowerCase();
      return copyFor === "video" || copyFor === "videolink";
    });
    const videoPath = (videoAttachment?.FilePath ?? videoAttachment?.filePath ?? "").toString().trim();

    const damageItems = matched
      .filter((a) => {
        const copyFor = String(a?.CopyFor ?? a?.copyFor ?? "").trim().toLowerCase();
        return !(copyFor === "video" || copyFor === "videolink");
      })
      .map((a, i) => {
        const copyFor = String(a?.CopyFor ?? a?.copyFor ?? "").trim();
        const path = (a?.FilePath ?? a?.filePath ?? "").toString().trim();
        const name = (a?.FileName ?? a?.fileName ?? path.split("/").pop() ?? "attachment").toString();
        if (!path) return null;
        return {
          id: `draft-checklist-${trimmedTemplate}-${i}`,
          name,
          path,
          preview: null,
          url: path,
          size: a.FileSize ?? a.fileSize ?? null,
          type: a.FileType ?? a.fileType ?? null,
        };
      })
      .filter(Boolean);

    setUploadFiles(damageItems);
    setVideoUrl((prev) => (videoPath ? String(videoPath) : (prev || "")));
  }, [draftChecklistAttachments, templateCode, searchParams]);

  // Load question hierarchy for each template (tabular: totalRecords 1 to n)
  useEffect(() => {
    if (!templates || templates.length === 0) return;
    let cancelled = false;
    const loadAll = async () => {
      setChecklistLoading(true);
      const trees = {};
      try {
        for (const t of templates) {
          const tc = t.templateCode;
          if (!tc) continue;
          try {
            const res = await checklistQuestionAPI.getQuestionsHierarchy(tc);
            const raw = Array.isArray(res) ? res : (res?.data ?? res?.data?.data ?? res?.data?.Data ?? res?.data?.items ?? res?.Data ?? []);
            const list = Array.isArray(raw) ? raw : [];
            if (!cancelled) trees[tc] = buildChecklistTree(list);
          } catch (e) {
            if (!cancelled) trees[tc] = [];
          }
        }
        if (!cancelled) setTemplateQuestionTrees((prev) => ({ ...prev, ...trees }));
      } finally {
        if (!cancelled) setChecklistLoading(false);
      }
    };
    loadAll();
    return () => { cancelled = true; };
  }, [templates]);

  // Initialize Select2 on all select elements
  useEffect(() => {
    // Use setTimeout to ensure React has finished rendering the options
    const timer = setTimeout(() => {
      // Initialize select2 on all select elements
      $("select.form-select-checklist").each(function () {
        const $select = $(this);

        // Destroy existing select2 if any
        if ($select.data("select2")) {
          $select.select2("destroy");
        }

        // Initialize select2
        $select.select2({
          width: "100%",
          placeholder: "Select an option",
          allowClear: false,
          minimumResultsForSearch: 5, // Show search box if more than 5 options
        });

        // Handle change event to update React state
        $select.on("change", function () {
          const event = new Event("change", { bubbles: true });
          this.dispatchEvent(event);
        });
      });
    }, 0);

    // Cleanup function
    return () => {
      clearTimeout(timer);
      $("select.form-select-checklist").each(function () {
        const $select = $(this);
        if ($select.data("select2")) {
          $select.off("change");
          $select.select2("destroy");
        }
      });
    };
  }, [
    activeTab,
    productVariants,
    sizeUOM,
    quantityUOM,
    complaintLocations,
    liabilityTypes,
    complainerStatuses,
  ]); // Re-run when tab changes or dropdown data is loaded

  // Unique, prominent icon per question type (MUI icons in dynamic-q-icon box)
  const iconSize = { fontSize: 20 };
  const getDynamicQuestionTypeIcon = (typeCode) => {
    const code = (typeCode || "").toUpperCase();
    switch (code) {
      case "QT001":
        return <RadioButtonCheckedIcon sx={iconSize} aria-hidden="true" />;
      case "QT002":
        return <LabelIcon sx={iconSize} aria-hidden="true" />;
      case "QT003":
        return <CheckBoxIcon sx={iconSize} aria-hidden="true" />;
      case "QT004":
        return <ArrowDropDownIcon sx={iconSize} aria-hidden="true" />;
      case "QT005":
        return <DescriptionIcon sx={iconSize} aria-hidden="true" />;
      case "QT006":
        return <ChecklistRtlIcon sx={iconSize} aria-hidden="true" />;
      case "QT007":
        return <SubjectIcon sx={iconSize} aria-hidden="true" />;
      case "QT008":
        return <NumbersIcon sx={iconSize} aria-hidden="true" />;
      case "QT009":
        return <ImageIcon sx={iconSize} aria-hidden="true" />;
      case "QT010":
        return <CalendarMonthIcon sx={iconSize} aria-hidden="true" />;
      default:
        return <EditNoteIcon sx={iconSize} aria-hidden="true" />;
    }
  };

  // Render one question from the dynamic checklist (by QuestionTypeCode). level = indent depth for n-level hierarchy.
  // options: { …, moreThanTwoSectionDirectChildren, qt004HalfWidthBetweenSectionSiblings, … }.
  // moreThanTwoSectionDirectChildren: QT006 section has >2 direct children → QT002/QT005/QT007 (not QT001/QT003) use col-lg-3 in row; radio/checkbox count rules unchanged for QT001/QT003.
  const renderDynamicQuestion = (node, level = 0, options = {}) => {
    const code = node.questionCode || node.QuestionCode;
    const typeCode = (node.questionTypeCode || node.QuestionTypeCode || "").toUpperCase();
    const rawText = node.questionText || node.QuestionText || "";
    const text = rawText;
    const isOthersPleaseSpecifyQuestion =
      normalizeOptionText(rawText) === normalizeOptionText("Others (Please specify)");
    const children = node.children || [];
    const mandatoryRaw = node.isMandatory ?? node.IsMandatory;
    const mandatory =
      mandatoryRaw !== null &&
      mandatoryRaw !== undefined &&
      mandatoryRaw !== false &&
      mandatoryRaw !== 0 &&
      mandatoryRaw !== "0";
    const answerScope = options.answerScope || "combined";
    const answersView =
      answerScope === "general"
        ? generalDynamicAnswers
        : answerScope === "category"
        ? categoryDynamicAnswers
        : dynamicAnswers;
    const setAnswer = (qCode, val) => setScopedAnswer(qCode, val, answerScope);
    const setAnswerError = (qCode, msg) => setScopedAnswerError(qCode, msg, answerScope);
    const getAnswerError = (qCode) => getScopedAnswerError(qCode, answerScope);
    const value = answersView[code];
    const nameSuffix =
      usePerTabCategoryAnswers && answerScope === "category" && activeSrCategoryCode
        ? `_${activeSrCategoryCode}`
        : "";
    const name = `q_${code}${nameSuffix}`;
    const typeIcon = getDynamicQuestionTypeIcon(typeCode);
    const indentStyle = options.flatQt001Qt003ConditionalChild
      ? undefined
      : level > 0
        ? { marginLeft: level * 28, paddingLeft: 12, borderLeft: "3px solid #e0e0e0" }
        : undefined;

    const labelClass = `form-label checklist-form-label component-details-label ${mandatory ? "required" : ""}`;

    const trimmedLabelText = String(text || "").trim();
    const sizedFieldLabelClass =
      trimmedLabelText.length > 0 && trimmedLabelText.length < 40
        ? `${labelClass} checklist-form-label-short`
        : trimmedLabelText.length > 40 && trimmedLabelText.length < 60
          ? `${labelClass} checklist-form-label-medium`
          : labelClass;


    const labelStyle = { textAlign: "left", fontWeight: "bold", fontSize: "14px" };

    const renderManufacturingDateField = (colClass) => {
      const stored = value == null ? "" : String(value);
      const displayValue = toManufacturingDateDisplay(stored);
      const hasError = Boolean(getAnswerError(code));
      const errorMessage = getAnswerError(code) || "";
      return (
        <div key={code} className={colClass} style={indentStyle}>
          <span className="dynamic-q-icon" aria-hidden="true">{typeIcon}</span>
          <div className="dynamic-q-control">
            {!options.suppressQtFieldLabel && (
              <label className={sizedFieldLabelClass} style={labelStyle}>
                {text}
                {mandatory && <span className="text-danger ms-1">*</span>}
              </label>
            )}
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              maxLength={10}
              placeholder="DD-MM-YYYY"
              className={`form-control checklist-form-control component-details-input ${hasError ? "is-invalid" : ""}`}
              style={{ minHeight: "47px", maxHeight: "47px", height: "47px" }}
              value={displayValue}
              title="DD-MM-YYYY"
              onChange={(e) => {
                const next = formatManufacturingDateTyping(e.target.value);
                setAnswer(code, next);
                setAnswerError(code, next.length === 10 ? validateManufacturingDate(next) : "");
              }}
              onBlur={(e) => {
                const next = toManufacturingDateDisplay(formatManufacturingDateTyping(e.target.value));
                setAnswer(code, next);
                setAnswerError(code, validateManufacturingDate(next));
              }}
            />
            {hasError && (
              <div className="invalid-feedback d-block">{errorMessage}</div>
            )}
          </div>
        </div>
      );
    };

    // QT013 (Level) – transparent wrapper; render nested questions without an extra field.
    if (typeCode === "QT013") {
      const levelChildren = children || [];
      if (levelChildren.length === 0) return null;
      return (
        <React.Fragment key={code || `level_${level}`}>
          {levelChildren.map((child) => renderDynamicQuestion(child, level, options))}
        </React.Fragment>
      );
    }

    // QT014 (Level Image Upload) – hide QuestionText; level text label like QT001, then upload.
    if (typeCode === "QT014") {
      const levelText = getChecklistLevelInputText(node);
      return (
        <React.Fragment key={code || `level_image_${level}`}>
          {renderChecklistLevelInputLabel(levelText, {
            typeIcon,
            labelClass,
            labelStyle,
            mandatory,
            indentStyle,
          })}
          {renderDynamicQuestion(
            { ...node, questionTypeCode: "QT009", QuestionTypeCode: "QT009" },
            level,
            { ...options, suppressQtFieldLabel: true }
          )}
        </React.Fragment>
      );
    }

    // QT015 (Level Text Box) – hide QuestionText; level text label like QT001, then text box.
    if (typeCode === "QT015") {
      const levelText = getChecklistLevelInputText(node);
      return (
        <React.Fragment key={code || `level_text_${level}`}>
          {renderChecklistLevelInputLabel(levelText, {
            typeIcon,
            labelClass,
            labelStyle,
            mandatory,
            indentStyle,
          })}
          {renderDynamicQuestion(
            { ...node, questionTypeCode: "QT007", QuestionTypeCode: "QT007" },
            level,
            { ...options, suppressQtFieldLabel: true }
          )}
        </React.Fragment>
      );
    }

    // Group Radio Button (QT012) – same runtime behavior as QT001.
    if (typeCode === "QT012") {
      return renderDynamicQuestion(
        { ...node, questionTypeCode: "QT001", QuestionTypeCode: "QT001" },
        level,
        options
      );
    }

    // Group Check Box (QT011) – same runtime behavior as QT003.
    if (typeCode === "QT011") {
      return renderDynamicQuestion(
        { ...node, questionTypeCode: "QT003", QuestionTypeCode: "QT003" },
        level,
        options
      );
    }

    // Label only (QT002) – Product Details style
    // When parent has children or node is a child (level > 0), use col-md-6 so parent/child sit side by side
    if (typeCode === "QT002") {
      if (
        isGreenlamBrandingCaptureQuestion(rawText) &&
        !isGreenlamBrandingCaptureVisible(questionTree, answersView)
      ) {
        return null;
      }

      const hasChildrenOrIsChild = children.length > 0 || level > 0;
      const qt002ColClass =
        options.inDynamicQSectionChildren && options.exactlyTwoSectionProductDetailRows
          ? "col-12 col-md-6 col-lg-6 dynamic-q-row dynamic-q-product-details"
          : options.inDynamicQSectionChildren && options.moreThanTwoSectionDirectChildren
            ? "col-12 col-md-6 col-lg-3 dynamic-q-row dynamic-q-product-details"
            : "col-12 col-md-12 dynamic-q-row dynamic-q-product-details";
      return (
        <React.Fragment key={code}>
          <div className={qt002ColClass} style={indentStyle}>
            <span className="dynamic-q-icon" aria-hidden="true">{typeIcon}</span>
            <label className={sizedFieldLabelClass} style={labelStyle}>
              {text}
              {mandatory && <span className="text-danger ms-1">*</span>}
            </label>
          </div>
          {children.length > 0 && (
            <div className="col-12 dynamic-q-qt002-children">
              <div className="row g-3">
                {children.map((child) =>
                  renderDynamicQuestion(child, level + 1, options)
                )}
              </div>
            </div>
          )}
        </React.Fragment>
      );
    }

    // QT006 as section header when it has sub-questions (children that have their own children)
    // col-md-6 so it sits next to "Does Prelam has printed information..." in the same row
    const hasSubQuestionChildren = (node.children || []).some((c) => (c.children || []).length > 0);
    if (typeCode === "QT006" && hasSubQuestionChildren) {
      return (
        <div key={code} className="col-12 col-md-12 dynamic-q-row dynamic-q-product-details dynamic-q-section" style={indentStyle}>
          <span className="dynamic-q-icon" aria-hidden="true">{typeIcon}</span>
          <label className={sizedFieldLabelClass} style={labelStyle}>
            {text}
            {mandatory && <span className="text-danger ms-1">*</span>}
          </label>
        </div>
      );
    }

    // QT006 (no sub-question children): either Multiple Choice (option rows from admin, often Label) → dropdown on parent, or QT007-only block → textboxes per child.
    if (typeCode === "QT006" && !hasSubQuestionChildren) {
      const qt007Children = (children || []).filter(
        (c) => (c.questionTypeCode || c.QuestionTypeCode || "").toUpperCase() === "QT007"
      );
      const qt005Children = (children || []).filter(
        (c) => (c.questionTypeCode || c.QuestionTypeCode || "").toUpperCase() === "QT005"
      );
      const allChildrenAreQt007 =
        (children || []).length > 0 && qt007Children.length === (children || []).length;

      // Make all QT006 blocks collapsible like QT006 sections (e.g. "Product Storage")
      const isCollapsed = collapsedSections.has(code);
      const shouldBeExpanded = !isCollapsed;
      const toggleSection = (e) => {
        e.stopPropagation();
        setCollapsedSections((prev) => {
          const next = new Set(prev);
          if (next.has(code)) next.delete(code);
          else next.add(code);
          return next;
        });
      };

      // QT005 / QT007 leaf children under QT006 — render each field (mixed QT005+QT007 when both exist).
      if (qt005Children.length > 0) {
        const fieldsToRender =
          qt007Children.length > 0
            ? (children || []).filter((c) => {
                const t = (c.questionTypeCode || c.QuestionTypeCode || "").toUpperCase();
                return t === "QT005" || t === "QT007";
              })
            : qt005Children;
        return (
          <div key={code} className="col-12 col-md-12 dynamic-q-section-with-children" style={indentStyle}>
            <div
              className="dynamic-q-row dynamic-q-product-details dynamic-q-section dynamic-q-section-toggle"
              onClick={toggleSection}
              style={{ cursor: "pointer" }}
            >
              <label className={labelClass} style={{ ...labelStyle, flex: 1 }}>
                {text}
                {mandatory && <span className="text-danger ms-1">*</span>}
              </label>
              <span className="dynamic-q-section-caret">
                {shouldBeExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </span>
            </div>
            {shouldBeExpanded && (
              <div className="dynamic-q-section-children">
                <div className="row g-3">
                  {fieldsToRender.map((child, idx) =>
                    renderDynamicQuestion(
                      child,
                      level + 1,
                      {
                        parentIsQt006: true,
                        siblingIndex: idx,
                        inDynamicQSectionChildren: true,
                        ...(isProductDetailsSectionLabel(text)
                          ? { inProductDetailsSection: true }
                          : {}),
                        ...(fieldsToRender.length === 1
                          ? { soleQt005OrQt007InSectionChildren: true }
                          : {}),
                        ...(fieldsToRender.length > 2 ? { moreThanTwoSectionDirectChildren: true } : {}),
                        ...(fieldsToRender.length === 2 ? { exactlyTwoSectionProductDetailRows: true } : {}),
                      }
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        );
      }

      // Admin "Multiple Choice" is QT006 with option-like leaf children (e.g. QT002 Label). Mirror QT004: one select, value on parent questionCode.
      if (!allChildrenAreQt007 && (children || []).length > 0) {
        const leafOptions = (children || []).filter((c) => !((c.children || []).length > 0));
        const mcOptions = leafOptions.length ? leafOptions : children;
        const optionLikeLeafTypeCodes = new Set(["QT001", "QT002", "QT003", "QT004", "QT006"]);
        const hasOnlyOptionLikeLeafChildren =
          mcOptions.length > 0 &&
          mcOptions.every((child) =>
            optionLikeLeafTypeCodes.has(
              String(child.questionTypeCode || child.QuestionTypeCode || "").toUpperCase()
            )
          );

        if (!hasOnlyOptionLikeLeafChildren) {
          return (
            <div key={code} className="col-12 col-md-12 dynamic-q-section-with-children" style={indentStyle}>
              <div
                className="dynamic-q-row dynamic-q-product-details dynamic-q-section dynamic-q-section-toggle"
                onClick={toggleSection}
                style={{ cursor: "pointer" }}
              >
                <label className={labelClass} style={{ ...labelStyle, flex: 1 }}>
                  {text}
                  {mandatory && <span className="text-danger ms-1">*</span>}
                </label>
                <span className="dynamic-q-section-caret">
                  {shouldBeExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </span>
              </div>
              {shouldBeExpanded && (
                <div className="dynamic-q-section-children">
                  <div className="row g-3">
                    {mcOptions.map((child, idx) =>
                      renderDynamicQuestion(
                        child,
                        level + 1,
                        {
                          parentIsQt006: true,
                          siblingIndex: idx,
                          inDynamicQSectionChildren: true,
                          ...(isProductDetailsSectionLabel(text)
                            ? { inProductDetailsSection: true }
                            : {}),
                          ...(mcOptions.length > 2 ? { moreThanTwoSectionDirectChildren: true } : {}),
                          ...(mcOptions.length === 2 ? { exactlyTwoSectionProductDetailRows: true } : {}),
                        }
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        }

        return (
          <div key={code} className="col-12 col-md-12 dynamic-q-section-with-children" style={indentStyle}>
            <div
              className="dynamic-q-row dynamic-q-product-details dynamic-q-section dynamic-q-section-toggle"
              onClick={toggleSection}
              style={{ cursor: "pointer" }}
            >
              <label className={labelClass} style={{ ...labelStyle, flex: 1 }}>
                {text}
                {mandatory && <span className="text-danger ms-1">*</span>}
              </label>
              <span className="dynamic-q-section-caret">
                {shouldBeExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </span>
            </div>
            {shouldBeExpanded && (
              <div className="dynamic-q-section-children">
                <div className="row g-3">
                  <div className="col-12 col-md-3 dynamic-q-row dynamic-q-product-details">
                    <div className="dynamic-q-control">
                      <select
                        className="form-select form-control checklist-form-control component-details-input"
                        value={value || ""}
                        onChange={(e) => setAnswer(code, e.target.value)}
                      >
                        <option value="">Select</option>
                        {mcOptions.map((opt, idx) => {
                          const optCode = opt.questionCode || opt.QuestionCode || idx;
                          const optText = opt.questionText || opt.QuestionText || "";
                          return (
                            <option key={optCode} value={optText}>
                              {optText}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      }

      return (
        <div key={code} className="col-12 col-md-12 dynamic-q-section-with-children" style={indentStyle}>
          <div
            className="dynamic-q-row dynamic-q-product-details dynamic-q-section dynamic-q-section-toggle"
            onClick={toggleSection}
            style={{ cursor: "pointer" }}
          >
            <label className={labelClass} style={{ ...labelStyle, flex: 1 }}>
              {text}
              {mandatory && <span className="text-danger ms-1">*</span>}
            </label>
            <span className="dynamic-q-section-caret">
              {shouldBeExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </span>
          </div>
          {shouldBeExpanded && (
            <div className="dynamic-q-section-children">
              <div className="row g-3">
                <div className="col-12 col-md-12 dynamic-q-row dynamic-q-product-details dynamic-q-qt006-with-inputs">
                  <div className="dynamic-q-control">
                    {qt007Children.length > 0 && (
                      <div className="dynamic-q-qt006-qt007-inputs mt-2 d-flex flex-wrap">
                        {qt007Children.map((child) => {
                          const childCode = child.questionCode || child.QuestionCode;
                          const childText = child.questionText || child.QuestionText || "";
                          const childValue = childCode ? (answersView[childCode] ?? "") : "";
                          const childMandatoryRaw = child.isMandatory ?? child.IsMandatory;
                          const childMandatory =
                            childMandatoryRaw !== null &&
                            childMandatoryRaw !== undefined &&
                            childMandatoryRaw !== false &&
                            childMandatoryRaw !== 0 &&
                            childMandatoryRaw !== "0";
                            const childLabelClass = `form-label checklist-form-label component-details-label ${childMandatory ? "required" : ""}`;
                            const trimmedChildLabelText = String(childText || "").trim();
                            const sizedChildLabelClass =
                              trimmedChildLabelText.length > 0 && trimmedChildLabelText.length < 40
                                ? `${childLabelClass} checklist-form-label-short`
                                : trimmedChildLabelText.length > 40 && trimmedChildLabelText.length < 60
                                  ? `${childLabelClass} checklist-form-label-medium`
                                  : childLabelClass;
                            const childHasError = childCode ? Boolean(getAnswerError(childCode)) : false;
                            const childErrorMessage = childCode ? (getAnswerError(childCode) || "") : "";

                          
                          const handleChildQt007Change = (e) => {
                            const newVal = e.target.value;
                            if (childCode) setAnswer(childCode, newVal);
                            const err = validateQt007Text(newVal);
                            if (childCode) setAnswerError(childCode, err || undefined);
                          };
                          return (
                            <div key={childCode || childText} className="col-12 col-md-6 col-lg-3 mb-2 px-2">
                              <label className={sizedChildLabelClass} style={{ textAlign: "left", fontWeight: "bold", fontSize: "14px" }}>
                                {childText}
                                {childMandatory && <span className="text-danger ms-1">*</span>}
                              </label>
                              <input
                                type="text"
                                className={`form-control checklist-form-control component-details-input mt-1 ${childHasError ? "is-invalid" : ""}`}
                                placeholder="Enter text"
                                value={childValue}
                                onChange={handleChildQt007Change}
                              />
                              {childHasError && (
                                <div className="invalid-feedback d-block">{childErrorMessage}</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    // Radio Button (QT001) – label above, options in styled block
    if (typeCode === "QT001") {
      const optionItems = getQt001Qt003OptionItems(node);
      const visibleConditionalChildren = mergeVisibleChecklistFollowUpChildren(node, answersView);
      const qt001Count = optionItems.length;
      const hasLongOptionLabelWithTwoItems =
        qt001Count === 2 &&
        optionItems.some((opt) => {
          const optText = String(opt?.questionText || opt?.QuestionText || "").trim();
          return optText.length >= 28;
        });
      const qt001ColClass =
        "dynamic-q-option-item dynamic-q-row dynamic-q-product-details";
      return (
        <div key={code} className={`${qt001ColClass} dynamic-q-row dynamic-q-product-details`} style={indentStyle}>
          <span className="dynamic-q-icon" aria-hidden="true">{typeIcon}</span>
          <div className="dynamic-q-control dynamic-q-control-inline-auto">
            <label className={`${labelClass} dynamic-q-label-qt001-qt003`} style={labelStyle}>
              {text}
              {mandatory && <span className="text-danger ms-1">*</span>}
            </label>
            <div className="component-details-options component-details-input dynamic-q-inline-options">
              <div className="radio-group d-flex flex-wrap gap-3 flex-row">
                {optionItems.map((opt, idx) => {
                  const optText = opt.questionText || opt.QuestionText || "";
                  const id = `${name}_${idx}`;
                  const valStr = String(value ?? "").trim();
                  const optStr = String(optText ?? "").trim();
                  const isChecked = valStr === optStr || valStr.toLowerCase() === optStr.toLowerCase();
                  return (
                    <div key={id} className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name={name}
                        id={id}
                        value={optText}
                        checked={isChecked}
                        onChange={() => {
                          setAnswer(code, optText);
                          if (
                            isGreenlamBrandingQuestion(rawText) &&
                            !isAffirmativeChecklistAnswer(optText)
                          ) {
                            clearGreenlamBrandingCaptureAnswers();
                          }
                          if (isSiteVisitQuestion(rawText)) {
                            syncComplaintAttendedByForSiteVisit(
                              templateCode,
                              isAffirmativeChecklistAnswer(optText)
                            );
                          }
                        }}
                      />
                      <label className="form-check-label" htmlFor={id}>{optText}</label>
                    </div>
                  );
                })}
              </div>
            </div>
            {visibleConditionalChildren.length > 0 && (
              <div className="row g-3 mt-1 dynamic-q-conditional-children-row">
                {visibleConditionalChildren.map((child, idx) =>
                  renderDynamicQuestion(
                    child,
                    level,
                    buildOptionRowFollowUpRenderOptions(child, node, {
                      ...options,
                      flatQt001Qt003ConditionalChild: true,
                      parentIsQt006: false,
                      siblingIndex: undefined,
                      ...(visibleConditionalChildren.length > 2 ? { moreThanTwoSectionDirectChildren: true } : {}),
                      ...(visibleConditionalChildren.length === 2 ? { exactlyTwoSectionProductDetailRows: true } : {}),
                    })
                  )
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    // Check Box (QT003) – label above, checkboxes in styled block
    // Show ALL children as options (e.g. Universal, Studio, Zenith). Children with sub-questions
    // (e.g. Universal) also get a separate row below with their dropdown/sub-options.
    if (typeCode === "QT003") {
      const optionItems = getQt001Qt003OptionItems(node);
      const visibleConditionalChildren = mergeVisibleChecklistFollowUpChildren(node, answersView);
      const hasOthersWithTwoMoreOptions =
        optionItems.length >= 3 &&
        optionItems.some((opt) => {
          const optText = normalizeOptionText(opt?.questionText || opt?.QuestionText || "");
          return optText === normalizeOptionText("Others (Please specify)");
        });
      const selected = (() => {
        if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
        if (typeof value === "string") {
          const s = value.trim();
          if (s.startsWith("[") && s.endsWith("]")) {
            try {
              const parsed = JSON.parse(s);
              if (Array.isArray(parsed)) return parsed.map((v) => String(v).trim()).filter(Boolean);
            } catch (_) {}
          }
          return s ? [s] : [];
        }
        return value ? [String(value).trim()] : [];
      })();
      const hasOthersSelectedInCheckbox = selected.some(
        (s) => normalizeOptionText(s) === normalizeOptionText("Others (Please specify)")
      );
      const toggle = (optText) => {
        const next = selected.includes(optText) ? selected.filter((s) => s !== optText) : [...selected, optText];
        setAnswer(code, next);
      };
      const qt003Count = optionItems.length;
      const qt003ColClass =
        hasOthersWithTwoMoreOptions
          ? "dynamic-q-option-item-full dynamic-q-row dynamic-q-product-details"
          : "dynamic-q-option-item dynamic-q-row dynamic-q-product-details";
      return (
        <div key={code} className={`${qt003ColClass} dynamic-q-row dynamic-q-product-details`} style={indentStyle}>
          <span className="dynamic-q-icon" aria-hidden="true">{typeIcon}</span>
          <div className="dynamic-q-control dynamic-q-control-inline-auto">
            <label className={`${labelClass} dynamic-q-label-qt001-qt003`} style={labelStyle}>
              {text}
              {mandatory && <span className="text-danger ms-1">*</span>}
            </label>
            <div className="component-details-options component-details-input dynamic-q-checkbox-options dynamic-q-inline-options">
              <div className="d-flex flex-wrap gap-3">
                {optionItems.map((opt, idx) => {
                  const optCode = opt.questionCode || opt.QuestionCode || `opt_${idx}`;
                  const optText = opt.questionText || opt.QuestionText || "";
                  const id = `${name}_${String(optCode).replace(/\s/g, "_")}`;
                  return (
                    <div key={optCode} className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={id}
                        checked={selected.includes(optText)}
                        onChange={() => toggle(optText)}
                      />
                      <label className="form-check-label" htmlFor={id}>{optText}</label>
                    </div>
                  );
                })}
              </div>
            </div>
            {visibleConditionalChildren.length > 0 && (
              <div
                className="row g-3 mt-1 dynamic-q-conditional-children-row"
                style={{
                  width: "100%",
                  minWidth: "100%",
                  maxWidth: "100%",
                  flexBasis: "100%",
                }}
              >
                {visibleConditionalChildren.map((child, idx) =>
                  renderDynamicQuestion(
                    child,
                    level,
                    buildOptionRowFollowUpRenderOptions(child, node, {
                      ...options,
                      flatQt001Qt003ConditionalChild: true,
                      parentIsQt006: false,
                      siblingIndex: undefined,
                      ...(
                        hasOthersSelectedInCheckbox &&
                        normalizeOptionText(child?.questionText || child?.QuestionText || "") ===
                          normalizeOptionText("Others (Please specify)")
                          ? { forceFullWidthFromConditionalCheckbox: true }
                          : {}
                      ),
                      ...(
                        hasOthersSelectedInCheckbox &&
                        normalizeOptionText(child?.questionText || child?.QuestionText || "") ===
                          normalizeOptionText("Others (Please specify)")
                          ? { forceMandatoryWhenOthersSelected: true }
                          : {}
                      ),
                      ...(hasOthersWithTwoMoreOptions ? { forceFullWidthFromConditionalCheckbox: true } : {}),
                      ...(visibleConditionalChildren.length > 2 ? { moreThanTwoSectionDirectChildren: true } : {}),
                      ...(visibleConditionalChildren.length === 2 ? { exactlyTwoSectionProductDetailRows: true } : {}),
                    })
                  )
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    // Dropdowns (QT004) – label above, select with Product Details styling
    // Options = all children (e.g. under "Universal" the dropdown shows Universal-01, Universal-02)
    if (typeCode === "QT004") {
      const qt004DropdownItems = getSelectableOptionItems(node);
      const qt004ColClass =
        options.qt004HalfWidthBetweenSectionSiblings
          ? "col-12 col-md-6 col-lg-6 dynamic-q-row dynamic-q-product-details"
          : options.lastDropdownFullWidthAfterNestedSection
            ? "col-12 col-md-6 col-lg-12 dynamic-q-row dynamic-q-product-details"
          : options.inDynamicQSectionChildren && options.exactlyTwoSectionProductDetailRows
            ? "col-12 col-md-6 col-lg-6 dynamic-q-row dynamic-q-product-details"
            : "col-12 col-md-6 col-lg-3 dynamic-q-row dynamic-q-product-details";
      return (
        <div key={code} className={qt004ColClass} style={indentStyle}>
          <span className="dynamic-q-icon" aria-hidden="true">{typeIcon}</span>
          <div className="dynamic-q-control">
            <label className={sizedFieldLabelClass} style={labelStyle}>
              {text}
              {mandatory && <span className="text-danger ms-1">*</span>}
            </label>
            <select
              className="form-select form-control checklist-form-control component-details-input"
              value={value || ""}
              onChange={(e) => setAnswer(code, e.target.value)}
            >
              <option value="">Select</option>
              {qt004DropdownItems.map((opt, idx) => {
                const optCode = opt.questionCode || opt.QuestionCode || idx;
                const optText = opt.questionText || opt.QuestionText || "";
                return <option key={optCode} value={optText}>{optText}</option>;
              })}
            </select>
          </div>
        </div>
      );
    }

    // Special Text (QT005) – always full row width (long descriptive answers)
    if (typeCode === "QT005") {
      if (
        isComplaintAttendedByQuestion(rawText) &&
        !isComplaintAttendedByVisible(questionTree, answersView)
      ) {
        return null;
      }
      const mandatoryForRender = mandatory || Boolean(options.forceMandatoryWhenOthersSelected);
      const complaintAttendedByLocked =
        isComplaintAttendedByQuestion(rawText) &&
        isSiteVisitAffirmative(questionTree, answersView);
      const complaintAttendedByValue = complaintAttendedByLocked
        ? getLoggedInUserDisplayName() || value || ""
        : value || "";
      const qt005ColClass =
        "col-12 col-md-12 col-lg-12 dynamic-q-row dynamic-q-product-details";
      return (
        <div key={code} className={qt005ColClass} style={indentStyle}>
          {!isOthersPleaseSpecifyQuestion && (
            <span className="dynamic-q-icon" aria-hidden="true">{typeIcon}</span>
          )}
          <div className="dynamic-q-control">
            <label
              className={`${sizedFieldLabelClass} dynamic-q-label-qt005`}
              style={labelStyle}
            >
              {text}
              {mandatoryForRender && <span className="text-danger ms-1">*</span>}
            </label>
            <textarea
              className={`form-control checklist-form-control component-details-input dynamic-q-input-textbox${complaintAttendedByLocked ? " bg-light" : ""}`}
              rows={1}
              placeholder="Enter text"
              value={complaintAttendedByValue}
              title={complaintAttendedByValue}
              readOnly={complaintAttendedByLocked}
              style={{
                minHeight: "47px",
                maxHeight: "47px",
                height: "47px",
                resize: "none",
                ...(complaintAttendedByLocked
                  ? { backgroundColor: "#e9ecef", cursor: "not-allowed" }
                  : {}),
              }}
              onChange={(e) => {
                if (complaintAttendedByLocked) return;
                setAnswer(code, e.target.value);
              }}
            />
          </div>
        </div>
      );
    }

    // Text Box (QT007) – same size and validation as Search via Invoice No.: single-line input; only A–Z, a–z, 0–9; no spaces/special chars; show "Special characters are not allowed." when invalid.
    if (typeCode === "QT007") {
      if (
        isComplaintAttendedByQuestion(rawText) &&
        !isComplaintAttendedByVisible(questionTree, answersView)
      ) {
        return null;
      }
      const mandatoryForRender = mandatory || Boolean(options.forceMandatoryWhenOthersSelected);
      const complaintAttendedByLocked =
        isComplaintAttendedByQuestion(rawText) &&
        isSiteVisitAffirmative(questionTree, answersView);
      const complaintAttendedByValue = complaintAttendedByLocked
        ? getLoggedInUserDisplayName() || value || ""
        : value || "";
      const hasError = Boolean(getAnswerError(code));
      const errorMessage = getAnswerError(code) || "";
      const handleQt007Change = (e) => {
        if (complaintAttendedByLocked) return;
        const newVal = e.target.value;
        setAnswer(code, newVal);
        const err = validateQt007Text(newVal);
        setAnswerError(code, err || undefined);
      };
      const qt007FullWidthInSection =
        options.soleQt005OrQt007InSectionChildren ||
        options.lastTextFieldFullWidthAfterNestedSection;
      const qt007ColClass =
        options.forceFullWidthFromConditionalCheckbox
          ? "col-12 col-md-12 col-lg-12 dynamic-q-row dynamic-q-product-details"
          : options.inDynamicQSectionChildren && options.exactlyTwoSectionProductDetailRows
          ? "col-12 col-md-6 col-lg-6 dynamic-q-row dynamic-q-product-details"
          : options.inDynamicQSectionChildren && qt007FullWidthInSection
            ? "col-12 col-md-6 col-lg-12 dynamic-q-row dynamic-q-product-details"
            : options.inDynamicQSectionChildren && options.moreThanTwoSectionDirectChildren
              ? "col-12 col-md-6 col-lg-3 dynamic-q-row dynamic-q-product-details"
              : "col-12 col-md-6 dynamic-q-row dynamic-q-product-details";
      if (isManufacturingDateQuestion(rawText)) {
        return renderManufacturingDateField(qt007ColClass);
      }
      return (
        <div key={code} className={qt007ColClass} style={indentStyle}>
          {!isOthersPleaseSpecifyQuestion && (
            <span className="dynamic-q-icon" aria-hidden="true">{typeIcon}</span>
          )}
          <div className="dynamic-q-control">
            {!options.suppressQtFieldLabel && (
              <label className={sizedFieldLabelClass} style={labelStyle}>
                {text}
                {mandatoryForRender && <span className="text-danger ms-1">*</span>}
              </label>
            )}
            <input
              type="text"
              className={`form-control checklist-form-control component-details-input ${hasError ? "is-invalid" : ""}${complaintAttendedByLocked ? " bg-light" : ""}`}
              placeholder="Enter text"
              value={complaintAttendedByValue}
              title={complaintAttendedByValue}
              readOnly={complaintAttendedByLocked}
              style={{
                minHeight: "47px",
                maxHeight: "47px",
                height: "47px",
                ...(complaintAttendedByLocked
                  ? { backgroundColor: "#e9ecef", cursor: "not-allowed" }
                  : {}),
              }}
              onChange={handleQt007Change}
            />
            {hasError && (
              <div className="invalid-feedback d-block">{errorMessage}</div>
            )}
          </div>
        </div>
      );
    }

    // Number (QT008) – digits only; no <= invoice/SR qty cap on checklist page.
    if (typeCode === "QT008") {
      const hasError = Boolean(getAnswerError(code));
      const errorMessage = getAnswerError(code) || "";
      const isMaterialDerivedField = isMaterialDerivedQt008Field(rawText);
      const handleQt008Change = (e) => {
        if (isMaterialDerivedField) return;
        const digitsOnly = String(e.target.value || "").replace(/\D/g, "");
        setAnswer(code, digitsOnly);

        if (digitsOnly === "") {
          setAnswerError(code, undefined);
          return;
        }
        if (!/^\d+$/.test(digitsOnly)) {
          setAnswerError(code, "Only digits are allowed.");
          return;
        }
        setAnswerError(code, undefined);
      };
      const qt008ColClass =
        options.inDynamicQSectionChildren && options.exactlyTwoSectionProductDetailRows
          ? "col-12 col-md-6 col-lg-6 dynamic-q-row dynamic-q-product-details"
          : options.inDynamicQSectionChildren && options.moreThanTwoSectionDirectChildren
            ? "col-12 col-md-6 col-lg-3 dynamic-q-row dynamic-q-product-details"
            : "col-12 col-md-6 dynamic-q-row dynamic-q-product-details";
      const qt008Placeholder = isMaterialDerivedField ? "" : "Enter Number / Qty";
      return (
        <div key={code} className={qt008ColClass} style={indentStyle}>
          <span className="dynamic-q-icon" aria-hidden="true">{typeIcon}</span>
          <div className="dynamic-q-control">
            <label className={sizedFieldLabelClass} style={labelStyle}>
              {text}
              {mandatory && <span className="text-danger ms-1">*</span>}
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className={`form-control checklist-form-control component-details-input ${hasError ? "is-invalid" : ""} ${isMaterialDerivedField ? "bg-light" : ""}`}
              placeholder={qt008Placeholder}
              value={value || ""}
              title={value || ""}
              style={{
                minHeight: "47px",
                maxHeight: "47px",
                height: "47px",
                ...(isMaterialDerivedField
                  ? { backgroundColor: "#e9ecef", cursor: "not-allowed" }
                  : {}),
              }}
              readOnly={isMaterialDerivedField}
              onChange={handleQt008Change}
            />
            {hasError && (
              <div className="invalid-feedback d-block">{errorMessage}</div>
            )}
          </div>
        </div>
      );
    }

    // Image Upload (QT009) – upload one image and store uploaded file path as answer
    if (typeCode === "QT009") {
      if (
        isGreenlamBrandingCaptureQuestion(rawText) &&
        !isGreenlamBrandingCaptureVisible(questionTree, answersView)
      ) {
        return null;
      }

      const hasError = Boolean(getAnswerError(code));
      const errorMessage = getAnswerError(code) || "";
      const uploadKey = getQt009UploadKey(templateCode, code, answerScope);
      const localImages = Array.isArray(qt009ImageAnswers[uploadKey]) ? qt009ImageAnswers[uploadKey] : [];
      const isUploading = Boolean(qt009Uploading[uploadKey]);
      const answerItems = parseQt009AnswerToItems(value);
      const displayItems = localImages.length > 0 ? localImages : answerItems;

      const handleQt009FileChange = async (e) => {
        const selectedFiles = Array.from(e.target.files || []);
        if (selectedFiles.length === 0) return;

        const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
        const validFiles = selectedFiles.filter((f) => allowedTypes.includes(String(f.type || "").toLowerCase()));
        if (validFiles.length !== selectedFiles.length) {
          Swal.fire({
            title: "Invalid File",
            text: "Only JPG and PNG images are allowed.",
            icon: "warning",
            confirmButtonColor: "#ff9800",
          });
        }
        if (validFiles.length === 0) {
          e.target.value = "";
          return;
        }

        const existingItems = displayItems;

        const existingSize = existingItems.reduce(
          (total, item) => total + (Number(item?.file?.size ?? item?.size ?? 0) || 0),
          0
        );
        const incomingSize = validFiles.reduce(
          (total, file) => total + (Number(file?.size) || 0),
          0
        );
        if (existingSize + incomingSize > MAX_DAMAGE_IMAGE_TOTAL_BYTES) {
          Swal.fire({
            title: "Limit Exceeded",
            text: "Total image size cannot exceed 12 MB.",
            icon: "warning",
            confirmButtonColor: "#ff9800",
          });
          e.target.value = "";
          return;
        }

        Promise.all(
          validFiles.map(
            (file) =>
              new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                  resolve({
                    id: `${Date.now()}_${Math.random()}`,
                    file,
                    preview: reader.result,
                    name: file.name,
                    type: file.type,
                    size: file.size,
                    filePath: "",
                  });
                };
                reader.readAsDataURL(file);
              })
          )
        ).then((newItems) => {
          setQt009ImageAnswers((prev) => ({
            ...prev,
            [uploadKey]: [...existingItems, ...newItems],
          }));
          setAnswer(
            code,
            [...existingItems, ...newItems].map((x) => x.filePath || x.name).filter(Boolean)
          );
          setAnswerError(code, undefined);
        });
        e.target.value = "";
      };

      const qt009ColClass =
        "col-12 dynamic-q-row dynamic-q-product-details";

      return (
        <div key={code} className={qt009ColClass} style={indentStyle}>
          <span className="dynamic-q-icon" aria-hidden="true">{typeIcon}</span>
          <div className="dynamic-q-control">
          {!options.optionRowTypedFollowUp && !options.suppressQtFieldLabel && (
            <label className={sizedFieldLabelClass} style={labelStyle}>
              {text}
              {mandatory && <span className="text-danger ms-1">*</span>}
            </label>
          )}
            <FileUploadPanelLayout
              className={IMAGE_UPLOAD_PANEL_CLASS}
              invalid={hasError}
              title="Upload Images"
              formatNote={IMAGE_UPLOAD_FORMAT_NOTE}
              helperText={IMAGE_UPLOAD_HELPER}
              chooseLabel={isUploading ? "Uploading..." : "Choose Files"}
              accept="image/jpeg,image/jpg,image/png"
              multiple
              onChange={handleQt009FileChange}
              disabled={isUploading}
              chooseStyle={getImageUploadChooseStyle(isUploading)}
              totalHint={buildImageUploadTotalHint(displayItems)}
              files={displayItems}
              onOpen={(item) => {
                const itemPath = String(item?.filePath || "").trim();
                const itemUrl = itemPath ? resolveFileUrl(itemPath) : "";
                if (item?.file || itemUrl) handleOpenChecklistAttachment(item);
              }}
              onRemove={(item) => {
                const nextItems = displayItems.filter((x) => x.id !== item.id);
                setQt009ImageAnswers((prev) => ({ ...prev, [uploadKey]: nextItems }));
                setAnswer(
                  code,
                  nextItems.map((x) => x.filePath || x.name).filter(Boolean)
                );
              }}
            />
            {hasError && (
              <div className="invalid-feedback d-block">{errorMessage}</div>
            )}
          </div>
        </div>
      );
    }

    // Datepicker (QT010) – date input with current date default; cannot exceed today.
    if (typeCode === "QT010") {
      const qt010ColClass = "dynamic-q-option-item dynamic-q-row dynamic-q-product-details";
      if (isManufacturingDateQuestion(text)) {
        return renderManufacturingDateField(qt010ColClass);
      }
      const hasError = Boolean(getAnswerError(code));
      const errorMessage = getAnswerError(code) || "";
      const today = getTodayISODate();
      const displayValue = clampChecklistDateToToday(value) || today;

      return (
        <div key={code} className={qt010ColClass} style={indentStyle}>
          <span className="dynamic-q-icon" aria-hidden="true">{typeIcon}</span>
          <div className="dynamic-q-control dynamic-q-control-inline-fit dynamic-q-control-date-inline">
          <label className={sizedFieldLabelClass} style={labelStyle}>
              {text}
              {mandatory && <span className="text-danger ms-1">*</span>}
            </label>
            <input
              type="date"
              className={`form-control checklist-form-control component-details-input ${hasError ? "is-invalid" : ""}`}
              value={displayValue}
              max={today}
              onChange={(e) => {
                const selectedDate = e.target.value;
                const clamped = clampChecklistDateToToday(selectedDate);
                setAnswer(code, clamped);
                if (selectedDate && isChecklistDateAfterToday(selectedDate)) {
                  setAnswerError(code, CHECKLIST_FUTURE_DATE_ERROR);
                } else {
                  setAnswerError(code, "");
                }
              }}
              onBlur={(e) => {
                const selectedDate = e.target.value;
                if (selectedDate && isChecklistDateAfterToday(selectedDate)) {
                  setAnswerError(code, CHECKLIST_FUTURE_DATE_ERROR);
                  setAnswer(code, today);
                }
              }}
            />
            {hasError && (
              <div className="invalid-feedback d-block">{errorMessage}</div>
            )}
          </div>
        </div>
      );
    }

    // Fallback: text input – label above
    const fallbackColClass =
      options.inDynamicQSectionChildren && options.moreThanTwoSectionDirectChildren
        ? "col-12 col-md-6 col-lg-3 dynamic-q-row dynamic-q-product-details"
        : "col-12 col-md-12 dynamic-q-row dynamic-q-product-details";
    return (
      <div key={code} className={fallbackColClass} style={indentStyle}>
        <span className="dynamic-q-icon" aria-hidden="true">{typeIcon}</span>
        <div className="dynamic-q-control">
        <label className={sizedFieldLabelClass} style={labelStyle}>
            {text}
            {mandatory && <span className="text-danger ms-1">*</span>}
          </label>
          <input
            type="text"
            className="form-control checklist-form-control component-details-input"
            placeholder="Enter value"
            value={value || ""}
            onChange={(e) => setAnswer(code, e.target.value)}
          />
        </div>
      </div>
    );
  };

  // Invoice Details card (same design as /csrp/raise-complaint) - used at top when no templates, and inside each checklist tab
  const renderInvoiceDetailsCard = () => {
    const showInv = searchParams.get("invoiceNo") || invoiceNo;
    if (!showInv) return null;
    const products = invoiceDetails && (Array.isArray(invoiceDetails.products) ? invoiceDetails.products : Array.isArray(invoiceDetails.Products) ? invoiceDetails.Products : []);
    const urlProdCode = searchParams.get("productCode") || productCode;
    const selectedProduct = products && (products.find((p) => (p.ProductCode || p.productCode || "").toString() === String(urlProdCode || "").trim()) || products[0]);
    const invNo = invoiceDetails?.invoiceNo ?? invoiceDetails?.InvoiceNo ?? invoiceDetails?.invoiceNumber ?? searchParams.get("invoiceNo") ?? "";
    const invDate = invoiceDetails?.invoiceDate ?? invoiceDetails?.InvoiceDate ?? "";
    const factoryInvoiceDetailsValue = invNo;
    const urlBusinessVerticalRaw =
      searchParams.get("businessVertical") || searchParams.get("divisionName") || "";
    const urlBusinessVertical = normalizeQueryValue(urlBusinessVerticalRaw) || urlBusinessVerticalRaw;
    const bvLower = String(urlBusinessVertical).trim().toLowerCase();
    const urlProductCategoryNorm = normalizeQueryValue(searchParams.get("productCategory"));
    const invoiceProductCategoryNorm =
      selectedProduct != null
        ? normalizeQueryValue(
            selectedProduct.ProductCategory ??
              selectedProduct.productCategory ??
              selectedProduct.ProductCategoryDescription ??
              selectedProduct.productCategoryDescription ??
              ""
          )
        : "";
    const activeTemplateName =
      templates[activeChecklistTab]?.templateName ?? templates[0]?.templateName ?? "";
    const invoiceBusinessVerticalHint = normalizeQueryValue(
      invoiceDetails?.businessVertical ??
        invoiceDetails?.BusinessVertical ??
        invoiceDetails?.divisionName ??
        invoiceDetails?.DivisionName ??
        ""
    );
    const isSturdoBusinessVertical =
      matchesSturdoText(urlBusinessVertical) ||
      matchesSturdoText(invoiceBusinessVerticalHint) ||
      matchesSturdoText(searchParams.get("productCategory")) ||
      matchesSturdoText(urlProductCategoryNorm) ||
      matchesSturdoText(invoiceProductCategoryNorm) ||
      matchesSturdoText(activeTemplateName) ||
      matchesSturdoText(
        selectedProduct?.BusinessVertical ?? selectedProduct?.businessVertical
      ) ||
      matchesSturdoText(
        selectedProduct?.DivisionName ?? selectedProduct?.divisionName
      );
    const isPrelamBusinessVertical = bvLower.includes("prelam");
    /** Strong hide: some global/layout CSS can fight Bootstrap `.d-none`; inline display wins for invoice field columns. */
    const invColProps = (visible) => ({
      className: `col-lg-3 col-md-6${visible ? "" : " d-none invoice-detail-field-hidden"}`,
      style: visible ? undefined : { display: "none" },
    });
    const isMikasaPlyBusinessVertical =
      matchesMikasaPlyText(urlBusinessVertical) ||
      matchesMikasaPlyText(invoiceBusinessVerticalHint) ||
      matchesMikasaPlyText(searchParams.get("productCategory")) ||
      matchesMikasaPlyText(urlProductCategoryNorm) ||
      matchesMikasaPlyText(invoiceProductCategoryNorm) ||
      matchesMikasaPlyText(activeTemplateName) ||
      matchesMikasaPlyText(
        selectedProduct?.BusinessVertical ?? selectedProduct?.businessVertical
      ) ||
      matchesMikasaPlyText(
        selectedProduct?.DivisionName ?? selectedProduct?.divisionName
      );
    const isMikasaFloorsBusinessVertical =
      matchesMikasaFloorsText(urlBusinessVertical) ||
      matchesMikasaFloorsText(invoiceBusinessVerticalHint) ||
      matchesMikasaFloorsText(searchParams.get("productCategory")) ||
      matchesMikasaFloorsText(urlProductCategoryNorm) ||
      matchesMikasaFloorsText(invoiceProductCategoryNorm) ||
      matchesMikasaFloorsText(activeTemplateName) ||
      matchesMikasaFloorsText(
        selectedProduct?.BusinessVertical ?? selectedProduct?.businessVertical
      ) ||
      matchesMikasaFloorsText(
        selectedProduct?.DivisionName ?? selectedProduct?.divisionName
      );
    const isMikasaDoorBusinessVertical =
      matchesMikasaDoorText(urlBusinessVertical) ||
      matchesMikasaDoorText(invoiceBusinessVerticalHint) ||
      matchesMikasaDoorText(searchParams.get("productCategory")) ||
      matchesMikasaDoorText(urlProductCategoryNorm) ||
      matchesMikasaDoorText(invoiceProductCategoryNorm) ||
      matchesMikasaDoorText(activeTemplateName) ||
      matchesMikasaDoorText(
        selectedProduct?.BusinessVertical ?? selectedProduct?.businessVertical
      ) ||
      matchesMikasaDoorText(
        selectedProduct?.DivisionName ?? selectedProduct?.divisionName
      );
    const isMikasaVeneerBusinessVertical =
      (bvLower.includes("mikasa") && bvLower.includes("veneer")) ||
      matchesMikasaVeneerText(urlBusinessVertical) ||
      matchesMikasaVeneerText(invoiceBusinessVerticalHint) ||
      matchesMikasaVeneerText(searchParams.get("productCategory")) ||
      matchesMikasaVeneerText(urlProductCategoryNorm) ||
      matchesMikasaVeneerText(invoiceProductCategoryNorm) ||
      matchesMikasaVeneerText(activeTemplateName) ||
      matchesMikasaVeneerText(
        selectedProduct?.BusinessVertical ?? selectedProduct?.businessVertical
      ) ||
      matchesMikasaVeneerText(
        selectedProduct?.DivisionName ?? selectedProduct?.divisionName
      );
    const isLaminateBusinessVertical =
      (bvLower.includes("laminate") && !bvLower.includes("prelam")) ||
      matchesLaminateText(urlBusinessVertical) ||
      matchesLaminateText(invoiceBusinessVerticalHint) ||
      matchesLaminateText(searchParams.get("productCategory")) ||
      matchesLaminateText(urlProductCategoryNorm) ||
      matchesLaminateText(invoiceProductCategoryNorm) ||
      matchesLaminateText(activeTemplateName) ||
      matchesLaminateText(
        selectedProduct?.BusinessVertical ?? selectedProduct?.businessVertical
      ) ||
      matchesLaminateText(
        selectedProduct?.DivisionName ?? selectedProduct?.divisionName
      );
    const isExteriorCompactCladsBusinessVertical =
      matchesExteriorCompactCladsText(urlBusinessVertical) ||
      matchesExteriorCompactCladsText(invoiceBusinessVerticalHint) ||
      matchesExteriorCompactCladsText(searchParams.get("productCategory")) ||
      matchesExteriorCompactCladsText(urlProductCategoryNorm) ||
      matchesExteriorCompactCladsText(invoiceProductCategoryNorm) ||
      matchesExteriorCompactCladsText(activeTemplateName) ||
      matchesExteriorCompactCladsText(
        selectedProduct?.BusinessVertical ?? selectedProduct?.businessVertical
      ) ||
      matchesExteriorCompactCladsText(
        selectedProduct?.DivisionName ?? selectedProduct?.divisionName
      );
    const isExteriorCompactMikasaFxBusinessVertical =
      matchesExteriorCompactMikasaFxText(urlBusinessVertical) ||
      matchesExteriorCompactMikasaFxText(invoiceBusinessVerticalHint) ||
      matchesExteriorCompactMikasaFxText(searchParams.get("productCategory")) ||
      matchesExteriorCompactMikasaFxText(urlProductCategoryNorm) ||
      matchesExteriorCompactMikasaFxText(invoiceProductCategoryNorm) ||
      matchesExteriorCompactMikasaFxText(activeTemplateName) ||
      matchesExteriorCompactMikasaFxText(
        selectedProduct?.BusinessVertical ?? selectedProduct?.businessVertical
      ) ||
      matchesExteriorCompactMikasaFxText(
        selectedProduct?.DivisionName ?? selectedProduct?.divisionName
      );
    const isInteriorCompactsNonSturdoBusinessVertical =
      matchesInteriorCompactsNonSturdoText(urlBusinessVertical) ||
      matchesInteriorCompactsNonSturdoText(invoiceBusinessVerticalHint) ||
      matchesInteriorCompactsNonSturdoText(searchParams.get("productCategory")) ||
      matchesInteriorCompactsNonSturdoText(urlProductCategoryNorm) ||
      matchesInteriorCompactsNonSturdoText(invoiceProductCategoryNorm) ||
      matchesInteriorCompactsNonSturdoText(activeTemplateName) ||
      matchesInteriorCompactsNonSturdoText(
        selectedProduct?.BusinessVertical ?? selectedProduct?.businessVertical
      ) ||
      matchesInteriorCompactsNonSturdoText(
        selectedProduct?.DivisionName ?? selectedProduct?.divisionName
      );
    const mergedProductCategoryLower = (
      urlProductCategoryNorm ||
      invoiceProductCategoryNorm ||
      ""
    ).toLowerCase();
    const invoiceProductLineHintsLower =
      selectedProduct != null
        ? [
            selectedProduct.ProductName,
            selectedProduct.productName,
            selectedProduct.ProductDescription,
            selectedProduct.productDescription,
            selectedProduct.MaterialDescription,
            selectedProduct.materialDescription,
            selectedProduct.ItemDescription,
            selectedProduct.itemDescription,
          ]
            .filter((x) => x != null && String(x).trim() !== "")
            .map((x) => String(x).toLowerCase())
            .join(" ")
        : "";
    /** PPB–MFC may be sent as `productCategory` or as `businessVertical` (e.g. Plain+Particle+Board+(PPB)+-+MFC). */
    const bvNormalizedForPpbHints = normalizeQueryValue(
      searchParams.get("businessVertical") || searchParams.get("divisionName") || ""
    ).toLowerCase();
    const matchesPpbMfcText = matchesPpbMfcBusinessVerticalText;
    const isPrelamProductCategory =
      mergedProductCategoryLower.includes("prelam") ||
      invoiceProductLineHintsLower.includes("prelam") ||
      bvNormalizedForPpbHints.includes("prelam");
    /** Plain Particle Board (PPB) – MFC (from product category, business vertical, or invoice line text). */
    const isPpbMfcProductCategory =
      matchesPpbMfcText(mergedProductCategoryLower) ||
      matchesPpbMfcText(bvNormalizedForPpbHints) ||
      matchesPpbMfcText(invoiceProductLineHintsLower);
    const isPpbMfcBusinessVertical =
      matchesPpbMfcText(urlBusinessVertical) ||
      matchesPpbMfcText(invoiceBusinessVerticalHint) ||
      matchesPpbMfcText(searchParams.get("productCategory")) ||
      matchesPpbMfcText(urlProductCategoryNorm) ||
      matchesPpbMfcText(invoiceProductCategoryNorm) ||
      matchesPpbMfcText(activeTemplateName) ||
      matchesPpbMfcText(
        selectedProduct?.BusinessVertical ?? selectedProduct?.businessVertical
      ) ||
      matchesPpbMfcText(
        selectedProduct?.DivisionName ?? selectedProduct?.divisionName
      ) ||
      isPpbMfcProductCategory;
    const isPrelamMfcBusinessVertical =
      matchesPrelamMfcText(urlBusinessVertical) ||
      matchesPrelamMfcText(invoiceBusinessVerticalHint) ||
      matchesPrelamMfcText(searchParams.get("productCategory")) ||
      matchesPrelamMfcText(urlProductCategoryNorm) ||
      matchesPrelamMfcText(invoiceProductCategoryNorm) ||
      matchesPrelamMfcText(activeTemplateName) ||
      matchesPrelamMfcText(
        selectedProduct?.BusinessVertical ?? selectedProduct?.businessVertical
      ) ||
      matchesPrelamMfcText(
        selectedProduct?.DivisionName ?? selectedProduct?.divisionName
      );
    /** Legacy Prelam (non-MFC) only: Thickness, Size, Emission. */
    const showInvoiceFourFieldOnly =
      !isMikasaPlyBusinessVertical &&
      !isMikasaFloorsBusinessVertical &&
      !isMikasaDoorBusinessVertical &&
      !isLaminateBusinessVertical &&
      !isMikasaVeneerBusinessVertical &&
      !isPrelamMfcBusinessVertical &&
      !isPpbMfcBusinessVertical &&
      !isExteriorCompactCladsBusinessVertical &&
      !isExteriorCompactMikasaFxBusinessVertical &&
      !isInteriorCompactsNonSturdoBusinessVertical &&
      !isSturdoBusinessVertical &&
      (isPrelamBusinessVertical || isPrelamProductCategory);
    /** MikasaPly BV: Variant Name, Thickness, Size, Invoice Number. */
    const showInvoiceMikasaPlyThreeFieldOnly =
      isMikasaPlyBusinessVertical &&
      !isMikasaFloorsBusinessVertical &&
      !isMikasaDoorBusinessVertical;
    /** Mikasa Door BV: Product name, Factory Invoice Details, Size (W x L X T). */
    const showInvoiceMikasaDoorThreeFieldOnly =
      isMikasaDoorBusinessVertical &&
      !isMikasaPlyBusinessVertical &&
      !isMikasaFloorsBusinessVertical &&
      !isLaminateBusinessVertical &&
      !isMikasaVeneerBusinessVertical &&
      !isSturdoBusinessVertical &&
      !isPrelamMfcBusinessVertical &&
      !isExteriorCompactCladsBusinessVertical &&
      !isExteriorCompactMikasaFxBusinessVertical &&
      !isInteriorCompactsNonSturdoBusinessVertical;
    /** Interior Compacts - Non-Sturdo Applications: Manufacturing Plant, Thickness, Size, Factory Invoice Details. */
    const showInvoiceInteriorCompactsNonSturdoFourFieldOnly =
      isInteriorCompactsNonSturdoBusinessVertical &&
      !isMikasaPlyBusinessVertical &&
      !isMikasaFloorsBusinessVertical &&
      !isMikasaDoorBusinessVertical &&
      !isLaminateBusinessVertical &&
      !isMikasaVeneerBusinessVertical &&
      !isSturdoBusinessVertical &&
      !isPrelamMfcBusinessVertical &&
      !isExteriorCompactCladsBusinessVertical &&
      !isExteriorCompactMikasaFxBusinessVertical;
    /** Mikasa Floors BV: Product name, Invoice no, Invoice Date, Quantity, Size, Thickness (Quantity Ordered hidden). */
    const showInvoiceMikasaFloorsSevenFieldOnly =
      isMikasaFloorsBusinessVertical &&
      !isMikasaPlyBusinessVertical &&
      !isMikasaDoorBusinessVertical &&
      !isLaminateBusinessVertical &&
      !isMikasaVeneerBusinessVertical &&
      !isSturdoBusinessVertical &&
      !isPrelamMfcBusinessVertical &&
      !isExteriorCompactCladsBusinessVertical &&
      !isExteriorCompactMikasaFxBusinessVertical &&
      !isInteriorCompactsNonSturdoBusinessVertical;
    /** Exterior Compact - Mikasa Fx BV: Design Number, Thickness. */
    const showInvoiceExteriorCompactMikasaFxTwoFieldOnly =
      isExteriorCompactMikasaFxBusinessVertical &&
      !isMikasaPlyBusinessVertical &&
      !isMikasaFloorsBusinessVertical &&
      !isMikasaDoorBusinessVertical &&
      !isLaminateBusinessVertical &&
      !isMikasaVeneerBusinessVertical &&
      !isSturdoBusinessVertical &&
      !isInteriorCompactsNonSturdoBusinessVertical &&
      !isPrelamMfcBusinessVertical &&
      !isExteriorCompactCladsBusinessVertical;
    /** Exterior Compact - Clads BV: Manufacturing Plant, Design Number, Thickness/Size, Factory Invoice Details. */
    const showInvoiceExteriorCompactCladsFourFieldOnly =
      isExteriorCompactCladsBusinessVertical &&
      !isExteriorCompactMikasaFxBusinessVertical &&
      !isMikasaPlyBusinessVertical &&
      !isMikasaFloorsBusinessVertical &&
      !isMikasaDoorBusinessVertical &&
      !isLaminateBusinessVertical &&
      !isMikasaVeneerBusinessVertical &&
      !isSturdoBusinessVertical &&
      !isInteriorCompactsNonSturdoBusinessVertical &&
      !isPrelamMfcBusinessVertical;
    /** Laminate BV: Manufacturing Plant, Laminate Brand, Design Number, Product Finish, Size, Thickness, Factory Invoice Details. */
    const showInvoiceLaminateSevenFieldOnly =
      isLaminateBusinessVertical &&
      !isMikasaPlyBusinessVertical &&
      !isMikasaFloorsBusinessVertical &&
      !isMikasaDoorBusinessVertical &&
      !isMikasaVeneerBusinessVertical &&
      !isExteriorCompactCladsBusinessVertical &&
      !isExteriorCompactMikasaFxBusinessVertical &&
      !isInteriorCompactsNonSturdoBusinessVertical;
    /** Mikasa Veneer BV: Design No./Collection, Species Name, Invoice no/date, Substrate, Size. */
    const showInvoiceMikasaVeneerSevenFieldOnly =
      isMikasaVeneerBusinessVertical &&
      !isMikasaPlyBusinessVertical &&
      !isMikasaFloorsBusinessVertical &&
      !isMikasaDoorBusinessVertical &&
      !isLaminateBusinessVertical &&
      !isSturdoBusinessVertical &&
      !isPrelamMfcBusinessVertical &&
      !isExteriorCompactCladsBusinessVertical &&
      !isExteriorCompactMikasaFxBusinessVertical &&
      !isInteriorCompactsNonSturdoBusinessVertical;
    /** Sturdo BV: Manufacturing Plant, Variant Name, Model Name, Hardware Colour, Size, Thickness, Factory Invoice Details. */
    const showInvoiceSturdoSevenFieldOnly =
      isSturdoBusinessVertical &&
      !isMikasaPlyBusinessVertical &&
      !isMikasaFloorsBusinessVertical &&
      !isMikasaDoorBusinessVertical &&
      !isLaminateBusinessVertical &&
      !isMikasaVeneerBusinessVertical &&
      !isExteriorCompactCladsBusinessVertical &&
      !isExteriorCompactMikasaFxBusinessVertical &&
      !isInteriorCompactsNonSturdoBusinessVertical &&
      !isPrelamMfcBusinessVertical;
    /** Prelam - MFC / PPB - MFC: invoice + product fields with variant radios. */
    const showInvoicePrelamPpbMfcLayout =
      (isPrelamMfcBusinessVertical || isPpbMfcBusinessVertical) &&
      !isMikasaPlyBusinessVertical &&
      !isMikasaFloorsBusinessVertical &&
      !isMikasaDoorBusinessVertical &&
      !isLaminateBusinessVertical &&
      !isMikasaVeneerBusinessVertical &&
      !isSturdoBusinessVertical &&
      !isExteriorCompactCladsBusinessVertical &&
      !isExteriorCompactMikasaFxBusinessVertical &&
      !isInteriorCompactsNonSturdoBusinessVertical;
    const prelamPpbMfcVariantOptions =
      productVariants.length > 0 ? productVariants : DEFAULT_PRELAM_PPB_MFC_VARIANT_OPTIONS;
    const prelamPpbMfcFields = selectedProduct
      ? resolvePrelamPpbMfcInvoiceFields(selectedProduct, prelamPpbMfcVariantOptions)
      : {
          emission: "",
          finish: "",
          variantProductGrade: "",
          designNumber: "",
          size: "",
          thickness: "",
          quantity: "",
        };
    const prelamPpbMfcQuantity =
      selectedMaterialQtyLimit != null && selectedMaterialQtyLimit > 0
        ? String(selectedMaterialQtyLimit)
        : prelamPpbMfcFields.quantity != null && String(prelamPpbMfcFields.quantity).trim() !== ""
          ? String(prelamPpbMfcFields.quantity)
          : "";
    const salesInvoiceNoDateValue =
      invNo && invDate ? `${invNo} / ${invDate}` : invNo || invDate || "";
    const mikasaPlyVariantName = [
      selectedProduct?.productVariantName,
      selectedProduct?.ProductVariantName,
      selectedProduct?.ProductDescription,
      selectedProduct?.productDescription,
      selectedProduct?.ProductCategoryDescription,
      selectedProduct?.productCategoryDescription,
      selectedProduct?.ProductCategory,
      selectedProduct?.productCategory,
    ].find((value) => value != null && String(value).trim() !== "");
    const mikasaPlyInvoiceNumber =
      invNo || searchParams.get("invoiceNo") || "";
    const mikasaFloorsProductName =
      selectedProduct?.ProductName ??
      selectedProduct?.productName ??
      selectedProduct?.ProductDescription ??
      selectedProduct?.productDescription ??
      selectedProduct?.MaterialDescription ??
      selectedProduct?.materialDescription ??
      "";
    const mikasaDoorProductName = mikasaFloorsProductName;
    const mikasaDoorSizeWlxlt = (() => {
      const size = selectedProduct?.Size ?? selectedProduct?.size ?? "";
      const thickness =
        selectedProduct?.ProductThickness ??
        selectedProduct?.productThickness ??
        selectedProduct?.Thickness ??
        "";
      if (size && thickness) return `${size} x ${thickness}`;
      return size || thickness || "";
    })();
    const mikasaFloorsQuantity =
      selectedMaterialQtyLimit != null && selectedMaterialQtyLimit > 0
        ? String(selectedMaterialQtyLimit)
        : "";
    const invFieldVisible = (whenFourFieldOnly, otherwiseVisible) =>
      invColProps(showInvoiceFourFieldOnly ? whenFourFieldOnly : otherwiseVisible);
    const showInvoiceHeaderRow =
      !isSturdoBusinessVertical &&
      !isLaminateBusinessVertical &&
      !isExteriorCompactCladsBusinessVertical &&
      !isExteriorCompactMikasaFxBusinessVertical &&
      !isInteriorCompactsNonSturdoBusinessVertical &&
      !isMikasaFloorsBusinessVertical &&
      !isMikasaDoorBusinessVertical &&
      !isMikasaPlyBusinessVertical &&
      !isMikasaVeneerBusinessVertical &&
      !isPrelamMfcBusinessVertical &&
      !isPpbMfcBusinessVertical;
    const showLaminateLayout = isLaminateBusinessVertical && !isSturdoBusinessVertical;
    const showDefaultLayout = showInvoiceHeaderRow;
    return (
      <div className="card-panel mt-2 pt-2 pb-3 ">
        <div className="section-heading">Invoice & Product Details</div>
        <div className="row g-2">
          {invoiceDetailsLoading && (
            <div className="col-12">
              <div className="checklist-page-loader py-4">
                <CircularProgress size={32} />
                <p className="text-muted mt-2 mb-0">Loading invoice details...</p>
              </div>
            </div>
          )}
          {!invoiceDetailsLoading && invoiceDetails && (
            <>
              {selectedProduct && (
                <>
                  {showInvoiceMikasaPlyThreeFieldOnly ? (
                    <>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Variant Name</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={mikasaPlyVariantName ?? ""}
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Thickness (MM)</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={
                            selectedProduct.ProductThickness ??
                            selectedProduct.productThickness ??
                            ""
                          }
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Size (MM)</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={selectedProduct.Size ?? selectedProduct.size ?? ""}
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Invoice Number</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={mikasaPlyInvoiceNumber}
                          readOnly
                        />
                      </div>
                    </>
                  ) : showInvoiceMikasaVeneerSevenFieldOnly ? (
                    <>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Design No./Collection</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={(() => {
                            const no =
                              selectedProduct.DesignNo ?? selectedProduct.designNo ?? "";
                            const desc =
                              selectedProduct.DesignDescription ??
                              selectedProduct.designDescription ??
                              "";
                            if (no && desc) return `${no} / ${desc}`;
                            return no || desc || "";
                          })()}
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Species Name</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={
                            selectedProduct.SpeciesName ??
                            selectedProduct.speciesName ??
                            ""
                          }
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Invoice no</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={invNo}
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Invoice date</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={invDate}
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Substrate</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={selectedProduct.substrate ?? selectedProduct.Substrate ?? ""}
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Size (MM)</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={selectedProduct.Size ?? selectedProduct.size ?? ""}
                          readOnly
                        />
                      </div>
                    </>
                  ) : showInvoiceMikasaDoorThreeFieldOnly ? (
                    <>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Product name</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={mikasaDoorProductName}
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Factory Invoice Details</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={factoryInvoiceDetailsValue}
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Size (MM) (W x L X T)</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={mikasaDoorSizeWlxlt}
                          readOnly
                        />
                      </div>
                    </>
                  ) : showInvoiceInteriorCompactsNonSturdoFourFieldOnly ? (
                    <>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Manufacturing Plant</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={
                            selectedProduct.productPlantDescription ??
                            selectedProduct.ProductPlantDescription ??
                            selectedProduct.PlantName ??
                            selectedProduct.plantName ??
                            ""
                          }
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Thickness (MM)</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={
                            selectedProduct.ProductThickness ??
                            selectedProduct.productThickness ??
                            selectedProduct.Thickness ??
                            ""
                          }
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Size (MM)</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={selectedProduct.Size ?? selectedProduct.size ?? ""}
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Factory Invoice Details</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={factoryInvoiceDetailsValue}
                          readOnly
                        />
                      </div>
                    </>
                  ) : showInvoiceExteriorCompactMikasaFxTwoFieldOnly ? (
                    <>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Design Number</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={selectedProduct.DesignNo ?? selectedProduct.designNo ?? ""}
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Thickness (MM)</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={
                            selectedProduct.ProductThickness ??
                            selectedProduct.productThickness ??
                            selectedProduct.Thickness ??
                            ""
                          }
                          readOnly
                        />
                      </div>
                    </>
                  ) : showInvoiceExteriorCompactCladsFourFieldOnly ? (
                    <>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Manufacturing Plant</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={
                            selectedProduct.productPlantDescription ??
                            selectedProduct.ProductPlantDescription ??
                            selectedProduct.PlantName ??
                            selectedProduct.plantName ??
                            ""
                          }
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Design Number</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={selectedProduct.DesignNo ?? selectedProduct.designNo ?? ""}
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Thickness (MM)/Size (MM)</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={(() => {
                            const thickness =
                              selectedProduct.ProductThickness ??
                              selectedProduct.productThickness ??
                              selectedProduct.Thickness ??
                              "";
                            const size = selectedProduct.Size ?? selectedProduct.size ?? "";
                            if (thickness && size) return `${thickness} / ${size}`;
                            return thickness || size || "";
                          })()}
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Factory Invoice Details</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={factoryInvoiceDetailsValue}
                          readOnly
                        />
                      </div>
                    </>
                  ) : showInvoiceLaminateSevenFieldOnly ? (
                    <>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Manufacturing Plant</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={
                            selectedProduct.productPlantDescription ??
                            selectedProduct.ProductPlantDescription ??
                            selectedProduct.PlantName ??
                            selectedProduct.plantName ??
                            ""
                          }
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Laminate Brand</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={selectedProduct.ProductBrand ?? selectedProduct.productBrand ?? ""}
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Design Number</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={selectedProduct.DesignNo ?? selectedProduct.designNo ?? ""}
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Product Finish</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={selectedProduct.ProductFinish ?? selectedProduct.productFinish ?? ""}
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Size (MM)</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={selectedProduct.Size ?? selectedProduct.size ?? ""}
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Thickness (MM)</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={
                            selectedProduct.ProductThickness ??
                            selectedProduct.productThickness ??
                            ""
                          }
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Factory Invoice Details</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={factoryInvoiceDetailsValue}
                          readOnly
                        />
                      </div>
                    </>
                  ) : showInvoiceMikasaFloorsSevenFieldOnly ? (
                    <>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Product name</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={mikasaFloorsProductName}
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Invoice no</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={invNo}
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Invoice Date</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={invDate}
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Quantity</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={mikasaFloorsQuantity}
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Size (MM)</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={selectedProduct.Size ?? selectedProduct.size ?? ""}
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Thickness (MM)</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={
                            selectedProduct.ProductThickness ??
                            selectedProduct.productThickness ??
                            selectedProduct.Thickness ??
                            ""
                          }
                          readOnly
                        />
                      </div>
                    </>
                  ) : showInvoiceSturdoSevenFieldOnly ? (
                    <>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Manufacturing Plant</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={
                            selectedProduct.productPlantDescription ??
                            selectedProduct.ProductPlantDescription ??
                            selectedProduct.PlantName ??
                            selectedProduct.plantName ??
                            ""
                          }
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Variant Name</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={
                            selectedProduct.productVariantName ??
                            selectedProduct.ProductVariantName ??
                            ""
                          }
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Model Name</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={selectedProduct.ModelName ?? selectedProduct.modelName ?? ""}
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Hardware Colour</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={
                            selectedProduct.HardwareColour ?? selectedProduct.hardwareColour ?? ""
                          }
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Size (MM)</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={selectedProduct.Size ?? selectedProduct.size ?? ""}
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Thickness (MM)</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={
                            selectedProduct.ProductThickness ??
                            selectedProduct.productThickness ??
                            ""
                          }
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Factory Invoice Details</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={factoryInvoiceDetailsValue}
                          readOnly
                        />
                      </div>
                    </>
                  ) : showInvoicePrelamPpbMfcLayout ? (
                    <>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Invoice No.</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={invNo}
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Invoice Date</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={invDate}
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Quantity</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={prelamPpbMfcQuantity}
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Size (MM)</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={prelamPpbMfcFields.size || (selectedProduct.Size ?? selectedProduct.size ?? "")}
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Thickness (MM)</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={
                            prelamPpbMfcFields.thickness ||
                            (selectedProduct.ProductThickness ??
                              selectedProduct.productThickness ??
                              selectedProduct.Thickness ??
                              "")
                          }
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Design Number</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={
                            prelamPpbMfcFields.designNumber ||
                            (selectedProduct.DesignNo ?? selectedProduct.designNo ?? "")
                          }
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Finish</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={prelamPpbMfcFields.finish}
                          readOnly
                        />
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <label className="input-label">Emission</label>
                        <input
                          type="text"
                          className="form-control form-control-light"
                          value={prelamPpbMfcFields.emission}
                          readOnly
                        />
                      </div>
                    </>
                  ) : (
                    <>
                  <div {...invFieldVisible(false,showInvoiceHeaderRow)}>
                    <label className="input-label">Invoice Number</label>
                    <input type="text" className="form-control form-control-light" value={invNo} readOnly />
                  </div>
                  <div {...invFieldVisible(false,showInvoiceHeaderRow)}>
                    <label className="input-label">Invoice Date</label>
                    <input type="text" className="form-control form-control-light" value={invDate} readOnly />
                  </div>
                  <div {...invFieldVisible(false,true)}>
                    <label className="input-label">Manufacturing Plant</label>
                    <input
                      type="text"
                      className="form-control form-control-light"
                      value={
                        selectedProduct.productPlantDescription ??
                        selectedProduct.ProductPlantDescription ??
                        selectedProduct.PlantName ??
                        selectedProduct.plantName ??
                        ""
                      }
                      readOnly
                    />
                  </div>
                  <div {...invFieldVisible(false,!isSturdoBusinessVertical)}>
                    <label className="input-label">Laminate Brand</label>
                    <input
                      type="text"
                      className="form-control form-control-light"
                      value={selectedProduct.ProductBrand ?? selectedProduct.productBrand ?? ""}
                      readOnly
                    />
                  </div>
                  <div {...invFieldVisible(false,showLaminateLayout)}>
                    <label className="input-label">Design Number</label>
                    <input
                      type="text"
                      className="form-control form-control-light"
                      value={selectedProduct.DesignNo ?? selectedProduct.designNo ?? ""}
                      readOnly
                    />
                  </div>
                  <div {...invFieldVisible(false,showDefaultLayout)}>
                    <label className="input-label">Design No.</label>
                    <input
                      type="text"
                      className="form-control form-control-light"
                      value={selectedProduct.DesignNo ?? selectedProduct.designNo ?? ""}
                      readOnly
                    />
                  </div>
                  <div {...invFieldVisible(false,showLaminateLayout)}>
                    <label className="input-label">Product Finish</label>
                    <input
                      type="text"
                      className="form-control form-control-light"
                      value={selectedProduct.ProductFinish ?? selectedProduct.productFinish ?? ""}
                      readOnly
                    />
                  </div>
                  <div {...invFieldVisible(false,showDefaultLayout)}>
                    <label className="input-label">Finish</label>
                    <input
                      type="text"
                      className="form-control form-control-light"
                      value={selectedProduct.ProductFinish ?? selectedProduct.productFinish ?? ""}
                      readOnly
                    />
                  </div>
                  <div {...invFieldVisible(true, true)}>
                    <label className="input-label">Size (MM)</label>
                    <input
                      type="text"
                      className="form-control form-control-light"
                      value={selectedProduct.Size ?? selectedProduct.size ?? ""}
                      readOnly
                    />
                  </div>
                  <div {...invFieldVisible(true, isPrelamBusinessVertical || showLaminateLayout)}>
                    <label className="input-label">Thickness (MM)</label>
                    <input
                      type="text"
                      className="form-control form-control-light"
                      value={selectedProduct.ProductThickness ?? selectedProduct.productThickness ?? ""}
                      readOnly
                    />
                  </div>
                  <div {...invFieldVisible(false,isSturdoBusinessVertical || showLaminateLayout)}>
                    <label className="input-label">Factory Invoice Details</label>
                    <input
                      type="text"
                      className="form-control form-control-light"
                      value={factoryInvoiceDetailsValue}
                      readOnly
                    />
                  </div>
                  <div {...invFieldVisible(true, showDefaultLayout)}>
                    <label className="input-label">Emission</label>
                    <input
                      type="text"
                      className="form-control form-control-light"
                      value={selectedProduct.Emission ?? selectedProduct.emission ?? ""}
                      readOnly
                    />
                  </div>
                  <div {...invFieldVisible(false, false)}>
                    <label className="input-label">Date of manufacturing (MFD)</label>
                    <input
                      type="text"
                      className="form-control form-control-light"
                      value={selectedProduct.MFGDate ?? selectedProduct.mfgDate ?? ""}
                      readOnly
                    />
                  </div>
                  <div {...invFieldVisible(false,isSturdoBusinessVertical)}>
                    <label className="input-label">Variant Name</label>
                    <input
                      type="text"
                      className="form-control form-control-light"
                      value={selectedProduct.productVariantName ?? selectedProduct.ProductVariantName ?? ""}
                      readOnly
                    />
                  </div>
                  <div {...invFieldVisible(false,isSturdoBusinessVertical)}>
                    <label className="input-label">Model Name</label>
                    <input
                      type="text"
                      className="form-control form-control-light"
                      value={selectedProduct.ModelName ?? selectedProduct.modelName ?? ""}
                      readOnly
                    />
                  </div>
                  <div {...invFieldVisible(false,isSturdoBusinessVertical)}>
                    <label className="input-label">Hardware Colour</label>
                    <input
                      type="text"
                      className="form-control form-control-light"
                      value={selectedProduct.HardwareColour ?? selectedProduct.hardwareColour ?? ""}
                      readOnly
                    />
                  </div>
                  <div {...invFieldVisible(false,showDefaultLayout)}>
                    <label className="input-label">Variant</label>
                    <input
                      type="text"
                      className="form-control form-control-light"
                      value={selectedProduct.productVariantName ?? selectedProduct.ProductVariantName ?? ""}
                      readOnly
                    />
                  </div>
                  <div {...invFieldVisible(false,showDefaultLayout)}>
                    <label className="input-label">Product name</label>
                    <input
                      type="text"
                      className="form-control form-control-light"
                      value={
                        selectedProduct.ProductName ??
                        selectedProduct.productName ??
                        selectedProduct.ProductDescription ??
                        selectedProduct.MaterialDescription ??
                        ""
                      }
                      readOnly
                    />
                  </div>
                  <div {...invFieldVisible(false,showDefaultLayout)}>
                    <label className="input-label">Size Ordered / Received</label>
                    <input
                      type="text"
                      className="form-control form-control-light"
                      value={selectedProduct.Size ?? selectedProduct.size ?? ""}
                      readOnly
                    />
                  </div>
                  <div {...invFieldVisible(false,showDefaultLayout)}>
                    <label className="input-label">Substrate</label>
                    <input
                      type="text"
                      className="form-control form-control-light"
                      value={selectedProduct.substrate ?? selectedProduct.Substrate ?? ""}
                      readOnly
                    />
                  </div>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // Customer Details card - auto-populated from invoice details; shown for each checklist (and when no templates)
  const renderCustomerDetailsCard = () => {
    const showInv = searchParams.get("invoiceNo") || invoiceNo;
    if (!showInv) return null;
    const inv = invoiceDetails;
    const customerName = inv?.customerName ?? inv?.CustomerName ?? "";
    const customerContactPerson =
      inv?.customerContactPerson ??
      inv?.CustomerContactPerson ??
      inv?.contactPerson ??
      inv?.ContactPerson ??
      inv?.customerPersonName ??
      inv?.CustomerPersonName ??
      "";
    const addressParts = [
      inv?.customerAddress ?? inv?.CustomerAddress ?? "",
      inv?.customerCity ?? inv?.CustomerCity ?? "",
      inv?.customerState ?? inv?.CustomerState ?? "",
      inv?.customerPinCode ?? inv?.CustomerPinCode ?? "",
      inv?.customerCountry ?? inv?.CustomerCountry ?? "",
    ].filter(Boolean);
    const address = addressParts.length > 0 ? addressParts.join(", ") : "";
    const contactNumber = inv?.customerMobileNo ?? inv?.CustomerAlternateNo ?? inv?.customerPhone ?? inv?.CustomerPhone ?? "";
    return (
      <div className="card-panel mt-2 pt-2 pb-3 ">
        <div className="section-heading">Customer Details</div>
        <div className="row g-2">
          <div className="col-lg-3 col-md-6">
            <label className="input-label">Customer Name</label>
            <input type="text" className="form-control form-control-light" value={customerName} readOnly />
          </div>
          <div className="col-lg-3 col-md-6">
            <label className="input-label">Customer Contact Person</label>
            <input type="text" className="form-control form-control-light" value={customerContactPerson} readOnly />
          </div>
          <div className="col-lg-3 col-md-6">
            <label className="input-label">Address</label>
            <input type="text" className="form-control form-control-light" value={address} readOnly />
          </div>
          <div className="col-lg-3 col-md-6">
            <label className="input-label">Mobile No.</label>
            <input type="text" className="form-control form-control-light" value={contactNumber} readOnly />
          </div>
          <div
            id="complaintReceivedDateField"
            className="col-lg-3 col-md-6 d-none invoice-detail-field-hidden"
            style={{ display: "none" }}
          >
            <label className="input-label" htmlFor="complaintReceivedDate">
              Date of SR Received
            </label>
            <input
              id="complaintReceivedDate"
              type="date"
              className="form-control form-control-light"
              value={complaintReceivedDate}
              max={getLocalTodayISODate()}
              onChange={(e) => {
                const selected = e.target.value;
                setComplaintReceivedDate(
                  selected && isChecklistDateAfterToday(selected)
                    ? getLocalTodayISODate()
                    : selected
                );
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  // Render a question node and its nested sub-questions.
  // - For QT006 section nodes, put section header and ALL children in ONE column so the block sits immediately under the heading.
  // - Children of QT006 sections get letter-prefixed labels: a) Customer Address:, b) Contact Person:, etc.
  // - Otherwise only recurse into children that have their own children (sub-questions).
  /** Matches nodes that render the full-width .dynamic-q-section-with-children wrapper (QT006 with nested content). */
  const nodeRendersDynamicQSectionWithChildren = (n) => {
    if (!n) return false;
    const t = (n.questionTypeCode || n.QuestionTypeCode || "").toUpperCase();
    if (t !== "QT006") return false;
    const kids = n.children || [];
    if (kids.some((c) => (c.children || []).length > 0)) return true;
    return kids.length > 0;
  };
  /** Lone QT005/QT007 row (no nested sub-question rows under this node). */
  const isQt005OrQt007SingleLeafRow = (n) => {
    if (!n) return false;
    const t = (n.questionTypeCode || n.QuestionTypeCode || "").toUpperCase();
    if (t !== "QT005" && t !== "QT007") return false;
    return !(n.children || []).some((c) => (c.children || []).length > 0);
  };

  /** Per-child index: true when this QT004 is in a gap of 1–2 consecutive QT004 nodes between two QT006 .dynamic-q-section-with-children siblings. */
  const getQt004SandwichedHalfWidthFlags = (arr) => {
    const len = arr.length;
    const out = new Array(len).fill(false);
    if (!len) return out;
    let a = 0;
    while (a < len) {
      while (a < len && !nodeRendersDynamicQSectionWithChildren(arr[a])) a += 1;
      if (a >= len) break;
      let b = a + 1;
      while (b < len && !nodeRendersDynamicQSectionWithChildren(arr[b])) b += 1;
      if (b >= len) break;
      const gap = b - a - 1;
      if (gap >= 1 && gap <= 2) {
        let allQt004 = true;
        for (let k = a + 1; k < b; k += 1) {
          const t = (arr[k].questionTypeCode || arr[k].QuestionTypeCode || "").toUpperCase();
          if (t !== "QT004") {
            allQt004 = false;
            break;
          }
        }
        if (allQt004) {
          for (let k = a + 1; k < b; k += 1) out[k] = true;
        }
      }
      a = b;
    }
    return out;
  };

  const renderQuestionNode = (node, level = 0, options = {}) => {
    const typeCode = (node.questionTypeCode || node.QuestionTypeCode || "").toUpperCase();
    const hasSubQuestionChildren = (node.children || []).some((c) => (c.children || []).length > 0);
    const isQt006Section = typeCode === "QT006" && hasSubQuestionChildren;

    const childrenToRecurse = isQt006Section
      ? (node.children || [])
      : isChecklistOptionBasedParentType(typeCode) ||
          typeCode === "QT013" ||
          typeCode === "QT014" ||
          typeCode === "QT015"
        ? []
        : (node.children || []).filter((child) => (child.children || []).length > 0);

    const soleQt005OrQt007InSectionChildren =
      isQt006Section &&
      childrenToRecurse.length === 1 &&
      (() => {
        const onlyChild = childrenToRecurse[0];
        const t = (onlyChild.questionTypeCode || onlyChild.QuestionTypeCode || "").toUpperCase();
        if (t !== "QT005" && t !== "QT007") return false;
        const hasNestedSubRows = (onlyChild.children || []).some(
          (c) => (c.children || []).length > 0
        );
        return !hasNestedSubRows;
      })();

    const lastIdx = childrenToRecurse.length - 1;
    const lastNode = lastIdx >= 0 ? childrenToRecurse[lastIdx] : null;
    const lastTextFieldFullWidthAfterNestedSection =
      isQt006Section &&
      lastIdx >= 1 &&
      isQt005OrQt007SingleLeafRow(lastNode) &&
      childrenToRecurse.slice(0, lastIdx).some((c) => nodeRendersDynamicQSectionWithChildren(c));
    const lastDropdownFullWidthAfterNestedSection =
      isQt006Section &&
      lastIdx >= 1 &&
      String(lastNode?.questionTypeCode || lastNode?.QuestionTypeCode || "").toUpperCase() === "QT004" &&
      childrenToRecurse.slice(0, lastIdx).some((c) => nodeRendersDynamicQSectionWithChildren(c));

    const exactlyTwoSectionProductDetailRows =
      isQt006Section &&
      childrenToRecurse.length === 2 &&
      childrenToRecurse.every((c) => !nodeRendersDynamicQSectionWithChildren(c));

    const qt004SandwichedHalfWidthFlags = isQt006Section
      ? getQt004SandwichedHalfWidthFlags(childrenToRecurse)
      : null;

    const optionsForChildRecursion =
      !isQt006Section && options && options.qt004HalfWidthBetweenSectionSiblings
        ? (() => {
            const { qt004HalfWidthBetweenSectionSiblings: _omitQt004Root, ...rest } = options;
            return rest;
          })()
        : options;

    const childRows = childrenToRecurse.flatMap((child, idx) =>
      renderQuestionNode(
        child,
        level + 1,
        isQt006Section
          ? {
              ...optionsForChildRecursion,
              parentIsQt006: true,
              siblingIndex: idx,
              inDynamicQSectionChildren: true,
              ...(isProductDetailsSectionLabel(
                node.questionText || node.QuestionText || ""
              )
                ? { inProductDetailsSection: true }
                : {}),
              ...(soleQt005OrQt007InSectionChildren
                ? { soleQt005OrQt007InSectionChildren: true }
                : {}),
              ...(lastTextFieldFullWidthAfterNestedSection && idx === lastIdx
                ? { lastTextFieldFullWidthAfterNestedSection: true }
                : {}),
              ...(lastDropdownFullWidthAfterNestedSection && idx === lastIdx
                ? { lastDropdownFullWidthAfterNestedSection: true }
                : {}),
              ...(exactlyTwoSectionProductDetailRows
                ? { exactlyTwoSectionProductDetailRows: true }
                : {}),
              ...(qt004SandwichedHalfWidthFlags && qt004SandwichedHalfWidthFlags[idx]
                ? { qt004HalfWidthBetweenSectionSiblings: true }
                : {}),
              ...(childrenToRecurse.length > 2 ? { moreThanTwoSectionDirectChildren: true } : {}),
            }
          : optionsForChildRecursion
      )
    );

    if (isQt006Section && childRows.length > 0) {
      const code = node.questionCode || node.QuestionCode || "section";
      const text = node.questionText || node.QuestionText || "";
      const mandatoryRaw = node.isMandatory ?? node.IsMandatory;
      const mandatory =
        mandatoryRaw !== null &&
        mandatoryRaw !== undefined &&
        mandatoryRaw !== false &&
        mandatoryRaw !== 0 &&
        mandatoryRaw !== "0";
      const labelClass = `form-label checklist-form-label component-details-label ${mandatory ? "required" : ""}`;
      const labelStyle = { textAlign: "left", fontWeight: "bold", fontSize: "14px" };
      const qt006ToggleLabelStyle = { ...labelStyle, fontSize: "calc(1em + 1px)" };
      const typeIcon = getDynamicQuestionTypeIcon(typeCode);
      
      // Initialize as expanded (initially open)
      // If code is in collapsedSections Set, it means it was explicitly collapsed
      const isCollapsed = collapsedSections.has(code);
      const shouldBeExpanded = !isCollapsed;
      
      const toggleSection = (e) => {
        e.stopPropagation(); // Prevent any parent click handlers
        setCollapsedSections((prev) => {
          const next = new Set(prev);
          if (next.has(code)) {
            // Currently collapsed, remove from Set to expand
            next.delete(code);
          } else {
            // Currently expanded (not in Set), add to Set to collapse
            next.add(code);
          }
          return next;
        });
      };
      
      return [
        <div key={code} className="col-12 col-md-12 dynamic-q-section-with-children">
          <div 
            className="dynamic-q-row dynamic-q-product-details dynamic-q-section dynamic-q-section-toggle"
            onClick={toggleSection}
            style={{ cursor: "pointer" }}
          >
            <label className={labelClass} style={{ ...qt006ToggleLabelStyle, flex: 1 }}>
              {text}
              {mandatory && <span className="text-danger ms-1">*</span>}
            </label>
            <span className="dynamic-q-section-caret">
              {shouldBeExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </span>
          </div>
          {shouldBeExpanded && (
            <div className="dynamic-q-section-children">
              <div className="row g-3">{childRows}</div>
            </div>
          )}
        </div>,
      ];
    }

    const rows = [renderDynamicQuestion(node, level, options)];
    return [...rows, ...childRows];
  };

  const isChecklistPageLoading = invoiceDetailsLoading || checklistLoading || draftLoading;

  const getChecklistLoadingMessage = () => {
    if (invoiceDetailsLoading) return "Loading invoice details...";
    if (checklistLoading) {
      return templates.length > 0
        ? "Loading checklist questions..."
        : "Loading checklist...";
    }
    if (draftLoading) return "Loading saved draft...";
    return "Loading...";
  };

  const renderChecklistPageLoader = (message = "Loading...") => (
    <div className="checklist-page-loader text-center py-5" role="status" aria-live="polite">
      <CircularProgress size={42} />
      <p className="text-muted mt-3 mb-0">{message}</p>
    </div>
  );

  return (
    <div className="raise-page">
      <div className="card-panel mb-3">
        <div className="section-heading d-flex align-items-center gap-2">
          <FilterListIcon fontSize="small" className="text-muted" />
          <span>Fill in the details below to submit your SR.</span>
        </div>

        {isChecklistPageLoading && templates.length === 0 &&
          renderChecklistPageLoader(getChecklistLoadingMessage())}

        {/* Invoice Details card - when no checklist tabs: show here; when checklist tabs: shown inside each tab below */}
        {(searchParams.get("invoiceNo") || invoiceNo) &&
          templates.length === 0 &&
          !isChecklistPageLoading &&
          renderCustomerDetailsCard()}
        {(searchParams.get("invoiceNo") || invoiceNo) &&
          templates.length === 0 &&
          !isChecklistPageLoading &&
          renderInvoiceDetailsCard()}

        {/* Tabular checklists: 1 to n templates, each with own form and Draft Save */}
        {templates.length > 0 && (
          <div className="checklist-form raise-complaint-form dynamic-checklist-wrapper pt-0">
            {draftLoading && !checklistLoading && (
              <div className="checklist-page-loader-overlay">
                {renderChecklistPageLoader("Loading saved draft...")}
              </div>
            )}
            {templates.length > 1 && (
              <div className="tabs-container checklist-tabs mb-3">
                {templates.map((t, idx) => (
                  <button
                    key={t.templateCode}
                    className={`tab-button ${activeChecklistTab === idx ? "active" : ""}`}
                    onClick={() => setActiveChecklistTab(idx)}
                  >
                    {t.templateName || t.templateCode || `Checklist ${idx + 1}`}
                  </button>
                ))}
              </div>
            )}
            {checklistLoading ? (
              renderChecklistPageLoader(getChecklistLoadingMessage())
            ) : (
              (() => {
                const t = templates[activeChecklistTab];
                if (!t) return null;
                return (
                  <div className="checklist-modal-content dynamic-checklist-card mb-3">
                    <div className="checklist-modal-header">
                      <h5 className="checklist-modal-title mb-0">{t.templateName || t.templateCode || `Checklist ${activeChecklistTab + 1}`}</h5>
                    </div>
                    <div className="checklist-modal-body" style={{ padding: "1.5rem" , paddingTop: "10px"}}>
                      {(searchParams.get("invoiceNo") || invoiceNo) && renderCustomerDetailsCard()}
                      {(searchParams.get("invoiceNo") || invoiceNo) && renderInvoiceDetailsCard()}
                      <div className="card-panel mt-2 pt-2 pb-1 ">
                        <div
                          className="section-heading checklist-upload-section-heading mb-2 d-flex align-items-center justify-content-between mrgbotSecHdngQtn"
                          role="button"
                          tabIndex={0}
                          onClick={() => setIsChecklistQuestionsCollapsed((prev) => !prev)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setIsChecklistQuestionsCollapsed((prev) => !prev);
                            }
                          }}
                          style={{ cursor: "pointer", userSelect: "none" }}
                        >
                          <span>Checklist Questions</span>
                          <span style={{ display: "inline-flex", alignItems: "center", color: "#1976d2" }}>
                            {isChecklistQuestionsCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                          </span>
                        </div>
                        {generalQuestionTree.length > 0 && (
                          <div
                            className="row g-3 mb-3 mt-2 qtnstrvw checklist-general-questions"
                            style={{ display: isChecklistQuestionsCollapsed ? "none" : undefined }}
                          >
                            {(() => {
                              const rootQt004SandwichFlags =
                                getQt004SandwichedHalfWidthFlags(generalQuestionTree);
                              return generalQuestionTree.flatMap((node, idx) =>
                                renderQuestionNode(node, 0, {
                                  answerScope: "general",
                                  ...(rootQt004SandwichFlags[idx]
                                    ? { qt004HalfWidthBetweenSectionSiblings: true }
                                    : {}),
                                })
                              );
                            })()}
                          </div>
                        )}
                        {srCategoriesForTabs.length > 1 && (
                          <div
                            className="tabs-container checklist-tabs sr-category-tabs mb-3"
                            style={{ display: isChecklistQuestionsCollapsed ? "none" : undefined }}
                          >
                            {srCategoriesForTabs.map((cat, idx) => (
                              <button
                                key={cat.code}
                                type="button"
                                className={`tab-button ${activeSrCategoryTab === idx ? "active" : ""}`}
                                onClick={() => setActiveSrCategoryTab(idx)}
                              >
                                {cat.name}
                              </button>
                            ))}
                          </div>
                        )}
                        {categoryQuestionTree.length > 0 && (
                          <div
                            className="row g-3 mb-3 mt-2 qtnstrvw checklist-category-questions"
                            style={{ display: isChecklistQuestionsCollapsed ? "none" : undefined }}
                          >
                            {(() => {
                              const rootQt004SandwichFlags =
                                getQt004SandwichedHalfWidthFlags(categoryQuestionTree);
                              return categoryQuestionTree.flatMap((node, idx) =>
                                renderQuestionNode(node, 0, {
                                  answerScope: "category",
                                  ...(rootQt004SandwichFlags[idx]
                                    ? { qt004HalfWidthBetweenSectionSiblings: true }
                                    : {}),
                                })
                              );
                            })()}
                          </div>
                        )}
                      </div>

                      <div className="card-panel mt-3 pt-3 pb-3">
                        <div
                          className="section-heading mb-2 d-flex align-items-center justify-content-between mrgbotSecHdngQtn"
                          role="button"
                          tabIndex={0}
                          onClick={() => setIsUploadSectionCollapsed((prev) => !prev)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setIsUploadSectionCollapsed((prev) => !prev);
                            }
                          }}
                          style={{ cursor: "pointer", userSelect: "none" }}
                        >
                          <span>Upload Images, Video Links & Comments</span>
                          <span style={{ display: "inline-flex", alignItems: "center", color: "#1976d2" }}>
                            {isUploadSectionCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                          </span>
                        </div>

                        <div
                          className="upload-card checklist-upload-combined-panel"
                          style={{ display: isUploadSectionCollapsed ? "none" : undefined }}
                        >
                          <FileUploadPanelLayout
                            embedded
                            className={IMAGE_UPLOAD_PANEL_CLASS}
                            title={
                              <>
                                Upload Images related to the complaint<span className="text-danger ms-1">*</span>
                              </>
                            }
                            formatNote={IMAGE_UPLOAD_FORMAT_NOTE}
                            helperText={COMPLAINT_DAMAGE_IMAGE_UPLOAD_HELPER}
                            accept="image/jpeg,image/jpg,image/png"
                            multiple
                            onChange={handleFileChange}
                            disabled={isDamageImageUploadAtSizeLimit(uploadFiles)}
                            chooseStyle={getImageUploadChooseStyle(isDamageImageUploadAtSizeLimit(uploadFiles))}
                            totalHint={buildImageUploadTotalHint(uploadFiles)}
                            files={uploadFiles}
                            onOpen={handleOpenChecklistAttachment}
                            onRemove={(item) => handleRemoveFile(item.id)}
                          />

                          <FileUploadPanelLayout
                            embedded
                            className="checklist-video-upload-row"
                            title="Upload Video"
                            helperText={VIDEO_UPLOAD_FORMAT_NOTE}
                            rightContent={
                              <input
                                type="url"
                                className="form-control form-control-light checklist-video-url-input"
                                placeholder="Enter video URL (e.g., https://greenlamgroup-my.sharepoint.com)"
                                value={videoUrl}
                                onChange={(e) => setVideoUrl(e.target.value)}
                              />
                            }
                          />

                          <FileUploadPanelLayout
                            embedded
                            className="vistReptComnt checklist-visit-report-row"
                            title={
                              <>
                                Visit Report Comments<span className="text-danger ms-1">*</span>
                              </>
                            }
                            helperText={VISIT_REPORT_FORMAT_NOTE}
                            rightContent={
                              <div className="checklist-visit-report-control">
                                <textarea
                                  className={`form-control form-control-light raise-checklist-textarea checklist-visit-report-textarea${visitReportCommentsError ? " is-invalid" : ""}`}
                                  value={visitReportComments}
                                  onChange={(e) => {
                                    setVisitReportComments(e.target.value);
                                    if (visitReportCommentsError && hasVisitReportCommentsValue(e.target.value)) {
                                      setVisitReportCommentsError("");
                                    }
                                  }}
                                  maxLength={3000}
                                  rows={3}
                                  required
                                  placeholder="Enter visit report comments"
                                  aria-required="true"
                                />
                                {visitReportCommentsError ? (
                                  <div className="invalid-feedback d-block">{visitReportCommentsError}</div>
                                ) : null}
                                <div className="checklist-visit-report-char-count">
                                  {visitReportComments.length} / 3000
                                </div>
                              </div>
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()
            )}
          </div>
        )}

        {templates.length === 0 && !checklistLoading && !invoiceDetailsLoading && (
          <div className="card-panel mt-3 p-4 text-center">
            <p className="text-muted mb-0">
              No checklist template is available for this invoice/product/market. You cannot submit without a checklist.
            </p>
          </div>
        )}
      </div>

      {/* Action buttons - exact same design as /users/edit (AddEditUser) */}
      <div className="card-panel mt-3">
        <div className="row">
          <div className="col-12">
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                className="add-edit-user-back-button"
                onClick={handlePrevTab}
                disabled={savingDraft || submittingFinal}
                startIcon={<ArrowBackIcon />}
                title={
                  srCategoriesForTabs.length > 1 && activeSrCategoryTab > 0
                    ? "Go to previous SR Category"
                    : templates.length > 1 && activeChecklistTab > 0
                    ? "Go to previous checklist"
                    : templates.length > 1 || templates.length === 1
                    ? "Go back to previous page"
                    : templates.length === 0
                    ? "Go back to previous page"
                    : activeTab === "prelam"
                    ? "Go to Plain Particle Board tab"
                    : "Go back to previous page"
                }
                aria-label={
                  srCategoriesForTabs.length > 1 && activeSrCategoryTab > 0
                    ? "Go to previous SR Category"
                    : templates.length > 1 && activeChecklistTab > 0
                    ? "Go to previous checklist"
                    : templates.length > 1 || templates.length === 1
                    ? "Go back to previous page"
                    : templates.length === 0
                    ? "Go back to previous page"
                    : activeTab === "prelam"
                    ? "Go to Plain Particle Board tab"
                    : "Go back to previous page"
                }
              >
                Back
              </Button>
              {templates.length > 0 && templates[activeChecklistTab] && (
                <Button
                  variant="contained"
                  size="small"
                  className="btn-save-user checklist-draft-save-btn funbtns"
                  onClick={(ev) =>
                    handleSaveDraftForTemplate(
                      ev,
                      templates[activeChecklistTab].templateCode
                    )
                  }
                  disabled={
                    savingDraftTemplateCode ===
                      templates[activeChecklistTab].templateCode ||
                    submittingFinal
                  }
                  startIcon={
                    savingDraftTemplateCode ===
                    templates[activeChecklistTab].templateCode ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SaveIcon fontSize="small" />
                    )
                  }
                  sx={{
                    background:
                      savingDraftTemplateCode ===
                      templates[activeChecklistTab].templateCode
                        ? "#bdbdbd"
                        : "linear-gradient(135deg, #ffc107 0%, #ffb300 35%, #f9a825 70%, #f57f17 100%)",
                    boxShadow:
                      savingDraftTemplateCode ===
                      templates[activeChecklistTab].templateCode
                        ? "none"
                        : "0 4px 15px rgba(245, 127, 23, 0.45)",
                    "&:hover": {
                      background:
                        savingDraftTemplateCode ===
                        templates[activeChecklistTab].templateCode
                          ? "#bdbdbd"
                          : "linear-gradient(135deg, #ffc107 0%, #ffb300 35%, #f9a825 70%, #f57f17 100%)",
                      boxShadow:
                        savingDraftTemplateCode ===
                        templates[activeChecklistTab].templateCode
                          ? "none"
                          : "0 4px 15px rgba(245, 127, 23, 0.45)",
                    },
                  }}
                >
                  {savingDraftTemplateCode ===
                  templates[activeChecklistTab].templateCode
                    ? "Saving..."
                    : "Save as Draft"}
                </Button>
              )}
              <Button
                variant="contained"
                onClick={handleNextTab}
                disabled={savingDraft || submittingFinal || templates.length === 0}
                className="btn-save-user checklist-submit-btn-green funbtns"
                endIcon={
                  savingDraft || submittingFinal ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <ArrowForwardIcon />
                  )
                }
              >
                {submittingFinal
                  ? "Submitting..."
                  : savingDraft
                  ? "Saving..."
                  : templates.length > 0 &&
                    activeChecklistTab === templates.length - 1 &&
                    (srCategoriesForTabs.length <= 1 ||
                      activeSrCategoryTab >= srCategoriesForTabs.length - 1)
                  ? "Submit and raise SR"
                  : templates.length > 0 &&
                    (activeChecklistTab < templates.length - 1 ||
                      (srCategoriesForTabs.length > 1 &&
                        activeSrCategoryTab < srCategoriesForTabs.length - 1))
                  ? "Next"
                  : templates.length === 0
                  ? "Submit and raise SR"
                  : "Next"}
              </Button>
            </Stack>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="modal-overlay subSrModal" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">
              <img src={successIcon} alt="Success" width="80" height="80" />
            </div>
            <h2 className="success-title">SR submitted successfully.</h2>
            <p className="sr-number">
              Parent SR: {srNumber}
            </p>
            {childSRNumbers && childSRNumbers.length > 0 && (
              <div className="child-sr-numbers">
                <span className="child-sr-label">Child SR(s): </span>
                <ul className="child-sr-list">
                  {childSRNumbers.map((child, idx) => (
                    <li key={idx}>
                      <span
                        className="sr-number-link"
                        role="link"
                        tabIndex={0}
                        onClick={() => handleViewChildServiceRequest(child)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleViewChildServiceRequest(child);
                          }
                        }}
                        title="Click to view child service request details"
                      >
                        {child}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <button className="modal-ok-btn" onClick={handleCloseModal}>
              Ok
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/** Decrypts ?q=ENC:... then renders the checklist (avoids effects running on empty params). */
export default function RaiseComplaintChecklist() {
  const { searchParams, paramsReady } = useEncryptedSearchParams();

  if (!paramsReady || !searchParams) {
    return (
      <div className="raise-page d-flex justify-content-center align-items-center" style={{ minHeight: "40vh" }}>
        <CircularProgress aria-label="Loading checklist" />
      </div>
    );
  }

  return <RaiseComplaintChecklistPage searchParams={searchParams} />;
}
